import React, { useState, useEffect } from 'react';

// Version management - UPDATE ONLY package.json!
const { version: APP_VERSION } = require('../package.json');

export default function AgriAdmin() {
  // ============================================
  // ALL USESTATE HOOKS MUST BE AT THE TOP LEVEL
  // ============================================
  
  // Navigation & Setup
  const [section, setSection] = useState('home');
  const [editingInventoryItem, setEditingInventoryItem] = useState(null);
  const [isSetupComplete, setIsSetupComplete] = useState(
    localStorage.getItem('setupComplete') === 'true'
  );
  
  // Load records helper function
  const loadRecords = () => {
    try {
      const saved = localStorage.getItem('epaFarmRecords');
      return saved ? JSON.parse(saved) : [];
    } catch (error) {
      console.error('Error loading records:', error);
      return [];
    }
  };

  // Load inventory helper function
  const loadInventory = () => {
    try {
      const saved = localStorage.getItem('epaInventory');
      return saved ? JSON.parse(saved) : [];
    } catch (error) {
      console.error('Error loading inventory:', error);
      return [];
    }
  };

  // Core data states
  const [records, setRecords] = useState(loadRecords());
  const [inventory, setInventory] = useState(loadInventory());
  const [farmerName, setFarmerName] = useState(
    localStorage.getItem('defaultFarmerName') || ''
  );
  const [propertyAddress, setPropertyAddress] = useState(
    localStorage.getItem('propertyAddress') || ''
  );
  const [lastAutoBackup, setLastAutoBackup] = useState(
    localStorage.getItem('lastAutoBackup') || null
  );

  // Setup states
  const [setupFarmerName, setSetupFarmerName] = useState('');
  const [setupPropertyAddress, setSetupPropertyAddress] = useState('');

  // Records view states
  const [filter, setFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [filterYear, setFilterYear] = useState('');
  const [filterSeason, setFilterSeason] = useState('');
  const [filterPaddock, setFilterPaddock] = useState('');
  const [filterSpecies, setFilterSpecies] = useState('');
  const [filterFarm, setFilterFarm] = useState('');
  const [editingEntry, setEditingEntry] = useState(null);

  // Custom chemicals
  const [customChemicals, setCustomChemicals] = useState(
    JSON.parse(localStorage.getItem('customChemicals') || '[]')
  );

  // Saved paddocks list — persisted to localStorage
  // Each entry: { name, areaHectares, farm } — farm is optional; untagged (legacy)
  // paddocks remain visible under every farm until re-saved with a farm tag.
  const [savedPaddocks, setSavedPaddocks] = useState(() => {
    try {
      const stored = localStorage.getItem('savedPaddocks');
      return stored ? JSON.parse(stored) : [];
    } catch { return []; }
  });

  // Farm list — persisted to localStorage. Seeded from propertyAddress on first
  // load for existing users (new signups are seeded in completeSetup()).
  const [farmList, setFarmList] = useState(() => {
    try {
      const stored = localStorage.getItem('farmList');
      if (stored) return JSON.parse(stored);
      const seed = localStorage.getItem('propertyAddress');
      return seed ? [seed] : [];
    } catch { return []; }
  });

  // Last used farm — persisted to localStorage; defaults new records' farmName
  const [lastUsedFarm, setLastUsedFarm] = useState(() => {
    try {
      return localStorage.getItem('lastUsedFarm') || localStorage.getItem('propertyAddress') || '';
    } catch { return ''; }
  });

  // Farm picker UI state
  const [showFarmDropdown, setShowFarmDropdown] = useState(false);

  // Saved weeds list — seeded from commonPests on first use
  const DEFAULT_WEEDS = [
    'Annual Ryegrass', 'Barley Grass', 'Bathurst Burr', 'Bindweed', 'Blackberry',
    'Blue Heliotrope', 'Caltrop', 'Capeweed', 'Couch', 'Cotton Aphid',
    'Crown Rot', 'Diamondback Moth', 'Dwarf Amaranth', 'Dwarf Marigold',
    'Fleabane', 'Green Peach Aphid', 'Heliotrope', 'Helicoverpa',
    'Liverseed Grass', 'Lucerne Flea', 'Mallow', 'Milk Thistle',
    'Paterson\'s Curse', 'Pigweed', 'Powdery Mildew', 'Red Legged Earth Mite',
    'Rust Disease', 'Serrated Tussock', 'Skeleton Weed', 'Thornapple',
    'Tiger Pear', 'Volunteer Oats', 'Wild Oats', 'Wild Radish', 'Wireweed'
  ];
  const [savedWeeds, setSavedWeeds] = useState(() => {
    try {
      const stored = localStorage.getItem('savedWeeds');
      return stored ? JSON.parse(stored) : DEFAULT_WEEDS;
    } catch { return DEFAULT_WEEDS; }
  });

  // Paddock picker UI state
  const [paddockInput, setPaddockInput] = useState('');
  const [paddockAreaInput, setPaddockAreaInput] = useState('');
  const [showPaddockDropdown, setShowPaddockDropdown] = useState(false);

  // Weed picker UI state
  const [weedInput, setWeedInput] = useState('');
  const [showWeedDropdown, setShowWeedDropdown] = useState(false);

  // Purchase form state
  const [purchaseForm, setPurchaseForm] = useState({
    selectedChemical: '',
    chemicalName: '',
    activeIngredient: '',
    category: '',
    landRate: '',
    animalRate: '',
    animalRateNumber: '',
    animalRateUnit: 'mL/kg',
    withholdingPeriod: '',
    withholdingDays: '',
    esiDays: '',
    containerSizeNumber: '',
    containerSizeUnit: 'L',
    quantityRemainingNumber: '',
    quantityRemainingUnit: 'L',
    batchNumber: '',
    manufacturingDate: '',
    expiryDate: '',
    purchaseDate: new Date().toISOString().split('T')[0],
    applicationRateNumber: '',
    applicationRateUnit: 'L/ha'
  });

  // NEW: Label photo states
  const [labelPhotos, setLabelPhotos] = useState([]);
  const [showPhotoInstructions, setShowPhotoInstructions] = useState(false);
  const [labelPhotoContext, setLabelPhotoContext] = useState('full'); // 'full' or 'batch'
  const [isProcessingPhotos, setIsProcessingPhotos] = useState(false);
  const [isLookingUp, setIsLookingUp] = useState(false);
  // eslint-disable-next-line no-unused-vars
  const [showConfirmationDialog, setShowConfirmationDialog] = useState(false);
  const [showBackupPrompt, setShowBackupPrompt] = useState(false);
  const [extractedData, setExtractedData] = useState(null);
  const [confirmationChecks, setConfirmationChecks] = useState({});

  // PWA install prompt
  const [deferredInstallPrompt, setDeferredInstallPrompt] = useState(null);
  const [showInstallBanner, setShowInstallBanner] = useState(false);
  const [isIosInstallBanner, setIsIosInstallBanner] = useState(false);

  // General-purpose confirmation modal (replaces window.confirm — suppressed on iOS PWA)
  const [confirmModal, setConfirmModal] = useState({
    visible: false,
    title: '',
    message: '',
    onConfirm: null,
    confirmLabel: 'OK',
    confirmClass: 'bg-red-600 text-white',
    cancelLabel: 'Cancel'
  });

  const showConfirm = (title, message, onConfirm, options = {}) => {
    setConfirmModal({
      visible: true,
      title,
      message,
      onConfirm,
      confirmLabel: options.confirmLabel || 'OK',
      confirmClass: options.confirmClass || 'bg-red-600 text-white',
      cancelLabel: options.cancelLabel || 'Cancel'
    });
  };

  const dismissConfirm = () => {
    setConfirmModal(prev => ({ ...prev, visible: false, onConfirm: null }));
  };
 // Spray Rec Import state
  const [sprayRecModal, setSprayRecModal] = useState({ visible: false, parsed: null, loading: false, error: null });

  // PDF text extraction happens server-side (see parse-spray-rec.js) so this
  // works identically regardless of the customer's device/browser/iOS version —
  // client-side pdf.js extraction was dropped after it proved impossible to
  // support old iOS Safari (see project notes, 2026-07).
  const parseSprayRec = async (file) => {
    setSprayRecModal({ visible: true, parsed: null, loading: true, error: null });

    try {
      // Netlify synchronous functions cap request payloads at 6MB, and
      // base64 encoding adds ~30% overhead — guard client-side with a clear
      // message rather than letting an oversized upload fail as a generic error.
      const MAX_PDF_BYTES = 4 * 1024 * 1024; // 4MB raw file ceiling
      if (file.size > MAX_PDF_BYTES) {
        setSprayRecModal({ visible: true, parsed: null, loading: false, error: 'This PDF is too large to upload (max 4MB). Try re-exporting or reducing the file size.' });
        return;
      }

      // Read file as base64
      const pdfBase64 = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => {
          // reader.result is a data URL: "data:application/pdf;base64,XXXX"
          const commaIndex = reader.result.indexOf(',');
          resolve(reader.result.slice(commaIndex + 1));
        };
        reader.onerror = () => reject(reader.error);
        reader.readAsDataURL(file);
      });

      // Send to Netlify function — text extraction happens there
      const response = await fetch('/.netlify/functions/parse-spray-rec', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-app-secret': process.env.REACT_APP_SECRET || ''
        },
        body: JSON.stringify({ pdfBase64 })
      });

      if (!response.ok) {
        setSprayRecModal({ visible: true, parsed: null, loading: false, error: 'Server did not respond correctly — please try again.' });
        return;
      }

      const data = await response.json();

      if (data.error) {
        setSprayRecModal({ visible: true, parsed: null, loading: false, error: data.error });
        return;
      }

      const parsed = JSON.parse(data.result);
      setSprayRecModal({ visible: true, parsed, loading: false, error: null });

    } catch (err) {
      console.error('Spray rec parse error:', err);
      setSprayRecModal({ visible: true, parsed: null, loading: false, error: `Something went wrong reading this PDF (${err.message}). Please try again, or contact support if it persists.` });
    }
  };

  // Inventory stock check for a parsed herbicide
  // Returns: { status: 'ok' | 'low' | 'none', available: string }
  const checkSprayRecStock = (productName, totalQty, totalQtyUnit) => {
    const toBaseML = (qty, unit) => {
      const n = parseFloat(qty) || 0;
      if (!unit) return n;
      const u = unit.toLowerCase();
      if (u === 'l') return n * 1000;
      if (u === 'kg') return n * 1000;
      return n; // mL or g
    };

    // Find matching inventory items by product name (case-insensitive partial match)
    const matches = inventory.filter(item =>
      item.chemicalName.toLowerCase() === productName.toLowerCase() ||
      item.chemicalName.toLowerCase().includes(productName.toLowerCase()) ||
      productName.toLowerCase().includes(item.chemicalName.toLowerCase())
    );

    if (matches.length === 0) return { status: 'none', available: '0' };

    const totalAvailableBase = matches.reduce((sum, item) => {
      return sum + toBaseML(item.quantityRemainingNumber, item.quantityRemainingUnit);
    }, 0);

    const requiredBase = toBaseML(totalQty, totalQtyUnit);

    // Format available for display
    const displayUnit = totalQtyUnit || 'L';
    const displayAvailable = displayUnit.toLowerCase() === 'l'
      ? `${(totalAvailableBase / 1000).toFixed(2)} L`
      : `${totalAvailableBase.toFixed(0)} mL`;

    if (totalAvailableBase >= requiredBase) {
      return { status: 'ok', available: displayAvailable };
    }
    return { status: 'low', available: displayAvailable };
  };

  // Apply parsed spray rec to the land form
  const applySprayRec = (parsed) => {
    // 1. Update savedPaddocks — add any new ones
    const updatedPaddocks = [...savedPaddocks];
    (parsed.paddocks || []).forEach(p => {
      const exists = updatedPaddocks.some(sp => sp.name.toLowerCase() === p.name.toLowerCase());
      if (!exists && p.name) {
        updatedPaddocks.push({ name: p.name, areaHectares: p.areaHectares || '', farm: landForm.farmName || '' });
      } else if (exists && p.areaHectares) {
        // Update area if it's changed
        const idx = updatedPaddocks.findIndex(sp => sp.name.toLowerCase() === p.name.toLowerCase());
        if (idx >= 0) updatedPaddocks[idx] = { ...updatedPaddocks[idx], areaHectares: p.areaHectares };
      }
    });
    setSavedPaddocks(updatedPaddocks.sort((a, b) => a.name.localeCompare(b.name)));

    // 2. Update savedWeeds — add any new ones
    const updatedWeeds = [...savedWeeds];
    (parsed.pestTargets || []).forEach(weed => {
      if (weed && !updatedWeeds.some(w => w.toLowerCase() === weed.toLowerCase())) {
        updatedWeeds.push(weed);
      }
    });
    setSavedWeeds(updatedWeeds.sort((a, b) => a.localeCompare(b)));

    // 3. Build chemical entries from parsed herbicides
    const buildChemEntry = (herb) => {
      const match = allChemicals.find(c =>
        c.name.toLowerCase() === herb.productName.toLowerCase() ||
        c.name.toLowerCase().includes(herb.productName.toLowerCase()) ||
        herb.productName.toLowerCase().includes(c.name.toLowerCase())
      );

      // Parse rate into rateMode fields
      // Agworld rates are per-ha (e.g. "150 mL/ha") — use areaRate mode
      const rateQty = herb.rateQty || '';
      const rateUnit = (herb.rateUnit || 'mL/ha').replace('/ha', '').trim(); // "mL" or "L"

return {
        selectedChemical: match ? match.name : 'Other',
        productName: match ? match.name : (herb.productName || ''),
        activeIngredient: match ? match.activeIngredient : (herb.activeIngredient || ''),
        withholdingPeriod: match ? match.withholdingPeriod : '',
        withholdingDays: match ? String(match.withholdingDays ?? '') : '',
        rateMode: 'area',
        rateQty: '',
        rateUnit: 'mL',
        rateWaterQty: '100',
        areaRateQty: rateQty,
        areaRateUnit: rateUnit || 'mL',
        calculatedQty: herb.totalQty || '',
        calculatedQtyUnit: herb.totalQtyUnit || 'L',
        batchNumber: '',
        selectedBatches: [{ batchId: '', qty: '' }]
      };
    };

    const chemEntries = (parsed.herbicides || []).length > 0
      ? parsed.herbicides.map(buildChemEntry)
      : [emptyChemicalEntry()];

    // 4. Determine cropLandUse — match against commonCrops list
    const cropRaw = parsed.cropLandUse || '';
    const cropMatch = commonCrops.find(c => c.toLowerCase() === cropRaw.toLowerCase());
    const cropLandUse = cropMatch || (cropRaw ? 'Other' : '');
    const cropLandUseOther = (!cropMatch && cropRaw) ? cropRaw : '';

    // 5. Map applicationMethod
    const methodMap = {
      'Boom Spray': 'Boom Spray',
      'Boomspray': 'Boom Spray',
      'Boom spray': 'Boom Spray',
      'Spot Spray': 'Spot Spray',
      'Spot spray': 'Spot Spray',
      'Aerial Application': 'Aerial Application',
      'Aerial': 'Aerial Application',
      'Wiper Application': 'Wiper Application',
      'Hand Application': 'Hand Application'
    };
    const applicationMethod = methodMap[parsed.applicationMethod] || 'Boom Spray';

    // 6. Total volume — remove commas from numeric string
    const totalVol = parsed.totalVolumeQty ? String(parsed.totalVolumeQty).replace(/,/g, '') : '';

    // 7. Calculate total area
    const paddockObjs = (parsed.paddocks || []).filter(p => p.name);
    const totalHa = paddockObjs.reduce((sum, p) => sum + parseFloat(p.areaHectares || 0), 0);

    // 8. Apply to landForm
    setLandForm(prev => ({
      ...prev,
      date: new Date().toISOString().split('T')[0],
      paddocks: paddockObjs,
      paddockName: paddockObjs.map(p => p.name).join(', '),
      areaHectares: totalHa > 0 ? totalHa.toFixed(2) : '',
      pestTargets: parsed.pestTargets || [],
      pestTarget: (parsed.pestTargets || []).join(', '),
      cropLandUse,
      cropLandUseOther,
      applicationMethod,
      totalDilutedVolumeQty: totalVol,
      totalDilutedVolumeUnit: 'L',
      chemicals: chemEntries,
      applicator: farmerName
    }));

    setSprayRecModal({ visible: false, parsed: null, loading: false, error: null });
  };


  // Land form state
  const IMPERIAL_GALLON_TO_L = 4.546;

  const emptyChemicalEntry = () => ({
    selectedChemical: '',
    productName: '',
    activeIngredient: '',
    batchNumber: '',
    rateMode: 'dilution',
    rateQty: '',
    rateUnit: 'mL',
    rateWaterQty: '100',
    areaRateQty: '',
    areaRateUnit: 'L',
    calculatedQty: '',
    calculatedQtyUnit: 'mL',
    withholdingPeriod: '',
    withholdingDays: '',
    selectedBatches: [{ batchId: '', qty: '' }]
  });

  const emptyMedicationEntry = () => ({
    selectedMedication: '',
    productName: '',
    activeIngredient: '',
    batchNumber: '',
    withholdingPeriod: '',
    withholdingDays: '',
    esiDays: '',
    doseRate: '',
    doseRateNumber: '',
    doseRateUnit: 'mL/kg',
    doseRatePerKg: '',
    treatmentPurpose: '',
    applicationMethod: 'Injectable',
    calculatedDosePerAnimal: '',
    totalQuantity: '',
    selectedBatches: [{ batchId: '', qty: '' }],
  });

  const [landForm, setLandForm] = useState({
    date: new Date().toISOString().split('T')[0],
    farmName: lastUsedFarm,
    paddocks: [],          // [{ name, areaHectares }]
    paddockName: '',       // legacy — serialised on save
    areaHectares: '',      // auto-sum of paddocks, editable
    pestTargets: [],       // [string]
    pestTarget: '',        // legacy — serialised on save
    pestTargetOther: '',
    cropLandUse: '',
    cropLandUseOther: '',
    applicationMethod: 'Spot Spray',
    totalDilutedVolumeQty: '',
    totalDilutedVolumeUnit: 'L',
    chemicals: [emptyChemicalEntry()],
    windSpeed: '',
    windDirection: '',
    applicator: '',
    photos: [],
    notes: ''
  });

  // Animal form state
  const [animalForm, setAnimalForm] = useState({
    date: new Date().toISOString().split('T')[0],
    farmName: lastUsedFarm,
    species: 'Cattle',
    animalCount: '',
    averageWeight: '',
    animalIDs: '',
    applicator: '',
    photos: [],
    notes: '',
    medications: [emptyMedicationEntry()],
    // Legacy single-med fields kept for edit backwards compat
    selectedMedication: '',
    productName: '',
    activeIngredient: '',
    batchNumber: '',
    withholdingPeriod: '',
    withholdingDays: '',
    esiDays: '',
    doseRate: '',
    doseRateNumber: '',
    doseRateUnit: 'mL/kg',
    doseRatePerKg: '',
    calculatedDosePerAnimal: '',
    totalQuantity: '',
    treatmentPurpose: '',
    applicationMethod: 'Injectable',
  });

  // Auto-save records
  useEffect(() => {
    if (isSetupComplete) {
      try {
        localStorage.setItem('epaFarmRecords', JSON.stringify(records));
      } catch (error) {
        console.error('Error saving records:', error);
      }
    }
  }, [records, isSetupComplete]);

  // Auto-save inventory
  useEffect(() => {
    if (isSetupComplete) {
      try {
        localStorage.setItem('epaInventory', JSON.stringify(inventory));
      } catch (error) {
        console.error('Error saving inventory:', error);
      }
    }
  }, [inventory, isSetupComplete]);

  // Auto-save savedPaddocks
  useEffect(() => {
    try { localStorage.setItem('savedPaddocks', JSON.stringify(savedPaddocks)); } catch {}
  }, [savedPaddocks]);

  // One-time migration: paddocks saved before farm-tagging existed have no
  // `farm` field. Tag them with the property address so they don't disappear
  // from the picker now that paddock suggestions are filtered strictly by farm.
  useEffect(() => {
    if (!propertyAddress) return;
    setSavedPaddocks(prev => {
      if (!prev.some(p => !p.farm)) return prev;
      return prev.map(p => p.farm ? p : { ...p, farm: propertyAddress });
    });
  }, [propertyAddress]);

  // Auto-save farmList
  useEffect(() => {
    try { localStorage.setItem('farmList', JSON.stringify(farmList)); } catch {}
  }, [farmList]);

  // Auto-save lastUsedFarm
  useEffect(() => {
    try { localStorage.setItem('lastUsedFarm', lastUsedFarm); } catch {}
  }, [lastUsedFarm]);

  // Auto-save savedWeeds
  useEffect(() => {
    try { localStorage.setItem('savedWeeds', JSON.stringify(savedWeeds)); } catch {}
  }, [savedWeeds]);
// Reset scroll position on section change
useEffect(() => {
  window.scrollTo(0, 0);
}, [section]);

  // Update applicator when farmer name changes
  useEffect(() => {
    setLandForm(prev => ({ ...prev, applicator: farmerName }));
    setAnimalForm(prev => ({ ...prev, applicator: farmerName }));
  }, [farmerName]);

  // Handle browser back button to return to home screen
  useEffect(() => {
    const handlePopState = (event) => {
      // If not on home screen, go to home instead of going back in browser history
      if (section !== 'home') {
        event.preventDefault();
        setSection('home');
        setEditingEntry(null);
        // Push state again so back button works consistently
        window.history.pushState({ section: 'home' }, '', '');
      }
    };

    // Push initial state
    if (section !== 'home') {
      window.history.pushState({ section }, '', '');
    }

    // Listen for back button
    window.addEventListener('popstate', handlePopState);

    return () => {
      window.removeEventListener('popstate', handlePopState);
    };
  }, [section]);

  // Auto-check for updates on app launch
  useEffect(() => {
    if (!isSetupComplete) return; // Don't check during setup
    
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.ready.then((registration) => {
        // Check if there's already an update waiting (downloaded before app opened)
        if (registration.waiting) {
          showConfirm(
            '\ud83c\udf89 New Version Available',
            'An update was downloaded. Would you like to update now?\n\nYour data will be preserved.',
            () => {
              registration.waiting.postMessage({ type: 'SKIP_WAITING' });
              window.location.reload();
            },
            { confirmLabel: 'Update Now', confirmClass: 'bg-purple-600 text-white', cancelLabel: 'Later' }
          );
          return;
        }
        
        // Force check for updates
        registration.update();
        
        // Listen for new service worker being installed
        registration.addEventListener('updatefound', () => {
          const newWorker = registration.installing;
          
          if (newWorker) {
            newWorker.addEventListener('statechange', () => {
              if (newWorker.state === 'installed' && navigator.serviceWorker.controller) {
                // New version downloaded and ready!
                showConfirm(
                  '\ud83c\udf89 New Version Available',
                  'An update has been downloaded. Would you like to update now?\n\nYour data will be preserved.',
                  () => {
                    newWorker.postMessage({ type: 'SKIP_WAITING' });
                    window.location.reload();
                  },
                  { confirmLabel: 'Update Now', confirmClass: 'bg-purple-600 text-white', cancelLabel: 'Later' }
                );
              }
            });
          }
        });
      });
    }
  }, [isSetupComplete]); // Only run once when app loads and setup is complete

  // Auto-backup check on app launch - shows prompt after 7 days
  useEffect(() => {
    if (!isSetupComplete) return;
    const lastBackup = localStorage.getItem('lastAutoBackup');
    let needsBackup = false;
    if (!lastBackup) {
      needsBackup = true;
    } else {
      const daysSinceBackup = Math.floor((new Date() - new Date(lastBackup)) / (1000 * 60 * 60 * 24));
      if (daysSinceBackup >= 7) needsBackup = true;
    }
    if (needsBackup) setShowBackupPrompt(true);
  }, [isSetupComplete]);

  // PWA install prompt — Android/Chrome deferred prompt + iOS Safari instruction banner
  useEffect(() => {
    // Don't show if already installed (running in standalone mode)
    if (window.matchMedia('(display-mode: standalone)').matches || window.navigator.standalone) return;
    // Don't show if user has previously dismissed
    if (localStorage.getItem('installBannerDismissed') === 'true') return;

    // iOS Safari detection — no beforeinstallprompt support
    const isIos = /iphone|ipad|ipod/i.test(navigator.userAgent);
    const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
    if (isIos && isSafari) {
      setIsIosInstallBanner(true);
      setShowInstallBanner(true);
      return;
    }

    // Android/Chrome — capture the deferred prompt
    const handler = (e) => {
      e.preventDefault();
      setDeferredInstallPrompt(e);
      setIsIosInstallBanner(false);
      setShowInstallBanner(true);
    };
    window.addEventListener('beforeinstallprompt', handler);
    return () => window.removeEventListener('beforeinstallprompt', handler);
  }, []);

  // Shared backup function - iOS uses Web Share API, others use link download
  const performBackup = async (backupType = 'manual') => {
    const backupData = {
      records,
      inventory,
      customChemicals,
      savedPaddocks,
      farmList,
      lastUsedFarm,
      farmerName,
      propertyAddress,
      exportDate: new Date().toLocaleDateString('en-AU'),
      appVersion: APP_VERSION,
      backupType
    };
    const jsonString = JSON.stringify(backupData, null, 2);
    const dateStr = new Date().toISOString().split('T')[0];
    const fileName = `AgriAdmin_AutoBackup_${dateStr}.json`;
    const blob = new Blob([jsonString], { type: 'application/json' });

    try {
      if (navigator.share && navigator.canShare) {
        const file = new File([blob], fileName, { type: 'application/json' });
        if (navigator.canShare({ files: [file] })) {
          await navigator.share({ title: 'Agri Admin Backup', files: [file] });
        } else {
          throw new Error('canShare false');
        }
      } else {
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = fileName;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
      }
      const now = new Date().toISOString();
      localStorage.setItem('lastAutoBackup', now);
      setLastAutoBackup(now);
      setShowBackupPrompt(false);
    } catch (error) {
      // Fallback to link download if share fails
      try {
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = fileName;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
        const now = new Date().toISOString();
        localStorage.setItem('lastAutoBackup', now);
        setLastAutoBackup(now);
        setShowBackupPrompt(false);
      } catch (fallbackError) {
        console.error('Backup error:', fallbackError);
      }
    }
  };

  // Inject spinner-removal CSS globally (persists across section changes)
  useEffect(() => {
    const styleId = 'no-number-spinners';
    if (!document.getElementById(styleId)) {
      const style = document.createElement('style');
      style.id = styleId;
      style.textContent = 'input[type=number]::-webkit-inner-spin-button,input[type=number]::-webkit-outer-spin-button{-webkit-appearance:none;margin:0}input[type=number]{-moz-appearance:textfield}';
      document.head.appendChild(style);
    }
  }, []);


  // ============================================
  // CONSTANTS & DATA (AFTER HOOKS)
  // ============================================

  // Common pests/weeds
  const commonPests = [
    'Annual Ryegrass', 'Barley Grass', 'Blackberry', 'Blue Heliotrope',
    'Capeweed', 'Cotton Aphid', 'Crown Rot', 'Diamondback Moth',
    'Fleabane', 'Green Peach Aphid', 'Heliotrope', 'Helicoverpa',
    'Lucerne Flea', 'Milk Thistle', 'Paterson\'s Curse', 'Powdery Mildew',
    'Red Legged Earth Mite', 'Rust Disease', 'Serrated Tussock', 'Tiger Pear',
    'Wild Oats', 'Wild Radish', 'Other'
  ];

  // Common crops
  const commonCrops = [
    'Wheat', 'Barley', 'Canola', 'Oats', 'Chickpeas', 'Lentils',
    'Faba Beans', 'Pasture', 'Lucerne', 'Sorghum', 'Cotton',
    'Fallow Land', 'Non-Crop Area', 'Fence Lines', 'Roadside', 'Other'
  ];

  // Chemical database
  const chemicalDatabase = [
    // Herbicides
    // withholdingDays = worst-case numeric default; farmer must confirm correct value for their use pattern
    // esiDays = null for all land chemicals (ESI not applicable)
    { name: 'Roundup PowerMAX', activeIngredient: 'Glyphosate 570g/L',
      category: 'Herbicide', landRate: '1.5-3.5 L/ha (see label)', animalRate: 'N/A',
      withholdingPeriod: 'Nil to 7 days grazing depending on use — confirm label for your use pattern',
      withholdingDays: 7, esiDays: 0 },
    { name: 'Roundup', activeIngredient: 'Glyphosate 360g/L',
      category: 'Herbicide', landRate: '1.5-5 L/ha (see label)', animalRate: 'N/A',
      withholdingPeriod: 'Nil to 7 days grazing depending on use — confirm label for your use pattern',
      withholdingDays: 7, esiDays: 0 },
    { name: 'Crucial Herbicide', activeIngredient: 'Glyphosate 600g/L',
      category: 'Herbicide', landRate: '1.5-3 L/ha', animalRate: 'N/A',
      withholdingPeriod: 'Nil to 7 days grazing depending on use — confirm label for your use pattern',
      withholdingDays: 7, esiDays: 0 },
    { name: 'Spray.Seed', activeIngredient: 'Paraquat 250g/L + Diquat 250g/L',
      category: 'Herbicide', landRate: '1-3 L/ha (see label)', animalRate: 'N/A',
      withholdingPeriod: '1 day — do not graze or cut for stockfeed for 1 day after application',
      withholdingDays: 1, esiDays: 0 },
    { name: 'Verdict 520', activeIngredient: 'Haloxyfop 520g/L',
      category: 'Herbicide', landRate: '0.1-0.4 L/ha (see label)', animalRate: 'N/A',
      withholdingPeriod: '60 days — do not graze or cut for 60 days after application',
      withholdingDays: 60, esiDays: 0 },
    { name: 'Task Force', activeIngredient: 'Flupropanate 745g/L',
      category: 'Herbicide', landRate: '2 L/ha boom; 200mL/100L spot', animalRate: 'N/A',
      withholdingPeriod: 'Blanket: 120 days grazing; Spot: 14 days grazing; Slaughter: 14 days',
      withholdingDays: 120, esiDays: 0 },
    { name: 'Frenock 745', activeIngredient: 'Flupropanate 745g/L',
      category: 'Herbicide', landRate: '2 L/ha boom; 200mL/100L spot', animalRate: 'N/A',
      withholdingPeriod: 'Blanket: 120 days grazing; Spot: 14 days grazing; Slaughter: 14 days',
      withholdingDays: 120, esiDays: 0 },
    { name: 'Cutlass M', activeIngredient: 'MCPA 340g/L + Dicamba 80g/L',
      category: 'Herbicide', landRate: '5-8 L/ha', animalRate: 'N/A',
      withholdingPeriod: '7 days — do not cut or graze for 7 days after application',
      withholdingDays: 7, esiDays: 0 },
    { name: 'Grazon Extra', activeIngredient: 'Triclopyr 300g/L + Picloram 100g/L',
      category: 'Herbicide', landRate: '8 L/ha', animalRate: 'N/A',
      withholdingPeriod: 'Meat: 14 days; do not graze dairy cattle within milking distance for 5 days',
      withholdingDays: 14, esiDays: 0 },
    { name: 'Graslan', activeIngredient: 'Tebuthiuron 200g/kg',
      category: 'Herbicide', landRate: '1-2 g/m²', animalRate: 'N/A',
      withholdingPeriod: 'See label — withholding period is application rate and area specific',
      withholdingDays: 0, esiDays: 0 },
    { name: 'Titan MCPA 750', activeIngredient: 'MCPA 750g/L',
      category: 'Herbicide', landRate: '0.5-2 L/ha', animalRate: 'N/A',
      withholdingPeriod: '7 days — do not cut or graze for 7 days after application',
      withholdingDays: 7, esiDays: 0 },
    { name: 'Titan Metsulfuron 600 WG', activeIngredient: 'Metsulfuron-methyl 600g/kg',
      category: 'Herbicide', landRate: 'See label', animalRate: 'N/A',
      withholdingPeriod: 'Nil — do not graze for 3 days after application',
      withholdingDays: 3, esiDays: 0 },

    // Insecticides
    { name: 'Karate Zeon', activeIngredient: 'Lambda-cyhalothrin 100g/L',
      category: 'Insecticide', landRate: 'See label', animalRate: 'N/A',
      withholdingPeriod: '7 days (pasture/forage); see label for crop-specific uses',
      withholdingDays: 7, esiDays: 0 },
    { name: 'Steward', activeIngredient: 'Indoxacarb 150g/L',
      category: 'Insecticide', landRate: 'See label', animalRate: 'N/A',
      withholdingPeriod: '7 days minimum — see label for crop-specific uses',
      withholdingDays: 7, esiDays: 0 },
    { name: 'Surround Termiticide', activeIngredient: 'Bifenthrin 100g/L',
      category: 'Insecticide', landRate: '500mL/100L water', animalRate: 'N/A',
      withholdingPeriod: 'N/A — not for use on food crops or in areas accessible to livestock',
      withholdingDays: 0, esiDays: 0 },

    // Fungicides
    { name: 'Prosaro', activeIngredient: 'Prothioconazole 210g/L + Tebuconazole 210g/L',
      category: 'Fungicide', landRate: 'See label', animalRate: 'N/A',
      withholdingPeriod: 'Grain crops: 14 days — see label for crop-specific uses',
      withholdingDays: 14, esiDays: 0 },

    // Rodenticides
    { name: 'Ratsak', activeIngredient: 'Brodifacoum 0.05g/kg',
      category: 'Rodenticide', landRate: 'As needed per bait station', animalRate: 'N/A',
      withholdingPeriod: 'N/A — livestock exclusion required during baiting',
      withholdingDays: 0, esiDays: 0 },
    { name: 'Bromakill', activeIngredient: 'Bromadiolone 0.05g/kg',
      category: 'Rodenticide', landRate: 'As needed per bait station', animalRate: 'N/A',
      withholdingPeriod: 'N/A — livestock exclusion required during baiting',
      withholdingDays: 0, esiDays: 0 },

    // Markers
    { name: 'Mark It Blue', activeIngredient: 'Non-toxic marker dye',
      category: 'Marker', landRate: 'As per label', animalRate: 'N/A',
      withholdingPeriod: 'N/A',
      withholdingDays: 0, esiDays: 0 },
    { name: 'Rhodamine Red', activeIngredient: 'Non-toxic marker dye',
      category: 'Marker', landRate: 'As per label', animalRate: 'N/A',
      withholdingPeriod: 'N/A',
      withholdingDays: 0, esiDays: 0 },

    // Animal Medications - Parasiticides
    { name: 'Dectomax Injectable', activeIngredient: 'Doramectin 10mg/mL',
      category: 'Parasiticide', landRate: 'N/A', animalRate: '1mL/50kg SC/IM',
      withholdingPeriod: 'Meat: 42 days (cattle), 35 days (pigs). Do not use in lactating animals or within 60 days of calving if milk for human consumption. ESI: 42 days',
      withholdingDays: 42, esiDays: 42 },
    { name: 'Cydectin Pour-On', activeIngredient: 'Moxidectin 5mg/mL',
      category: 'Parasiticide', landRate: 'N/A', animalRate: '1mL/10kg',
      withholdingPeriod: 'Meat: 14 days. ESI: 42 days',
      withholdingDays: 14, esiDays: 42 },
    { name: 'Genesis Ultra Injection', activeIngredient: 'Ivermectin 10g/L + Closulon 100g/L',
      category: 'Parasiticide', landRate: 'N/A', animalRate: '1mL/50kg',
      withholdingPeriod: 'Meat: 28 days. ESI: 42 days',
      withholdingDays: 28, esiDays: 42 },

    // Vaccines
    { name: 'Coopers 5in1', activeIngredient: 'Clostridial vaccine',
      category: 'Vaccine', landRate: 'N/A', animalRate: '2mL initial; 1mL booster',
      withholdingPeriod: 'Nil',
      withholdingDays: 0, esiDays: 0 },
    { name: 'Ultravac 5in1', activeIngredient: 'Clostridial vaccine',
      category: 'Vaccine', landRate: 'N/A', animalRate: '2mL initial; 1mL booster',
      withholdingPeriod: 'Nil',
      withholdingDays: 0, esiDays: 0 },
    { name: 'Websters 5in1', activeIngredient: 'Clostridial vaccine',
      category: 'Vaccine', landRate: 'N/A', animalRate: '2mL initial; 1mL booster',
      withholdingPeriod: 'Nil',
      withholdingDays: 0, esiDays: 0 },

    // Supplements & Hormones
    { name: 'Multimin', activeIngredient: 'Trace minerals',
      category: 'Supplement', landRate: 'N/A', animalRate: '1mL/100kg',
      withholdingPeriod: 'Nil',
      withholdingDays: 0, esiDays: 0 },
    { name: 'Clomate', activeIngredient: 'Cloprostenol 250mcg/mL',
      category: 'Hormone', landRate: 'N/A', animalRate: '2mL/animal IM',
      withholdingPeriod: 'Meat: Nil. Milk: 1 day (24hrs)',
      withholdingDays: 1, esiDays: 0 },

    // Antibiotics
    { name: 'Noroclav', activeIngredient: 'Amoxicillin + Clavulanic acid (tablets)',
      category: 'Antibiotic', landRate: 'N/A', animalRate: '6.25-12.5mg/kg bodyweight (1 tablet per 40-80kg) twice daily — calves only. Not for bobby calves.',
      withholdingPeriod: 'Meat: 4 days (calves). Not for bobby calves. No ESI established — contact manufacturer 1800 665 866',
      withholdingDays: 4, esiDays: 0 },
    { name: 'Neo-Sulcin', activeIngredient: 'Neomycin + Sulfonamides',
      category: 'Antibiotic', landRate: 'N/A', animalRate: '1 tablet/35kg bodyweight',
      withholdingPeriod: 'Meat: 14 days (calves), 28 days (horses). No ESI established for this product',
      withholdingDays: 28, esiDays: 0 },
    { name: 'Orbenin Eye Ointment', activeIngredient: 'Cloxacillin Benzathine 166.7mg/g',
      category: 'Antibiotic', landRate: 'N/A', animalRate: '0.25-0.5 tube/eye',
      withholdingPeriod: 'Nil',
      withholdingDays: 0, esiDays: 0 },
  ];

  const allChemicals = [...chemicalDatabase, ...customChemicals];

  // ============================================
  // HELPER FUNCTIONS
  // ============================================

  // Get expiring chemicals (within 2 months)
  const getExpiringChemicals = () => {
    const today = new Date();
    const twoMonthsFromNow = new Date();
    twoMonthsFromNow.setMonth(today.getMonth() + 2);

    return inventory
      .filter(item => {
        if (!item.expiryDate) return false;
        const expiry = new Date(item.expiryDate);
        return expiry <= twoMonthsFromNow && expiry >= today;
      })
      .sort((a, b) => new Date(a.expiryDate) - new Date(b.expiryDate));
  };

  // Get available batches for a chemical
  const getAvailableBatches = (chemicalName) => {
    return inventory
      .filter(item => 
        item.chemicalName === chemicalName && 
        parseFloat(item.quantityRemainingNumber) > 0
      )
      .sort((a, b) => new Date(a.expiryDate) - new Date(b.expiryDate));
  };

  // Convert quantity to sensible display units
  // eslint-disable-next-line no-unused-vars
  const toSensibleUnits = (quantity, unit) => {
    const qty = parseFloat(quantity);
    
    // Convert mL to L if >= 1000 mL
    if (unit === 'mL' && qty >= 1000) {
      return `${(qty / 1000).toFixed(2)} L`;
    }
    
    // Convert g to kg if >= 1000 g
    if (unit === 'g' && qty >= 1000) {
      return `${(qty / 1000).toFixed(2)} kg`;
    }
    
    // Otherwise return as-is
    return `${qty.toFixed(2)} ${unit}`;
  };

  // ============================================
  // NEW: LOOKUP & POPULATE FROM LABEL FUNCTIONS
  // ============================================

  const handleLookup = async () => {
    if (!purchaseForm.chemicalName) {
    // Prevent multiple simultaneous lookups
    if (isLookingUp) {
      alert('Already looking up chemical, please wait....');
      return;
    }

      alert('Please enter a chemical name first');
      return;
    }

    // Check if running on localhost
    setIsLookingUp(true); // Disable button

    if (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1') {
      alert('\u26a0\ufe0f Lookup functionality requires deployment\n\nThe web search feature is restricted on localhost for security reasons. Please deploy the app to Netlify or similar hosting to use this feature.\n\nFor now, please enter chemical details manually or use "Populate from Label".');
      return;
    }

    try {
      // Call Netlify function for chemical lookup
      const response = await fetch("/.netlify/functions/lookup-chemical", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "x-app-secret": process.env.REACT_APP_SECRET || ''
        },
        body: JSON.stringify({
          chemicalName: purchaseForm.chemicalName
        })
      });

      const data = await response.json();
      
      // Check if there's an error in the response
      if (data.error) {
        alert(`Lookup failed: ${data.error}`);
        return;
      }
      
      // Extract text from response
      if (!data.content || !Array.isArray(data.content)) {
        alert('Invalid response from lookup service. Please try again.');
        return;
      }
      
      const text = data.content.map(item => item.type === "text" ? item.text : "").join("\n");
      
      // Try to parse JSON from response
      const jsonMatch = text.match(/\{[\s\S]*\}/);
      if (jsonMatch) {
        const chemicalInfo = JSON.parse(jsonMatch[0]);
        
        // Check if product was not found
        if (chemicalInfo.error) {
          alert(`\u274c ${chemicalInfo.error}\n\nTry:\n\u2022 Checking spelling\n\u2022 Using brand name or generic name\n\u2022 Entering details manually`);
          return;
        }
        
        // Extract application rate number and unit
        let appRateNumber = '';
        let appRateUnit = 'L/ha';
        const rate = chemicalInfo.landRate || chemicalInfo.animalRate;
        if (rate && rate !== 'N/A') {
          const rateMatch = rate.match(/(\d+\.?\d*)\s*(L\/ha|mL\/ha|kg\/ha|g\/ha|mL\/animal|mL\/kg)/);
          if (rateMatch) {
            appRateNumber = rateMatch[1];
            appRateUnit = rateMatch[2];
          }
        }
        
        // Show confirmation dialog
        showConfirm(
          `Chemical Found: ${chemicalInfo.chemicalName || purchaseForm.chemicalName}`,
          `Active Ingredient: ${chemicalInfo.activeIngredient || 'Not found'}\nCategory: ${chemicalInfo.category || 'Not found'}\nWithholding Period: ${chemicalInfo.withholdingPeriod || 'Not found'}\nApplication Rate: ${rate || 'Not found'}\n\nPopulate these fields?`,
          () => {
            setPurchaseForm(prev => ({
              ...prev,
              chemicalName: chemicalInfo.chemicalName || prev.chemicalName,
              activeIngredient: chemicalInfo.activeIngredient || prev.activeIngredient,
              category: chemicalInfo.category || prev.category,
              withholdingPeriod: chemicalInfo.withholdingPeriod || prev.withholdingPeriod,
              landRate: chemicalInfo.landRate || prev.landRate,
              animalRate: chemicalInfo.animalRate || prev.animalRate,
              applicationRateNumber: appRateNumber || prev.applicationRateNumber,
              applicationRateUnit: appRateUnit || prev.applicationRateUnit
            }));
          },
          { confirmLabel: 'Populate Fields', confirmClass: 'bg-green-600 text-white', cancelLabel: 'Cancel' }
        );
      } else {
        alert('Could not find reliable information. Please enter manually or try a different search term.');
      }
    } catch (error) {
      alert('Error looking up chemical information: ' + error.message);
    } finally {
      setIsLookingUp(false); // Re-enable button
    }
  };

  const handlePopulateFromLabel = (context = 'full') => {
    setLabelPhotoContext(context);
    setShowPhotoInstructions(true);
  };

  // OPTIMIZED: Resize images before storing to reduce API costs by 95%
  const resizeImageForAPI = (file, maxWidth = 800, quality = 0.85) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      
      reader.onload = (e) => {
        const img = new Image();
        
        img.onload = () => {
          // Calculate new dimensions
          let width = img.width;
          let height = img.height;
          
          if (width > maxWidth) {
            const ratio = maxWidth / width;
            width = maxWidth;
            height = height * ratio;
          }
          
          // Create canvas and resize
          const canvas = document.createElement('canvas');
          canvas.width = width;
          canvas.height = height;
          
          const ctx = canvas.getContext('2d');
          ctx.drawImage(img, 0, 0, width, height);
          
          // Convert to blob
          canvas.toBlob(
            (blob) => {
              if (blob) {
                // Convert blob to base64
                const blobReader = new FileReader();
                blobReader.onloadend = () => {
                  const base64 = blobReader.result.split(',')[1];
                  resolve({
                    base64: base64,
                    dataUrl: blobReader.result,
                    mimeType: 'image/jpeg',
                    originalSize: file.size,
                    resizedSize: blob.size
                  });
                };
                blobReader.readAsDataURL(blob);
              } else {
                reject(new Error('Canvas to blob conversion failed'));
              }
            },
            'image/jpeg',
            quality
          );
        };
        
        img.onerror = () => reject(new Error('Image load failed'));
        img.src = e.target.result;
      };
      
      reader.onerror = () => reject(new Error('File read failed'));
      reader.readAsDataURL(file);
    });
  };

  const captureLabelPhoto = () => {
    if (labelPhotos.length >= 3) {
      alert('Maximum 3 photos allowed');
      return;
    }

    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/*';
    input.capture = 'environment';
    
    input.onchange = async (event) => {
      const file = event.target.files[0];
      if (file) {
        try {
          // OPTIMIZED: Resize image before storing (reduces API costs by 95%)
          const resizedPhoto = await resizeImageForAPI(file);
          
          console.log('Image optimization:');
          console.log(`Original: ${(resizedPhoto.originalSize / 1024 / 1024).toFixed(2)} MB`);
          console.log(`Resized: ${(resizedPhoto.resizedSize / 1024).toFixed(2)} KB`);
          console.log(`Token savings: ~${((1 - resizedPhoto.resizedSize / resizedPhoto.originalSize) * 100).toFixed(0)}%`);
          
          setLabelPhotos(prev => [...prev, {
            file: file,
            dataUrl: resizedPhoto.dataUrl,
            base64: resizedPhoto.base64,
            mimeType: resizedPhoto.mimeType
          }]);
        } catch (error) {
          console.error('Image resize error:', error);
          alert('Error processing image: ' + error.message);
        }
      }
    };
    
    input.click();
  };

  const processLabelPhotos = async () => {
    if (labelPhotos.length === 0) {
      alert('Please capture at least one photo');
      return;
    }

    setIsProcessingPhotos(true);
    setShowPhotoInstructions(false);

    try {
      // Prepare images for Netlify function
      const photos = labelPhotos.map(photo => ({
        mimeType: photo.mimeType || "image/jpeg",
        base64: photo.base64
      }));

      // Call Netlify function for label reading
      const response = await fetch("/.netlify/functions/read-label", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "x-app-secret": process.env.REACT_APP_SECRET || ''
        },
        body: JSON.stringify({ photos })
      });

      // Check if response is OK
      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Function error (${response.status}): ${errorText}`);
      }

      // Get response text first to debug
      const responseText = await response.text();
      
      // Try to parse JSON
      let data;
      try {
        data = JSON.parse(responseText);
      } catch (parseError) {
        console.error('Failed to parse response:', responseText);
        throw new Error('Invalid response from server. Check Netlify function logs.');
      }

      // Check if response has error
      if (data.error) {
        throw new Error(data.error);
      }
      
      // Check if content exists before trying to use it
      if (!data.content || !Array.isArray(data.content)) {
        console.error('Invalid response structure:', data);
        throw new Error('Invalid response from AI - content missing. This usually means the Netlify function crashed. Check function logs in Netlify.');
      }
      
      const text = data.content.map(item => item.type === "text" ? item.text : "").join("\n");
      
      // Parse extracted data
      const jsonMatch = text.match(/\{[\s\S]*\}/);
      if (!jsonMatch) {
        throw new Error('Could not parse label data');
      }

      let extractedInfo = JSON.parse(jsonMatch[0]);

      // BATCH CONTEXT: only apply batch/expiry/container — never touch chemical identity fields
      if (labelPhotoContext === 'batch') {
        const batchUpdates = {};
        if (extractedInfo.batchNumber) batchUpdates.batchNumber = extractedInfo.batchNumber;
        if (extractedInfo.expiryDate) batchUpdates.expiryDate = extractedInfo.expiryDate;
        if (extractedInfo.manufacturingDate) {
          batchUpdates.manufacturingDate = extractedInfo.manufacturingDate;
          if (!extractedInfo.expiryDate) {
            const mfgDate = new Date(extractedInfo.manufacturingDate);
            const expiryDate = new Date(mfgDate);
            expiryDate.setFullYear(expiryDate.getFullYear() + 3);
            batchUpdates.expiryDate = expiryDate.toISOString().split('T')[0];
          }
        }
        if (extractedInfo.containerSizeNumber) {
          batchUpdates.containerSizeNumber = extractedInfo.containerSizeNumber;
          if (extractedInfo.containerSizeUnit) {
            batchUpdates.containerSizeUnit = extractedInfo.containerSizeUnit;
            batchUpdates.quantityRemainingUnit = extractedInfo.containerSizeUnit;
          }
          batchUpdates.quantityRemainingNumber = extractedInfo.containerSizeNumber;
        }
        setPurchaseForm(prev => ({ ...prev, ...batchUpdates }));
        setLabelPhotos([]);
        setIsProcessingPhotos(false);
        alert('\u2713 Batch/expiry information filled in from label.');
        return;
      }

      // Check if we got a chemical name
      if (!extractedInfo.chemicalName) {
        throw new Error('AI could not extract chemical name from label. Please ensure the photo shows the product name clearly.');
      }

      // Check if chemical exists in database
      const existingChemical = allChemicals.find(c => 
        c.name.toLowerCase() === extractedInfo.chemicalName.toLowerCase()
      );

      
      if (existingChemical) {
        // Chemical exists in database - use database info, only keep batch/expiry/container from AI
        extractedInfo = {
          chemicalName: existingChemical.name,
          activeIngredient: existingChemical.activeIngredient,
          category: existingChemical.category,
          withholdingPeriod: existingChemical.withholdingPeriod,
          landRate: existingChemical.landRate,
          animalRate: existingChemical.animalRate,
          // Keep AI-extracted batch/container/date info
          batchNumber: extractedInfo.batchNumber,
          containerSize: extractedInfo.containerSize,
          containerSizeNumber: extractedInfo.containerSizeNumber,
          containerSizeUnit: extractedInfo.containerSizeUnit,
          expiryDate: extractedInfo.expiryDate,
          manufacturingDate: extractedInfo.manufacturingDate,
          isExisting: true  // Flag to indicate this is existing chemical
        };
        
        alert(`\u2713 Chemical found in database!\n\n"${existingChemical.name}" already exists.\n\nUsing database information for:\n\u2022 Active Ingredient\n\u2022 Category\n\u2022 Withholding Period\n\u2022 Application Rates\n\nOnly batch/expiry/container info from label will be used.`);
        
      } else {
        // NEW chemical - not in database
        
        // Check for missing required fields
        const missingFields = [];
        if (!extractedInfo.activeIngredient) missingFields.push('Active Ingredient');
        if (!extractedInfo.category) missingFields.push('Category');
        if (!extractedInfo.withholdingPeriod && extractedInfo.category !== 'Herbicide' && extractedInfo.category !== 'Insecticide' && extractedInfo.category !== 'Fungicide' && extractedInfo.category !== 'Marker') {
          missingFields.push('Withholding Period');
        }
        if (!extractedInfo.landRate && !extractedInfo.animalRate) {
          missingFields.push('Application/Dose Rate');
        }
        
        // Store missing fields for display in confirmation dialog
        extractedInfo.missingFields = missingFields;
        
        extractedInfo.isExisting = false;  // Flag to indicate this is new chemical
      }

      // Set up confirmation checkboxes (all ticked by default)
      const checks = {
        chemicalName: true,
        activeIngredient: true,
        batchNumber: true,
        containerSize: true,
        expiryDate: true,
        manufacturingDate: true,
        withholdingPeriod: true,
        applicationRate: true,
        category: true
      };

      setConfirmationChecks(checks);
      setExtractedData(extractedInfo);
      setShowConfirmationDialog(true);
      setIsProcessingPhotos(false);

    } catch (error) {
      setIsProcessingPhotos(false);
      showConfirm(
        'Photo Processing Error',
        'Error processing photos: ' + error.message + '\n\nWould you like to retry with new photos?',
        () => {
          setLabelPhotos([]);
          setShowPhotoInstructions(true);
        },
        { confirmLabel: 'Retry', confirmClass: 'bg-blue-600 text-white', cancelLabel: 'Cancel' }
      );
      setLabelPhotos([]);
    }
  };

  // eslint-disable-next-line no-unused-vars
  const handleConfirmExtractedData = () => {
    // Validate mandatory fields
    const missingFields = [];

    if (!extractedData.chemicalName && confirmationChecks.chemicalName) {
      missingFields.push('Chemical Name');
    }
    if ((!extractedData.containerSizeNumber || !extractedData.containerSizeUnit) && confirmationChecks.containerSize) {
      missingFields.push('Container Size');
    }
    if (!extractedData.batchNumber && confirmationChecks.batchNumber) {
      missingFields.push('Batch Number');
    }
    if (!extractedData.expiryDate && !extractedData.manufacturingDate) {
      missingFields.push('Expiry Date or Manufacturing Date');
    }

    // For NEW chemicals, check additional required fields
    if (!extractedData.isExisting) {
      if (!extractedData.activeIngredient && confirmationChecks.activeIngredient) {
        missingFields.push('Active Ingredient');
      }
      if (!extractedData.category && confirmationChecks.category) {
        missingFields.push('Category');
      }
      // Withholding period required for animal medications
      if (!extractedData.withholdingPeriod && confirmationChecks.withholdingPeriod && 
          extractedData.category && 
          ['Parasiticide', 'Vaccine', 'Antibiotic', 'Anthelmintic', 'Supplement', 'Hormone'].includes(extractedData.category)) {
        missingFields.push('Withholding Period (required for animal medications)');
      }
      // Application/dose rate required
      if (!extractedData.landRate && !extractedData.animalRate && confirmationChecks.applicationRate) {
        missingFields.push('Application Rate or Dose Rate');
      }
    }

    // Missing fields are shown in the confirmation dialog — user proceeds regardless

    // Apply checked fields to purchase form
    const updates = {};
    
    if (confirmationChecks.chemicalName && extractedData.chemicalName) {
      updates.chemicalName = extractedData.chemicalName;
      
      // Check if this is an existing chemical or new
      if (extractedData.isExisting) {
        // Auto-select the existing chemical from dropdown
        updates.selectedChemical = extractedData.chemicalName;
      } else {
        // New chemical - select "Other"
        updates.selectedChemical = 'Other';
      }
    }
    if (confirmationChecks.activeIngredient && extractedData.activeIngredient) {
      updates.activeIngredient = extractedData.activeIngredient;
    }
    if (confirmationChecks.batchNumber && extractedData.batchNumber) {
      updates.batchNumber = extractedData.batchNumber;
    }
    if (confirmationChecks.containerSize && extractedData.containerSizeNumber) {
      updates.containerSizeNumber = extractedData.containerSizeNumber;
      updates.containerSizeUnit = extractedData.containerSizeUnit;
      updates.quantityRemainingNumber = extractedData.containerSizeNumber;
      updates.quantityRemainingUnit = extractedData.containerSizeUnit;
    }
    if (confirmationChecks.expiryDate && extractedData.expiryDate) {
      updates.expiryDate = extractedData.expiryDate;
    }
    if (confirmationChecks.manufacturingDate && extractedData.manufacturingDate) {
      updates.manufacturingDate = extractedData.manufacturingDate;
      // Auto-calculate expiry if not already set
      if (!extractedData.expiryDate) {
        const mfgDate = new Date(extractedData.manufacturingDate);
        const expiryDate = new Date(mfgDate);
        expiryDate.setFullYear(expiryDate.getFullYear() + 3);
        updates.expiryDate = expiryDate.toISOString().split('T')[0];
      }
    }
    if (confirmationChecks.withholdingPeriod && extractedData.withholdingPeriod) {
      updates.withholdingPeriod = extractedData.withholdingPeriod;
    }
    if (extractedData.withholdingDays !== undefined && extractedData.withholdingDays !== null) {
      updates.withholdingDays = String(extractedData.withholdingDays);
    }
    if (extractedData.esiDays !== undefined && extractedData.esiDays !== null) {
      updates.esiDays = String(extractedData.esiDays);
    }
    if (confirmationChecks.applicationRate) {
      if (extractedData.landRate) updates.landRate = extractedData.landRate;
      if (extractedData.animalRate) updates.animalRate = extractedData.animalRate;
      if (extractedData.applicationRate && !extractedData.landRate && !extractedData.animalRate) {
        updates.landRate = extractedData.applicationRate;
      }
      // Extract number and unit from application rate
      if (extractedData.applicationRate) {
        const rateMatch = extractedData.applicationRate.match(/(\d+\.?\d*)\s*(L\/ha|mL\/ha|kg\/ha|g\/ha)/);
        if (rateMatch) {
          updates.applicationRateNumber = rateMatch[1];
          updates.applicationRateUnit = rateMatch[2];
        }
      }
    }
    if (confirmationChecks.category && extractedData.category) {
      updates.category = extractedData.category;
    }

    setPurchaseForm(prev => ({ ...prev, ...updates }));
    
    // Reset states
    setShowConfirmationDialog(false);
    setExtractedData(null);
    setLabelPhotos([]);
    setConfirmationChecks({});
  };

  // ============================================
  // EXISTING FUNCTIONS
  // ============================================

  const completeSetup = () => {
    if (!setupFarmerName || !setupPropertyAddress) {
      alert('Please enter both your name and property address');
      return;
    }

    localStorage.setItem('defaultFarmerName', setupFarmerName);
    localStorage.setItem('propertyAddress', setupPropertyAddress);
    localStorage.setItem('setupComplete', 'true');

    setFarmerName(setupFarmerName);
    setPropertyAddress(setupPropertyAddress);

    // Seed the farm list from the property address entered at setup
    setFarmList([setupPropertyAddress]);
    setLastUsedFarm(setupPropertyAddress);
    setLandForm(prev => ({ ...prev, farmName: setupPropertyAddress }));
    setAnimalForm(prev => ({ ...prev, farmName: setupPropertyAddress }));

    setIsSetupComplete(true);
  };

  const handlePurchaseChemicalSelect = (chemicalName) => {
    if (chemicalName === 'Other') {
      setPurchaseForm({
        ...purchaseForm,
        selectedChemical: 'Other',
        chemicalName: '',
        activeIngredient: '',
        category: '',
        landRate: '',
        animalRate: '',
        withholdingPeriod: '',
        animalRateNumber: '',
        animalRateUnit: 'mL/kg',
        withholdingDays: '',
        esiDays: '',
        applicationRateNumber: '',
        applicationRateUnit: 'L/ha'
      });
    } else {
      const chemical = allChemicals.find(c => c.name === chemicalName);
      if (chemical) {
        // Extract application rate number and unit from landRate or animalRate
        let appRateNumber = '';
        let appRateUnit = 'L/ha';
        
        const rate = chemical.landRate || chemical.animalRate;
        if (rate && rate !== 'N/A' && rate !== '') {
          const rateMatch = rate.match(/(\d+\.?\d*)\s*(L\/ha|mL\/ha|kg\/ha|g\/ha|mL\/animal|mL\/kg|tablets\/animal|syringes\/animal)/);
          if (rateMatch) {
            appRateNumber = rateMatch[1];
            appRateUnit = rateMatch[2];
          }
        }
        
        // Auto-populate container size units based on category
        let defaultUnit = 'L'; // default for land chemicals
        if (['Parasiticide', 'Vaccine', 'Supplement', 'Hormone', 'Antibiotic', 'Anthelmintic'].includes(chemical.category)) {
          defaultUnit = 'mL'; // animal medications default to mL
        }
        
        setPurchaseForm({
          ...purchaseForm,
          selectedChemical: chemicalName,
          chemicalName: chemical.name,
          activeIngredient: chemical.activeIngredient,
          category: chemical.category,
          landRate: chemical.landRate,
          animalRate: chemical.animalRate,
          withholdingPeriod: chemical.withholdingPeriod,
          applicationRateNumber: appRateNumber,
          applicationRateUnit: appRateUnit,
          containerSizeUnit: defaultUnit,
          quantityRemainingUnit: defaultUnit
        });
      }
    }
  };

  const handleManufacturingDateChange = (date) => {
    setPurchaseForm(prev => {
      const newForm = { ...prev, manufacturingDate: date };
      
      // Auto-calculate expiry date (+3 years)
      if (date) {
        const mfgDate = new Date(date);
        const expiryDate = new Date(mfgDate);
        expiryDate.setFullYear(expiryDate.getFullYear() + 3);
        newForm.expiryDate = expiryDate.toISOString().split('T')[0];
      }
      
      return newForm;
    });
  };

  const savePurchase = () => {
    // Validation
    if (!purchaseForm.chemicalName || !purchaseForm.containerSizeNumber || 
        !purchaseForm.batchNumber || !purchaseForm.expiryDate) {
      alert('Please fill in all mandatory fields:\n- Chemical Name\n- Container Size\n- Batch Number\n- Expiry Date (or Manufacturing Date)');
      return;
    }

    // Build rate and withholding strings from split fields (used for both new chem and inventory item)
    const builtRate = purchaseForm.animalRateNumber
      ? `${purchaseForm.animalRateNumber} ${purchaseForm.animalRateUnit}`
      : purchaseForm.animalRate;
    const builtWHP = purchaseForm.withholdingDays
      ? `${purchaseForm.withholdingDays} days`
      : purchaseForm.withholdingPeriod;

    // If adding new chemical, add to custom chemicals
    if (purchaseForm.selectedChemical === 'Other') {
      const newChem = {
        name: purchaseForm.chemicalName,
        activeIngredient: purchaseForm.activeIngredient,
        category: purchaseForm.category,
        landRate: purchaseForm.landRate,
        animalRate: builtRate,
        withholdingPeriod: builtWHP
      };
      const updated = [...customChemicals, newChem];
      setCustomChemicals(updated);
      localStorage.setItem('customChemicals', JSON.stringify(updated));
    }

    // Create inventory item
    const newInventoryItem = {
      id: Date.now(),
      chemicalName: purchaseForm.chemicalName,
      activeIngredient: purchaseForm.activeIngredient,
      category: purchaseForm.category,
      withholdingPeriod: purchaseForm.withholdingDays ? `${purchaseForm.withholdingDays} days` : purchaseForm.withholdingPeriod,
      withholdingDays: purchaseForm.withholdingDays !== '' ? parseInt(purchaseForm.withholdingDays, 10) : null,
      esiDays: purchaseForm.esiDays !== '' ? parseInt(purchaseForm.esiDays, 10) : null,
      animalRate: builtRate,
      containerSizeNumber: purchaseForm.containerSizeNumber,
      containerSizeUnit: purchaseForm.containerSizeUnit,
      quantityRemainingNumber: purchaseForm.quantityRemainingNumber || purchaseForm.containerSizeNumber,
      quantityRemainingUnit: purchaseForm.quantityRemainingUnit || purchaseForm.containerSizeUnit,
      batchNumber: purchaseForm.batchNumber,
      manufacturingDate: purchaseForm.manufacturingDate,
      expiryDate: purchaseForm.expiryDate,
      purchaseDate: purchaseForm.purchaseDate,
      applicationRate: purchaseForm.applicationRateNumber 
        ? `${purchaseForm.applicationRateNumber} ${purchaseForm.applicationRateUnit}`
        : ''
    };

    setInventory([newInventoryItem, ...inventory]);

    // Reset form
    setPurchaseForm({
      selectedChemical: '',
      chemicalName: '',
      activeIngredient: '',
      category: '',
      landRate: '',
      animalRate: '',
      animalRateNumber: '',
      animalRateUnit: 'mL/kg',
      withholdingDays: '',
      withholdingPeriod: '',
      esiDays: '',
      containerSizeNumber: '',
      containerSizeUnit: 'L',
      quantityRemainingNumber: '',
      quantityRemainingUnit: 'L',
      batchNumber: '',
      manufacturingDate: '',
      expiryDate: '',
      purchaseDate: new Date().toISOString().split('T')[0],
      applicationRateNumber: '',
      applicationRateUnit: 'L/ha'
    });

    setSection('home');
    alert('\u2713 Purchase recorded and added to inventory!');
  };

  const deductFromInventory = (chemicalName, batchNumber, quantityUsed, usageUnit) => {
    if (!batchNumber) return;

    setInventory(prev => prev.map(item => {
      if (item.chemicalName === chemicalName && item.batchNumber === batchNumber) {
        const remaining = parseFloat(item.quantityRemainingNumber);
        let usedInInventoryUnits = parseFloat(quantityUsed);
        
        if (usageUnit === 'mL' && item.quantityRemainingUnit === 'L') {
          usedInInventoryUnits = usedInInventoryUnits / 1000;
        } else if (usageUnit === 'L' && item.quantityRemainingUnit === 'mL') {
          usedInInventoryUnits = usedInInventoryUnits * 1000;
        } else if (usageUnit === 'g' && item.quantityRemainingUnit === 'kg') {
          usedInInventoryUnits = usedInInventoryUnits / 1000;
        } else if (usageUnit === 'kg' && item.quantityRemainingUnit === 'g') {
          usedInInventoryUnits = usedInInventoryUnits * 1000;
        }
        
        const newRemaining = Math.max(0, remaining - usedInInventoryUnits);
        
        return {
          ...item,
          quantityRemainingNumber: newRemaining.toFixed(2).toString()
        };
      }
      return item;
    }));
  };

  // Cascade deduction across multiple batches for land chemicals.
  // Drains batches in order until the full quantity is consumed.
  const deductFromInventoryCascade = (chemicalName, selectedBatches, totalQty, usageUnit, inventorySnapshot) => {
    // Convert a quantity to base units (mL or g) for comparison
    const toBase = (qty, unit) => {
      const n = parseFloat(qty) || 0;
      if (unit === 'L' || unit === 'kg') return n * 1000;
      return n;
    };
    const fromBase = (base, unit) => {
      if (unit === 'L' || unit === 'kg') return base / 1000;
      return base;
    };

    let remainingToDeduct = toBase(totalQty, usageUnit);

    // Build a map of item.id -> amount to deduct (keyed by id, not batchNumber,
    // so two inventory items with the same batch number are handled independently)
    const deductions = {};

    for (const sb of selectedBatches) {
      if (remainingToDeduct <= 0) break;
      if (!sb.batchId) continue;

      const invItem = inventorySnapshot.find(
        item => item.chemicalName === chemicalName && String(item.id) === String(sb.batchId)
      );
      if (!invItem) continue;

      const availableBase = toBase(invItem.quantityRemainingNumber, invItem.quantityRemainingUnit);
      const deductBase = Math.min(remainingToDeduct, availableBase);
      deductions[invItem.id] = fromBase(deductBase, invItem.quantityRemainingUnit);
      remainingToDeduct -= deductBase;
    }

    // Apply all deductions in a single setInventory call
    setInventory(prev => prev.map(item => {
      if (item.chemicalName === chemicalName && deductions[item.id] !== undefined) {
        const remaining = parseFloat(item.quantityRemainingNumber);
        const newRemaining = Math.max(0, remaining - deductions[item.id]);
        return { ...item, quantityRemainingNumber: newRemaining.toFixed(2).toString() };
      }
      return item;
    }));
  };

  // Reverse a prior deduction when editing a record — adds quantity back to the batch.
  // Silently skips if the batch no longer exists in inventory.
  const addBackToInventory = (chemicalName, batchNumber, quantity, usageUnit) => {
    if (!batchNumber || !quantity || parseFloat(quantity) <= 0) return;
    setInventory(prev => prev.map(item => {
      if (item.chemicalName === chemicalName && item.batchNumber === batchNumber) {
        const remaining = parseFloat(item.quantityRemainingNumber);
        let addInInventoryUnits = parseFloat(quantity);
        if (usageUnit === 'mL' && item.quantityRemainingUnit === 'L') addInInventoryUnits /= 1000;
        else if (usageUnit === 'L' && item.quantityRemainingUnit === 'mL') addInInventoryUnits *= 1000;
        else if (usageUnit === 'g' && item.quantityRemainingUnit === 'kg') addInInventoryUnits /= 1000;
        else if (usageUnit === 'kg' && item.quantityRemainingUnit === 'g') addInInventoryUnits *= 1000;
        const containerSize = parseFloat(item.containerSizeNumber) || 0;
        // Cap at container size — can't restore more than was originally purchased
        const cap = containerSize > 0 ? containerSize : remaining + addInInventoryUnits;
        const newRemaining = Math.min(cap, remaining + addInInventoryUnits);
        return { ...item, quantityRemainingNumber: newRemaining.toFixed(2).toString() };
      }
      return item;
    }));
  };

  // eslint-disable-next-line no-unused-vars
  const capturePhoto = (formType) => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/*';
    input.capture = 'environment';
    
    input.onchange = (event) => {
      const file = event.target.files[0];
      if (file) {
        const dateStr = new Date().toLocaleDateString('en-AU').replace(/\//g, '');
        const productName = formType === 'land' 
          ? ((landForm.chemicals && landForm.chemicals[0] && landForm.chemicals[0].productName) || 'Chemical').replace(/\s+/g, '_')
          : (animalForm.productName || animalForm.selectedMedication || 'Medication').replace(/\s+/g, '_');
        const timestamp = Date.now();
        const filename = `${productName}_${dateStr}_${timestamp}.jpg`;
        
        const currentForm = formType === 'land' ? landForm : animalForm;
        const updatedPhotos = [...currentForm.photos, filename];
        
        if (formType === 'land') {
          setLandForm({ ...landForm, photos: updatedPhotos });
        } else {
          setAnimalForm({ ...animalForm, photos: updatedPhotos });
        }
        
        alert(`\u2713 Photo saved to Downloads folder!\nFilename: ${filename}\n\nFind this photo in your Downloads folder to attach to emails.`);
      }
    };
    
    input.click();
  };

  // eslint-disable-next-line no-unused-vars
  const uploadPhoto = (formType) => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/*';
    
    input.onchange = (event) => {
      const file = event.target.files[0];
      if (file) {
        const dateStr = new Date().toLocaleDateString('en-AU').replace(/\//g, '');
        const productName = formType === 'land' 
          ? ((landForm.chemicals && landForm.chemicals[0] && landForm.chemicals[0].productName) || 'Chemical').replace(/\s+/g, '_')
          : (animalForm.productName || animalForm.selectedMedication || 'Medication').replace(/\s+/g, '_');
        const timestamp = Date.now();
        const filename = `${productName}_${dateStr}_${timestamp}.jpg`;
        
        const currentForm = formType === 'land' ? landForm : animalForm;
        const updatedPhotos = [...currentForm.photos, filename];
        
        if (formType === 'land') {
          setLandForm({ ...landForm, photos: updatedPhotos });
        } else {
          setAnimalForm({ ...animalForm, photos: updatedPhotos });
        }
        
        alert(`\u2713 Photo uploaded!\nFilename: ${filename}\n\nFind this photo in your Downloads folder to attach to emails.`);
      }
    };
    
    input.click();
  };

  const handleLandChemicalChange = (index, chemicalName) => {
    const updatedChemicals = [...landForm.chemicals];
    if (chemicalName === 'Other') {
      updatedChemicals[index] = {
        ...updatedChemicals[index],
        selectedChemical: 'Other',
        productName: '',
        activeIngredient: '',
        batchNumber: ''
      };
    } else {
      const chemical = allChemicals.find(c => c.name === chemicalName);
      if (chemical) {
        // Prefer withholdingPeriod/withholdingDays from inventory if available (may be updated from label scan)
        const invItem = inventory.find(i => i.chemicalName === chemicalName && i.withholdingPeriod);
        updatedChemicals[index] = {
          ...updatedChemicals[index],
          selectedChemical: chemicalName,
          productName: chemical.name,
          activeIngredient: chemical.activeIngredient,
          withholdingPeriod: (invItem && invItem.withholdingPeriod) || chemical.withholdingPeriod || '',
          withholdingDays: (invItem && invItem.withholdingDays != null)
            ? String(invItem.withholdingDays)
            : (chemical.withholdingDays != null ? String(chemical.withholdingDays) : ''),
          batchNumber: ''
        };
      } else {
        updatedChemicals[index] = {
          ...updatedChemicals[index],
          selectedChemical: chemicalName,
          batchNumber: ''
        };
      }
    }
    setLandForm({ ...landForm, chemicals: updatedChemicals });
  };

  // Calculate quantity for a single chemical entry given current drum settings
  const calcChemQty = (chem, drumQty, drumUnit, areaHa) => {
    const drumL = drumUnit === 'gal'
      ? parseFloat(drumQty) * IMPERIAL_GALLON_TO_L
      : parseFloat(drumQty);

    if (chem.rateMode === 'dilution') {
      const rateQty = parseFloat(chem.rateQty);
      const waterQty = parseFloat(chem.rateWaterQty);
      if (!rateQty || !waterQty || waterQty <= 0 || !drumQty) return { qty: '', unit: chem.rateUnit };
      const raw = (rateQty / waterQty) * drumL;
      return { qty: raw.toFixed(2), unit: chem.rateUnit };
    } else {
      // area mode: rate per hectare
      const rateQty = parseFloat(chem.areaRateQty);
      const ha = parseFloat(areaHa);
      if (!rateQty || !ha) return { qty: '', unit: chem.areaRateUnit };
      return { qty: (rateQty * ha).toFixed(2), unit: chem.areaRateUnit };
    }
  };

  const updateChemicalField = (index, field, value) => {
    const updatedChemicals = [...landForm.chemicals];
    updatedChemicals[index] = { ...updatedChemicals[index], [field]: value };
    const c = updatedChemicals[index];
    const result = calcChemQty(c, landForm.totalDilutedVolumeQty, landForm.totalDilutedVolumeUnit, landForm.areaHectares);
    updatedChemicals[index].calculatedQty = result.qty;
    updatedChemicals[index].calculatedQtyUnit = result.unit;
    setLandForm({ ...landForm, chemicals: updatedChemicals });
  };

  const recalcAllChemicals = (newQty, newUnit) => {
    const qty = newQty !== undefined ? newQty : landForm.totalDilutedVolumeQty;
    const unit = newUnit !== undefined ? newUnit : landForm.totalDilutedVolumeUnit;
    const updatedChemicals = landForm.chemicals.map(chem => {
      const result = calcChemQty(chem, qty, unit, landForm.areaHectares);
      return { ...chem, calculatedQty: result.qty, calculatedQtyUnit: result.unit };
    });
    const updates = { chemicals: updatedChemicals };
    if (newQty !== undefined) updates.totalDilutedVolumeQty = newQty;
    if (newUnit !== undefined) updates.totalDilutedVolumeUnit = newUnit;
    setLandForm({ ...landForm, ...updates });
  };

  const addChemicalEntry = () => {
    if (landForm.chemicals.length >= 6) {
      alert('Maximum 6 chemicals per application record');
      return;
    }
    setLandForm({ ...landForm, chemicals: [...landForm.chemicals, emptyChemicalEntry()] });
  };

  const removeChemicalEntry = (index) => {
    if (landForm.chemicals.length === 1) return;
    const updated = landForm.chemicals.filter((_, i) => i !== index);
    setLandForm({ ...landForm, chemicals: updated });
  };

  const handleMedicationEntryChange = (index, medicationName) => {
    const purposeMap = {
      'Parasiticide': 'Parasite control',
      'Vaccine': 'Vaccination',
      'Supplement': 'Nutritional supplement',
      'Anthelmintic': 'Worm control',
      'Antibiotic': 'Bacterial infection treatment'
    };

    const updated = [...animalForm.medications];

    if (medicationName === 'Other') {
      updated[index] = {
        ...emptyMedicationEntry(),
        selectedMedication: 'Other',
      };
    } else if (medicationName === '') {
      updated[index] = { ...emptyMedicationEntry() };
    } else {
      const medication = allChemicals.find(c => c.name === medicationName);
      if (medication) {
        let parsedRateNum = '';
        let parsedRateUnit = 'mL/kg';
        if (medication.animalRate) {
          const r = medication.animalRate.trim();
          const spaceUnit = r.match(/^([\d.]+)\s+(mL\/kg|mL\/animal|tablets\/kg|tablets\/animal|syringes\/animal)$/i);
          const mKg = r.match(/^([\d.]+)\s*mL\s*\/\s*([\d.]+)\s*kg$/i);
          const mAnimal = r.match(/^([\d.]+)\s*mL\s*\/\s*animal$/i);
          const tKg = r.match(/^([\d.]+)\s*tablets?\s*\/\s*([\d.]+)?\s*kg$/i);
          const tAnimal = r.match(/^([\d.]+)\s*tablets?\s*\/\s*animal$/i);
          const sAnimal = r.match(/^([\d.]+)\s*syringes?\s*\/\s*animal$/i);
          if (spaceUnit) { parsedRateNum = spaceUnit[1]; parsedRateUnit = spaceUnit[2].toLowerCase(); }
          else if (mKg) { parsedRateNum = (parseFloat(mKg[1]) / parseFloat(mKg[2])).toString(); parsedRateUnit = 'mL/kg'; }
          else if (mAnimal) { parsedRateNum = mAnimal[1]; parsedRateUnit = 'mL/animal'; }
          else if (tKg) { const d = tKg[2] ? parseFloat(tKg[2]) : 1; parsedRateNum = (parseFloat(tKg[1]) / d).toFixed(4); parsedRateUnit = 'tablets/kg'; }
          else if (tAnimal) { parsedRateNum = tAnimal[1]; parsedRateUnit = 'tablets/animal'; }
          else if (sAnimal) { parsedRateNum = sAnimal[1]; parsedRateUnit = 'syringes/animal'; }
        }
        // Prefer withholdingDays/esiDays from inventory if available, else fall back to chemical database
        const invItem = inventory.find(i => i.chemicalName === medicationName);
        updated[index] = {
          ...updated[index],
          selectedMedication: medicationName,
          productName: medication.name,
          activeIngredient: medication.activeIngredient,
          doseRate: medication.animalRate,
          doseRateNumber: parsedRateNum,
          doseRateUnit: parsedRateUnit,
          doseRatePerKg: '',
          withholdingPeriod: medication.withholdingPeriod,
          withholdingDays: (invItem && invItem.withholdingDays != null)
            ? String(invItem.withholdingDays)
            : (medication.withholdingDays != null ? String(medication.withholdingDays) : ''),
          esiDays: (invItem && invItem.esiDays != null)
            ? String(invItem.esiDays)
            : (medication.esiDays != null ? String(medication.esiDays) : ''),
          treatmentPurpose: purposeMap[medication.category] || medication.category || '',
          batchNumber: '',
          calculatedDosePerAnimal: '',
          totalQuantity: '',
        };
      }
    }
    setAnimalForm({ ...animalForm, medications: updated });
  };

  const updateMedicationField = (index, field, value) => {
    const updated = [...animalForm.medications];
    updated[index] = { ...updated[index], [field]: value };
    setAnimalForm({ ...animalForm, medications: updated });
  };

  const addMedicationEntry = () => {
    if (animalForm.medications.length >= 6) {
      alert('Maximum 6 medications per batch record');
      return;
    }
    setAnimalForm({ ...animalForm, medications: [...animalForm.medications, emptyMedicationEntry()] });
  };

  const removeMedicationEntry = (index) => {
    if (animalForm.medications.length === 1) return;
    const updated = animalForm.medications.filter((_, i) => i !== index);
    setAnimalForm({ ...animalForm, medications: updated });
  };



  // Helper: calculate dose for a single medication entry given count + weight
  const calcMedicationDose = (med, animalCount, averageWeight) => {
    const { doseRateNumber, doseRateUnit, doseRatePerKg } = med;
    if (!animalCount || !doseRateNumber) return { dosePerAnimal: '', totalQuantity: '' };
    const rateNum = parseFloat(doseRateNumber);
    const count = parseFloat(animalCount);
    if (isNaN(rateNum) || isNaN(count) || count <= 0) return { dosePerAnimal: '', totalQuantity: '' };

    const isTablets = doseRateUnit === 'tablets/kg' || doseRateUnit === 'tablets/animal';
    const isSyringes = doseRateUnit === 'syringes/animal';
    const isDiscrete = isTablets || isSyringes;
    const roundToHalf = (n) => Math.round(n * 2) / 2;

    const perAnimalUnits = ['mL/animal', 'tablets/animal', 'syringes/animal'];
    const perKgUnits = ['mL/kg', 'g/kg', 'tablets/kg'];

    let exactDosePerAnimal;
    if (perAnimalUnits.includes(doseRateUnit)) {
      exactDosePerAnimal = rateNum;
    } else if (perKgUnits.includes(doseRateUnit)) {
      const weight = parseFloat(averageWeight);
      if (!averageWeight || isNaN(weight) || weight <= 0) return { dosePerAnimal: '', totalQuantity: '' };
      const perKg = doseRatePerKg && parseFloat(doseRatePerKg) > 0
        ? rateNum / parseFloat(doseRatePerKg)
        : rateNum;
      exactDosePerAnimal = perKg * weight;
    } else {
      return { dosePerAnimal: '', totalQuantity: '' };
    }

    const dosePerAnimal = isDiscrete ? roundToHalf(exactDosePerAnimal) : exactDosePerAnimal;
    const totalDose = isDiscrete ? dosePerAnimal * count : exactDosePerAnimal * count;
    return {
      dosePerAnimal: isDiscrete ? dosePerAnimal.toFixed(1) : dosePerAnimal.toFixed(2),
      totalQuantity: isDiscrete ? totalDose.toFixed(1) : totalDose.toFixed(2),
    };
  };

  // Reactive dosage calculation — recalculates all medications when count/weight/rate changes
  useEffect(() => {
    const { animalCount, averageWeight, medications } = animalForm;
    if (!medications || medications.length === 0) return;
    let changed = false;
    const updatedMeds = medications.map(med => {
      const { dosePerAnimal, totalQuantity } = calcMedicationDose(med, animalCount, averageWeight);
      if (dosePerAnimal !== med.calculatedDosePerAnimal || totalQuantity !== med.totalQuantity) {
        changed = true;
        return { ...med, calculatedDosePerAnimal: dosePerAnimal, totalQuantity };
      }
      return med;
    });
    if (changed) {
      setAnimalForm(prev => ({ ...prev, medications: updatedMeds }));
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    animalForm.animalCount,
    animalForm.averageWeight,
    // Stringify medications dose-relevant fields to detect per-med rate changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
    JSON.stringify((animalForm.medications || []).map(m => [m.doseRateNumber, m.doseRateUnit, m.doseRatePerKg]))
  ]);

  // Keep calculateDosage for legacy onBlur calls
  const calculateDosage = () => {};

  const saveLandRecord = (skipStockCheck = false) => {
    const firstChem = landForm.chemicals[0];
    if (landForm.paddocks.length === 0) {
      alert("Please add at least one paddock.");
      return;
    }
    if (!landForm.areaHectares) {
      alert("Please enter the total area (ha).");
      return;
    }
    if (landForm.pestTargets.length === 0) {
      alert("Please add at least one Pest/Weed Target.");
      return;
    }
    if (!landForm.cropLandUse) {
      alert("Please select a Crop/Land Use.");
      return;
    }
    if (!firstChem.productName) {
      alert("Please enter at least one Product Name.");
      return;
    }
    if (!landForm.windSpeed) {
      alert('Wind speed is required for compliance records.');
      return;
    }
    if (!landForm.windDirection) {
      alert('Wind direction is required for compliance records.');
      return;
    }
    if (!landForm.applicator.trim()) {
      alert('Applicator name is required for compliance records.');
      return;
    }

    // Enforce withholdingDays on every chemical — must be entered (0 = nil is valid)
    for (let i = 0; i < landForm.chemicals.length; i++) {
      const chem = landForm.chemicals[i];
      if (!chem.productName) continue;
      if (chem.withholdingDays === '' || chem.withholdingDays === null || chem.withholdingDays === undefined) {
        alert(`Withholding Period (days) is required for ${chem.productName || 'chemical ' + (i + 1)}.\nEnter 0 if nil.`);
        return;
      }
    }

    const toBaseCheck = (qty, unit) => {
      const n = parseFloat(qty) || 0;
      if (unit === 'L' || unit === 'kg') return n * 1000;
      return n;
    };

    // Stock check — skip if user already confirmed via modal
    if (!skipStockCheck) {
      for (const chem of landForm.chemicals) {
        if (!chem.calculatedQty) continue;
        const usedUnit = chem.calculatedQtyUnit || 'mL';
        const usedNorm = toBaseCheck(chem.calculatedQty, usedUnit);
        const batches = (chem.selectedBatches || []).filter(sb => sb.batchId);
        if (batches.length === 0) continue;
        const totalAvailNorm = batches.reduce((sum, sb) => {
          const invItem = inventory.find(item =>
            item.chemicalName === chem.productName && String(item.id) === String(sb.batchId)
          );
          return invItem ? sum + toBaseCheck(invItem.quantityRemainingNumber, invItem.quantityRemainingUnit) : sum;
        }, 0);
        if (usedNorm > totalAvailNorm) {
          const isLarge = (usedUnit === 'L' || usedUnit === 'kg');
          const availDisplay = isLarge ? (totalAvailNorm / 1000).toFixed(2) : totalAvailNorm.toFixed(2);
          showConfirm(
            '\u26a0\ufe0f Insufficient Stock',
            `Insufficient stock for ${chem.productName}.\n\nAvailable across selected batches: ${availDisplay} ${usedUnit}\nRequired: ${chem.calculatedQty} ${usedUnit}\n\nContinue anyway?`,
            () => saveLandRecord(true),
            { confirmLabel: 'Continue', confirmClass: 'bg-orange-500 text-white' }
          );
          return;
        }
      }
    }

const recordChemicals = landForm.chemicals.map(chem => ({
  productName: chem.productName,
  activeIngredient: chem.activeIngredient,
  batchNumber: chem.batchNumber,
  rateMode: chem.rateMode,
  rateQty: chem.rateQty || '',
  rateUnit: chem.rateUnit || 'mL',
  rateWaterQty: chem.rateWaterQty || '100',
  areaRateQty: chem.areaRateQty || '',
  areaRateUnit: chem.areaRateUnit || 'mL/ha',
  applicationRate: chem.rateMode === 'dilution'
    ? (chem.rateQty && chem.rateWaterQty ? `${chem.rateQty} ${chem.rateUnit} per ${chem.rateWaterQty} L` : '')
    : (chem.areaRateQty ? `${chem.areaRateQty} ${chem.areaRateUnit}/ha` : ''),
  calculatedQty: chem.calculatedQty,
  calculatedQtyUnit: chem.calculatedQtyUnit,
  withholdingPeriod: chem.withholdingPeriod,
  withholdingDays: chem.withholdingDays !== '' ? parseInt(chem.withholdingDays, 10) : null,
  selectedBatches: chem.selectedBatches || [],
}));

    // Build legacy rate string for first chemical for CSV/display compat
    const legacyRate = firstChem.rateMode === 'dilution'
      ? (firstChem.rateQty && firstChem.rateWaterQty ? `${firstChem.rateQty} ${firstChem.rateUnit} per ${firstChem.rateWaterQty} L` : '')
      : (firstChem.areaRateQty ? `${firstChem.areaRateQty} ${firstChem.areaRateUnit}/ha` : '');

    const record = {
      id: editingEntry ? editingEntry.id : Date.now(),
      displayId: editingEntry
        ? (records.find(r => r.id === editingEntry.id)?.displayId || '')
        : generateDisplayId('land', records),
      type: 'land',
      date: new Date(landForm.date).toLocaleDateString('en-AU'),
      location: propertyAddress,
      farmName: (landForm.farmName || '').trim(),
      paddocks: landForm.paddocks,
      paddockName: landForm.paddocks.map(p => p.name).join(', '),
      areaHectares: landForm.areaHectares,
      pestTargets: landForm.pestTargets,
      pestTarget: landForm.pestTargets.join(', '),
      cropLandUse: landForm.cropLandUse === 'Other' ? landForm.cropLandUseOther : landForm.cropLandUse,
      applicationMethod: landForm.applicationMethod,
      totalDilutedVolumeQty: landForm.totalDilutedVolumeQty,
      totalDilutedVolumeUnit: landForm.totalDilutedVolumeUnit,
      chemicals: recordChemicals,
      // Legacy fields for backward compatibility
      productName: firstChem.productName,
      activeIngredient: firstChem.activeIngredient,
      batchNumber: firstChem.batchNumber,
      applicationRate: legacyRate,
      totalQuantityUsed: firstChem.calculatedQty ? `${firstChem.calculatedQty} ${firstChem.calculatedQtyUnit || ''}`.trim() : '',
      windSpeed: landForm.windSpeed,
      windDirection: landForm.windDirection,
      applicator: landForm.applicator,
      photos: landForm.photos,
      notes: landForm.notes
    };

    // Update farm tracking — add new farm names to farmList, remember lastUsedFarm
    const trimmedLandFarm = (landForm.farmName || '').trim();
    if (trimmedLandFarm) {
      setLastUsedFarm(trimmedLandFarm);
      if (!farmList.some(f => f.toLowerCase() === trimmedLandFarm.toLowerCase())) {
        setFarmList(prev => [...prev, trimmedLandFarm].sort((a, b) => a.localeCompare(b)));
      }
    }

    if (editingEntry) {
      // Reverse original deductions before applying the updated record's deductions
      const originalRecord = records.find(r => r.id === editingEntry.id);
      if (originalRecord) {
        const originalChemicals = (originalRecord.chemicals && originalRecord.chemicals.length > 0)
          ? originalRecord.chemicals
          : [{ productName: originalRecord.productName, batchNumber: originalRecord.batchNumber, calculatedQty: originalRecord.totalQuantityUsed, calculatedQtyUnit: 'mL' }];
        for (const c of originalChemicals) {
          if (c.batchNumber && c.calculatedQty) {
            addBackToInventory(c.productName, c.batchNumber, c.calculatedQty, c.calculatedQtyUnit || 'mL');
          }
        }
      }
      setRecords(prev => prev.map(r => r.id === editingEntry.id ? record : r));
      setEditingEntry(null);
    } else {
      setRecords(prev => [record, ...prev]);
    }

    // Cascade deduct each chemical across selected batches.
    // addBackToInventory above uses functional setInventory updates, so they will
    // compose correctly with this cascade which also uses functional setInventory.
    for (const chem of landForm.chemicals) {
      if (chem.calculatedQty && chem.selectedBatches && chem.selectedBatches.some(sb => sb.batchId)) {
        deductFromInventoryCascade(chem.productName, chem.selectedBatches, chem.calculatedQty, chem.calculatedQtyUnit || 'mL', inventory);
      }
    }

    setLandForm({
      date: new Date().toISOString().split('T')[0],
      farmName: trimmedLandFarm || lastUsedFarm,
      paddocks: [],
      paddockName: '',
      areaHectares: '',
      pestTargets: [],
      pestTarget: '',
      pestTargetOther: '',
      cropLandUse: '',
      cropLandUseOther: '',
      applicationMethod: 'Spot Spray',
      totalDilutedVolumeQty: '',
      totalDilutedVolumeUnit: 'L',
      chemicals: [emptyChemicalEntry()],
      windSpeed: '',
      windDirection: '',
      applicator: farmerName,
      photos: [],
      notes: ''
    });
    setPaddockInput('');
    setPaddockAreaInput('');
    setWeedInput('');
    setSection('home');
    alert('\u2713 Land pesticide record saved!');
  };

  const saveAnimalRecord = (skipStockCheck = false) => {
    const meds = animalForm.medications || [];
    if (meds.length === 0 || !meds[0].productName) {
      alert('Please fill in at least one medication with a Product Name.');
      return;
    }
    if (!animalForm.species || !animalForm.animalCount) {
      alert('Please fill in all required fields: Species and Animal Count');
      return;
    }

    // Enforce withholdingDays and esiDays on every medication — must be entered (0 = nil is valid)
    for (let i = 0; i < meds.length; i++) {
      const med = meds[i];
      if (!med.productName) continue;
      const label = med.productName || `medication ${i + 1}`;
      if (med.withholdingDays === '' || med.withholdingDays === null || med.withholdingDays === undefined) {
        alert(`Withholding Period (days) is required for ${label}.\nEnter 0 if nil.`);
        return;
      }
      if (med.esiDays === '' || med.esiDays === null || med.esiDays === undefined) {
        alert(`Export Slaughter Interval (days) is required for ${label}.\nEnter 0 if nil.`);
        return;
      }
    }

    const toBaseCheck = (qty, unit) => {
      const n = parseFloat(qty) || 0;
      if (unit === 'L' || unit === 'kg') return n * 1000;
      return n;
    };

    // Stock check — skip if user already confirmed via modal
    if (!skipStockCheck) {
      for (const med of meds) {
        if (!med.totalQuantity) continue;
        const doseUnit = med.doseRateUnit || 'mL/animal';
        const baseUnit = doseUnit.startsWith('g') ? 'g' : doseUnit.startsWith('tablets') ? 'tablets' : doseUnit.startsWith('syringes') ? 'syringes' : 'mL';
        const selectedBatches = (med.selectedBatches || []).filter(sb => sb.batchId);
        if (selectedBatches.length === 0) continue;
        const totalAvail = selectedBatches.reduce((sum, sb) => {
          const invItem = inventory.find(item => item.chemicalName === med.productName && String(item.id) === String(sb.batchId));
          return invItem ? sum + toBaseCheck(invItem.quantityRemainingNumber, invItem.quantityRemainingUnit) : sum;
        }, 0);
        const needed = parseFloat(med.totalQuantity);
        if (needed > totalAvail) {
          showConfirm(
            '\u26a0\ufe0f Insufficient Stock',
            `Insufficient stock for ${med.productName}.\n\nAvailable across selected batches: ${totalAvail.toFixed(1)} ${baseUnit}\nRequired: ${needed.toFixed(2)} ${baseUnit}\n\nContinue anyway?`,
            () => saveAnimalRecord(true),
            { confirmLabel: 'Continue', confirmClass: 'bg-orange-500 text-white' }
          );
          return;
        }
      }
    }

    // Build medications array for record storage — include selectedBatches for cascade deduction on edit
    const recordMedications = meds.map(med => ({
      productName: med.productName,
      activeIngredient: med.activeIngredient,
      batchNumber: med.batchNumber,
      selectedBatches: med.selectedBatches || [],
      withholdingPeriod: med.withholdingPeriod,
      withholdingDays: med.withholdingDays !== '' ? parseInt(med.withholdingDays, 10) : null,
      esiDays: med.esiDays !== '' ? parseInt(med.esiDays, 10) : null,
      doseRate: med.doseRateNumber && med.doseRateUnit
        ? `${med.doseRateNumber} ${med.doseRateUnit}`
        : med.doseRate,
      doseRateUnit: med.doseRateUnit,
      doseRatePerKg: med.doseRatePerKg || '',
      dosePerAnimal: med.calculatedDosePerAnimal,
      totalQuantity: med.totalQuantity,
      treatmentPurpose: med.treatmentPurpose,
      applicationMethod: med.applicationMethod,
    }));

    const firstMed = recordMedications[0];

    const baseRecord = {
      id: editingEntry ? editingEntry.id : Date.now(),
      displayId: editingEntry
        ? (records.find(r => r.id === editingEntry.id)?.displayId || '')
        : generateDisplayId('animal', records),
      type: 'animal',
      date: new Date(animalForm.date).toLocaleDateString('en-AU'),
      farmName: (animalForm.farmName || '').trim(),
      species: animalForm.species,
      animalCount: animalForm.animalCount,
      averageWeight: animalForm.averageWeight,
      animalIDs: animalForm.animalIDs,
      applicator: animalForm.applicator,
      photos: animalForm.photos,
      notes: animalForm.notes,
      medications: recordMedications,
      // Legacy single-med fields for backward compat display/search
      productName: firstMed.productName,
      activeIngredient: firstMed.activeIngredient,
      batchNumber: firstMed.batchNumber,
      withholdingPeriod: firstMed.withholdingPeriod,
      withholdingDays: firstMed.withholdingDays,
      esiDays: firstMed.esiDays,
      doseRate: firstMed.doseRate,
      doseRateUnit: firstMed.doseRateUnit,
      doseRatePerKg: firstMed.doseRatePerKg,
      dosePerAnimal: firstMed.dosePerAnimal,
      totalQuantity: firstMed.totalQuantity,
      treatmentPurpose: firstMed.treatmentPurpose,
      applicationMethod: firstMed.applicationMethod,
    };

    // Update farm tracking — add new farm names to farmList, remember lastUsedFarm
    const trimmedAnimalFarm = (animalForm.farmName || '').trim();
    if (trimmedAnimalFarm) {
      setLastUsedFarm(trimmedAnimalFarm);
      if (!farmList.some(f => f.toLowerCase() === trimmedAnimalFarm.toLowerCase())) {
        setFarmList(prev => [...prev, trimmedAnimalFarm].sort((a, b) => a.localeCompare(b)));
      }
    }

    if (editingEntry) {
      // Reverse original deductions before applying the updated record's deductions
      const originalRecord = records.find(r => r.id === editingEntry.id);
      if (originalRecord) {
        const originalMeds = (originalRecord.medications && originalRecord.medications.length > 0)
          ? originalRecord.medications
          : [{ productName: originalRecord.productName, batchNumber: originalRecord.batchNumber, totalQuantity: originalRecord.totalQuantity, doseRateUnit: originalRecord.doseRateUnit, selectedBatches: [] }];
        for (const m of originalMeds) {
          if (!m.totalQuantity) continue;
          const invUnit = (() => {
            const u = m.doseRateUnit || 'mL/animal';
            if (u.startsWith('tablets')) return 'tablets';
            if (u.startsWith('syringes')) return 'syringes';
            if (u.startsWith('g')) return 'g';
            return 'mL';
          })();
          const origBatches = (m.selectedBatches || []).filter(sb => sb.batchId);
          if (origBatches.length > 0) {
            for (const sb of origBatches) {
              const invItem = inventory.find(item => item.chemicalName === m.productName && String(item.id) === String(sb.batchId));
              if (invItem) addBackToInventory(m.productName, invItem.batchNumber, m.totalQuantity, invUnit);
            }
          } else if (m.batchNumber) {
            addBackToInventory(m.productName, m.batchNumber, m.totalQuantity, invUnit);
          }
        }
      }
      setRecords(prev => prev.map(r => r.id === editingEntry.id ? baseRecord : r));
      setEditingEntry(null);
    } else {
      setRecords(prev => [baseRecord, ...prev]);
    }

    // Deduct inventory per medication — use cascade for multi-batch, simple deduct for single.
    // addBackToInventory above uses functional setInventory updates, so they compose correctly.
    for (const med of meds) {
      if (!med.totalQuantity) continue;
      const invUnit = (() => {
        const u = med.doseRateUnit || 'mL/animal';
        if (u.startsWith('tablets')) return 'tablets';
        if (u.startsWith('syringes')) return 'syringes';
        if (u.startsWith('g')) return 'g';
        return 'mL';
      })();
      const selectedBatches = (med.selectedBatches || []).filter(sb => sb.batchId);
      if (selectedBatches.length > 0) {
        deductFromInventoryCascade(med.productName, selectedBatches, med.totalQuantity, invUnit, inventory);
      } else if (med.batchNumber) {
        deductFromInventory(med.productName, med.batchNumber, med.totalQuantity, invUnit);
      }
    }

    setAnimalForm({
      date: new Date().toISOString().split('T')[0],
      farmName: trimmedAnimalFarm || lastUsedFarm,
      species: 'Cattle',
      animalCount: '',
      averageWeight: '',
      animalIDs: '',
      applicator: farmerName,
      photos: [],
      notes: '',
      medications: [emptyMedicationEntry()],
      selectedMedication: '',
      productName: '',
      activeIngredient: '',
      batchNumber: '',
      withholdingPeriod: '',
      withholdingDays: '',
      esiDays: '',
      doseRate: '',
      doseRateNumber: '',
      doseRateUnit: 'mL/kg',
      doseRatePerKg: '',
      calculatedDosePerAnimal: '',
      totalQuantity: '',
      treatmentPurpose: '',
      applicationMethod: 'Injectable',
    });

    setSection('home');
    alert('\u2713 Animal medication record saved!');
  };

  const generateDisplayId = (type, allRecords) => {
    const prefix = type === 'land' ? 'L' : 'A';
    const existing = allRecords
      .filter(r => r.type === type && r.displayId)
      .map(r => parseInt(r.displayId.slice(1), 10))
      .filter(n => !isNaN(n));
    const next = existing.length > 0 ? Math.max(...existing) + 1 : 1;
    return `${prefix}${String(next).padStart(4, '0')}`;
  };

  const deleteRecord = (recordId) => {
    showConfirm(
      'Delete Record',
      'Are you sure you want to delete this record? This cannot be undone.',
      () => {
        const record = records.find(r => r.id === recordId);
        if (record) {
          if (record.type === 'land') {
            const chemicals = (record.chemicals && record.chemicals.length > 0)
              ? record.chemicals
              : [{ productName: record.productName, batchNumber: record.batchNumber, calculatedQty: record.totalQuantityUsed, calculatedQtyUnit: 'mL' }];
            for (const c of chemicals) {
              if (c.batchNumber && c.calculatedQty) {
                addBackToInventory(c.productName, c.batchNumber, c.calculatedQty, c.calculatedQtyUnit || 'mL');
              }
            }
          } else if (record.type === 'animal') {
            const meds = (record.medications && record.medications.length > 0)
              ? record.medications
              : [{ productName: record.productName, batchNumber: record.batchNumber, totalQuantity: record.totalQuantity, doseRateUnit: record.doseRateUnit, selectedBatches: [] }];
            for (const m of meds) {
              if (!m.totalQuantity) continue;
              const invUnit = (() => {
                const u = m.doseRateUnit || 'mL/animal';
                if (u.startsWith('tablets')) return 'tablets';
                if (u.startsWith('syringes')) return 'syringes';
                if (u.startsWith('g')) return 'g';
                return 'mL';
              })();
              const origBatches = (m.selectedBatches || []).filter(sb => sb.batchId);
              if (origBatches.length > 0) {
                for (const sb of origBatches) {
                  const invItem = inventory.find(item => item.chemicalName === m.productName && String(item.id) === String(sb.batchId));
                  if (invItem) addBackToInventory(m.productName, invItem.batchNumber, m.totalQuantity, invUnit);
                }
              } else if (m.batchNumber) {
                addBackToInventory(m.productName, m.batchNumber, m.totalQuantity, invUnit);
              }
            }
          }
        }
        setRecords(prev => prev.filter(r => r.id !== recordId));
      },
      { confirmLabel: 'Delete', confirmClass: 'bg-red-600 text-white' }
    );
  };

  const deleteInventoryItem = (itemId) => {
    const item = inventory.find(i => i.id === itemId);
    const linkedRecords = item ? records.filter(r => {
      if (r.type === 'land') {
        const chems = r.chemicals || [];
        return chems.some(c => c.productName === item.chemicalName && c.batchNumber === item.batchNumber);
      } else {
        const meds = r.medications || [];
        return meds.some(m => m.productName === item.chemicalName && m.batchNumber === item.batchNumber);
      }
    }) : [];
    const warningText = linkedRecords.length > 0
      ? `\n\n\u26a0\ufe0f Warning: ${linkedRecords.length} usage record(s) reference this batch. Those records will remain but inventory reversal on edit will not work correctly.`
      : '';
    showConfirm(
      'Delete Inventory Item',
      `Delete ${item ? item.chemicalName : 'this item'} (Batch ${item ? item.batchNumber : ''}) from inventory?${warningText}`,
      () => setInventory(prev => prev.filter(i => i.id !== itemId)),
      { confirmLabel: 'Delete', confirmClass: 'bg-red-600 text-white' }
    );
  };

  const exportToCSV = () => {
    // Helper: look up batch expiry from inventory
    const getBatchExpiry = (productName, batchNumber) => {
      if (!batchNumber) return '';
      const inv = inventory.find(i => i.chemicalName === productName && i.batchNumber === batchNumber);
      return inv ? inv.expiryDate : '';
    };

    // LAND RECORDS: one row per chemical (handles multi-chemical applications)
    const landHeaders = [
      'Farm Name',
      'Record ID',
      'Record Type',
      'Date',
      'Paddock',
      'Area (ha)',
      'Crop/Land Use',
      'Pest/Weed Target',
      'Application Method',
      'Total Diluted Volume',
      'Wind Speed',
      'Wind Direction',
      'Product Name',
      'Active Ingredient',
      'Batch Number',
      'Batch Expiry Date',
      'Application Rate',
      'Quantity Used',
      'WHP (days)',
      'WHP Clear Date',
      'Applicator',
      'Notes',
      'Photos'
    ];

    const calcCsvExpiry = (days, dateStr) => {
      const d = parseInt(days, 10);
      if (isNaN(d) || days === '' || days == null) return '';
      if (d === 0) return 'Nil';
      if (!dateStr) return '';
      const parts = dateStr.includes('/') ? dateStr.split('/') : null;
      const base = parts
        ? new Date(`${parts[2]}-${parts[1].padStart(2,'0')}-${parts[0].padStart(2,'0')}`)
        : new Date(dateStr);
      if (isNaN(base.getTime())) return '';
      base.setDate(base.getDate() + d);
      return base.toLocaleDateString('en-AU');
    };

    const landRows = [];
    records.filter(r => r.type === 'land').forEach(record => {
      const chemicals = record.chemicals && record.chemicals.length > 0
        ? record.chemicals
        : [{ productName: record.productName, activeIngredient: record.activeIngredient, batchNumber: record.batchNumber, applicationRate: record.applicationRate, calculatedQty: '', calculatedQtyUnit: '', withholdingPeriod: record.withholdingPeriod, withholdingDays: record.withholdingDays }];

      chemicals.forEach(chem => {
        const expiryDate = getBatchExpiry(chem.productName, chem.batchNumber);
        const qtyUsed = chem.calculatedQty ? `${chem.calculatedQty} ${chem.calculatedQtyUnit || ''}`.trim() : '';
        const totalVol = record.totalDilutedVolumeQty ? `${record.totalDilutedVolumeQty} ${record.totalDilutedVolumeUnit}` : '';
        const whpClearDate = calcCsvExpiry(chem.withholdingDays, record.date);
        landRows.push([
          record.farmName || '',
          record.displayId || '',
          'Land Pesticide',
          record.date,
          record.paddockName || '',
          record.areaHectares || '',
          record.cropLandUse || '',
          record.pestTarget || '',
          record.applicationMethod || '',
          totalVol,
          record.windSpeed || '',
          record.windDirection || '',
          chem.productName || '',
          chem.activeIngredient || '',
          chem.batchNumber || '',
          expiryDate,
          chem.applicationRate || '',
          qtyUsed,
          chem.withholdingDays != null ? chem.withholdingDays : '',
          whpClearDate,
          record.applicator || '',
          record.notes || '',
          (record.photos || []).join('; ')
        ]);
      });
    });

    // ANIMAL RECORDS: one row per medication (mirrors land chemical approach)
    const animalHeaders = [
      'Farm Name',
      'Record ID',
      'Record Type',
      'Date',
      'Species',
      'Animal Count',
      'Average Weight (kg)',
      'Animal IDs/Mob',
      'Product Name',
      'Active Ingredient',
      'Batch Number',
      'Batch Expiry Date',
      'WHP (days)',
      'WHP Clear Date',
      'ESI (days)',
      'ESI Clear Date',
      'Application Method',
      'Dose Rate',
      'Dose Per Animal',
      'Total Quantity Used',
      'Treatment Purpose',
      'Applicator',
      'Notes',
      'Photos'
    ];

    const animalRows = [];
    records.filter(r => r.type === 'animal').forEach(record => {
      // Support both new multi-med records and legacy single-med records
      const meds = (record.medications && record.medications.length > 0)
        ? record.medications
        : [{
            productName: record.productName,
            activeIngredient: record.activeIngredient,
            batchNumber: record.batchNumber,
            withholdingPeriod: record.withholdingPeriod,
            withholdingDays: record.withholdingDays,
            esiDays: record.esiDays,
            applicationMethod: record.applicationMethod,
            doseRate: record.doseRate,
            dosePerAnimal: record.dosePerAnimal,
            totalQuantity: record.totalQuantity,
            treatmentPurpose: record.treatmentPurpose,
          }];

      meds.forEach(med => {
        const expiryDate = getBatchExpiry(med.productName, med.batchNumber);
        const doseUnit = (() => {
          const u = med.doseRate || '';
          if (u.includes('tablets')) return 'tablets';
          if (u.includes('syringes')) return 'syringes';
          if (u.includes('g/')) return 'g';
          return 'mL';
        })();
        const whpClearDate = calcCsvExpiry(med.withholdingDays, record.date);
        const esiClearDate = calcCsvExpiry(med.esiDays, record.date);
        animalRows.push([
          record.farmName || '',
          record.displayId || '',
          'Animal Medication',
          record.date,
          record.species || '',
          record.animalCount || '',
          record.averageWeight || '',
          record.animalIDs || '',
          med.productName || '',
          med.activeIngredient || '',
          med.batchNumber || '',
          expiryDate,
          med.withholdingDays != null ? med.withholdingDays : '',
          whpClearDate,
          med.esiDays != null ? med.esiDays : '',
          esiClearDate,
          med.applicationMethod || '',
          med.doseRate || '',
          med.dosePerAnimal ? `${med.dosePerAnimal} ${doseUnit}` : '',
          med.totalQuantity ? `${med.totalQuantity} ${doseUnit}` : '',
          med.treatmentPurpose || '',
          record.applicator || '',
          record.notes || '',
          (record.photos || []).join('; ')
        ]);
      });
    });

    // Build CSV with two sections
    const escape = cell => `"${String(cell == null ? '' : cell).replace(/"/g, '""')}"`;
    const rowToCSV = row => row.map(escape).join(',');

    const csvLines = [];
    csvLines.push('LAND PESTICIDE RECORDS');
    csvLines.push(rowToCSV(landHeaders));
    landRows.forEach(r => csvLines.push(rowToCSV(r)));
    csvLines.push('');
    csvLines.push('ANIMAL MEDICATION RECORDS');
    csvLines.push(rowToCSV(animalHeaders));
    animalRows.forEach(r => csvLines.push(rowToCSV(r)));

    const allPhotos = records.flatMap(r => r.photos || []).filter(Boolean);
    if (allPhotos.length > 0) {
      csvLines.push('');
      csvLines.push('PHOTOS TO ATTACH TO EMAIL:');
      allPhotos.forEach(photo => csvLines.push(escape(photo)));
    }

    const csvContent = csvLines.join('\n');
    const blob = new Blob(['\uFEFF' + csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `AgriAdmin_Records_${new Date().toISOString().split('T')[0]}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);

    const totalRows = landRows.length + animalRows.length;
    alert(`\u2713 CSV exported!\n\n${landRows.length} land record rows\n${animalRows.length} animal record rows\nTotal: ${totalRows} rows\n\nFind the file in your Downloads folder.`);
  };

  const exportInventoryToCSV = () => {
    const headers = [
      'Chemical Name', 'Active Ingredient', 'Category', 'Batch Number', 
      'Container Size', 'Quantity Remaining', 'Manufacturing Date', 'Expiry Date', 'Purchase Date', 'Status'
    ];

    const today = new Date();
    const twoMonthsFromNow = new Date();
    twoMonthsFromNow.setMonth(today.getMonth() + 2);

    const csvData = inventory
      .sort((a, b) => a.chemicalName.localeCompare(b.chemicalName))
      .map(item => {
        const expiry = new Date(item.expiryDate);
        let status = 'OK';
        if (expiry < today) status = 'EXPIRED';
        else if (expiry <= twoMonthsFromNow) status = 'Expiring Soon';

        return [
          item.chemicalName,
          item.activeIngredient,
          item.category,
          item.batchNumber,
          `${item.containerSizeNumber} ${item.containerSizeUnit}`,
          `${item.quantityRemainingNumber} ${item.quantityRemainingUnit}`,
          item.manufacturingDate || 'N/A',
          item.expiryDate,
          item.purchaseDate,
          status
        ];
      });

    const csvContent = [headers, ...csvData]
      .map(row => row.map(cell => `"${cell}"`).join(','))
      .join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `AgriAdmin_Inventory_Report_${new Date().toISOString().split('T')[0]}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);

    alert('\u2713 Inventory report exported to CSV!');
  };

  // ============================================
  // RENDER
  // ============================================

  // Confirm modal — rendered on top of whichever section is active
  const ConfirmModal = confirmModal.visible ? (
    <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg shadow-2xl p-6 max-w-sm w-full">
        <h3 className="text-lg font-bold text-gray-800 mb-3">{confirmModal.title}</h3>
        <p className="text-sm text-gray-700 mb-6 whitespace-pre-line">{confirmModal.message}</p>
        <div className="flex space-x-3">
          <button
            onClick={dismissConfirm}
            className="flex-1 bg-gray-200 text-gray-800 p-3 rounded-lg font-semibold hover:bg-gray-300"
          >
            {confirmModal.cancelLabel}
          </button>
          <button
            onClick={() => {
              const fn = confirmModal.onConfirm;
              dismissConfirm();
              if (fn) fn();
            }}
            className={`flex-1 p-3 rounded-lg font-semibold ${confirmModal.confirmClass}`}
          >
            {confirmModal.confirmLabel}
          </button>
        </div>
      </div>
    </div>
  ) : null;

  // Setup Screen
  if (!isSetupComplete) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-green-50 to-blue-50 flex items-center justify-center p-4">
        <div className="max-w-md w-full">
          <div className="bg-white rounded-lg shadow-2xl p-8">
            <div className="text-center mb-6">
              <div className="text-6xl mb-4">{'\ud83e\uddea'}</div>
              <h1 className="text-3xl font-bold text-green-800 mb-2">
                Agri Admin
              </h1>
              <p className="text-gray-600">Welcome! Let's set up your account</p>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Your Name *
                </label>
                <input
                  type="text"
                  value={setupFarmerName}
                  onChange={(e) => setSetupFarmerName(e.target.value)}
                  placeholder="e.g., John Smith"
                  className="w-full p-4 border-2 border-gray-300 rounded-lg text-lg"
                  autoFocus
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Farm Name / Property Address *
                </label>
                <input
                  type="text"
                  value={setupPropertyAddress}
                  onChange={(e) => setSetupPropertyAddress(e.target.value)}
                  placeholder="e.g., Meadowbrook or 123 Farm Road, Wagga Wagga NSW"
                  className="w-full p-4 border-2 border-gray-300 rounded-lg text-lg"
                />
              </div>

              <button
                onClick={completeSetup}
                className="w-full bg-green-600 text-white p-4 rounded-lg text-xl font-semibold hover:bg-green-700 mt-6"
              >
                Get Started {'\u2192'}
              </button>
            </div>

            <div className="mt-8 pt-6 border-t border-gray-200 text-center text-xs text-gray-500">
              NSW Pesticide & Medication Record Keeping
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Home Screen
  if (section === 'home') {
    const expiringChemicals = getExpiringChemicals();

    return (
      <>
      {ConfirmModal}
      <div className="min-h-screen bg-green-50 p-4">
        <div className="max-w-md mx-auto">
          <div className="bg-white rounded-lg shadow-lg p-6 mb-6">
            <h1 className="text-3xl font-bold text-center text-green-800">Agri Admin</h1>
            <p className="text-center text-gray-600">Pesticide & Medication Tracking</p>
            <p className="text-center text-sm text-gray-500 mt-2">
              {propertyAddress}
            </p>
          </div>

          {/* PWA Install Banner */}
          {showInstallBanner && (
            <div className="bg-blue-50 border-2 border-blue-300 rounded-lg p-4 mb-4 flex items-center justify-between gap-3">
              <div className="flex items-center gap-3 flex-1">
                <span className="text-2xl">📲</span>
                {isIosInstallBanner ? (
                  <div>
                    <p className="text-sm font-semibold text-blue-800">Add to Home Screen</p>
                    <p className="text-xs text-blue-700 mt-0.5">Tap the <strong>Share</strong> button in Safari, then tap <strong>Add to Home Screen</strong></p>
                  </div>
                ) : (
                  <div className="flex-1">
                    <p className="text-sm font-semibold text-blue-800">Add to Home Screen</p>
                    <button
                      onClick={async () => {
                        if (!deferredInstallPrompt) return;
                        deferredInstallPrompt.prompt();
                        await deferredInstallPrompt.userChoice;
                        setDeferredInstallPrompt(null);
                        setShowInstallBanner(false);
                        localStorage.setItem('installBannerDismissed', 'true');
                      }}
                      className="mt-1 bg-blue-600 text-white text-xs px-3 py-1 rounded-lg font-semibold hover:bg-blue-700"
                    >
                      Install App
                    </button>
                  </div>
                )}
              </div>
              <button
                onClick={() => {
                  setShowInstallBanner(false);
                  localStorage.setItem('installBannerDismissed', 'true');
                }}
                className="text-blue-400 hover:text-blue-600 text-xl font-bold leading-none flex-shrink-0"
                aria-label="Dismiss"
              >
                ×
              </button>
            </div>
          )}

          {expiringChemicals.length > 0 && (
            <div className="bg-red-50 border-2 border-red-300 rounded-lg p-4 mb-6">
              <div className="flex items-start">
                <div className="text-2xl mr-3">{'\u26a0\ufe0f'}</div>
                <div className="flex-1">
                  <h3 className="text-lg font-bold text-red-800 mb-2">
                    Chemicals Expiring Within 2 Months
                  </h3>
                  {expiringChemicals.slice(0, 3).map((item, index) => (
                    <div key={index} className="text-sm text-red-700 mb-1">
                      {'\u2022'} {item.chemicalName} (Batch {item.batchNumber}) - Expires {new Date(item.expiryDate).toLocaleDateString('en-AU')}
                    </div>
                  ))}
                  {expiringChemicals.length > 3 && (
                    <button
                      onClick={() => setSection('inventory')}
                      className="text-sm text-red-800 font-semibold underline mt-2"
                    >
                      More ({expiringChemicals.length - 3} more expiring)
                    </button>
                  )}
                </div>
              </div>
            </div>
          )}

          <div className="bg-white rounded-lg shadow-lg p-4 mb-6">
            <h2 className="text-xl font-bold text-gray-800 mb-3">Purchases</h2>
            <div className="grid grid-cols-2 gap-3">
              <button
                onClick={() => {
                setPurchaseForm(prev => ({ ...prev, selectedChemical: '', chemicalName: '', activeIngredient: '', category: '', batchNumber: '', manufacturingDate: '', expiryDate: '', containerSizeNumber: '', quantityRemainingNumber: '', landRate: '', animalRate: '', withholdingPeriod: '' }));
                setSection('purchaseLand');
              }}
                className="bg-green-600 text-white p-5 rounded-xl shadow-lg text-center hover:bg-green-700 active:scale-95 transition-transform"
              >
                <div className="text-4xl mb-2">{'\ud83d\udee2\ufe0f'}</div>
                <div className="font-bold text-sm leading-tight">Land Chemical<br/>Purchases</div>
              </button>
              <button
                onClick={() => {
                setPurchaseForm(prev => ({ ...prev, selectedChemical: '', chemicalName: '', activeIngredient: '', category: '', batchNumber: '', manufacturingDate: '', expiryDate: '', containerSizeNumber: '', quantityRemainingNumber: '', landRate: '', animalRate: '', withholdingPeriod: '' }));
                setSection('purchaseMeds');
              }}
                className="bg-orange-500 text-white p-5 rounded-xl shadow-lg text-center hover:bg-orange-600 active:scale-95 transition-transform"
              >
                <div className="text-4xl mb-2">{'\ud83d\udc89'}</div>
                <div className="font-bold text-sm leading-tight">Animal Meds<br/>Purchases</div>
              </button>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-lg p-4 mb-6">
            <h2 className="text-xl font-bold text-gray-800 mb-3">Usage</h2>
            <div className="grid grid-cols-2 gap-3">
              <button
                onClick={() => setSection('land')}
                className="bg-green-500 text-white p-5 rounded-xl shadow-lg text-center hover:bg-green-600 active:scale-95 transition-transform"
              >
                <div className="text-4xl mb-2">{'\ud83c\udf3e'}</div>
                <div className="font-bold text-sm leading-tight">Record Land Pesticide Use</div>
              </button>
              <button
                onClick={() => {
                  setAnimalForm(prev => ({
                    ...prev,
                    date: new Date().toISOString().split('T')[0],
                    species: prev.species || 'Cattle',
                    animalCount: '',
                    averageWeight: '',
                    animalIDs: '',
                    notes: '',
                    photos: [],
                    medications: [emptyMedicationEntry()],
                    selectedMedication: '',
                    productName: '',
                    activeIngredient: '',
                    batchNumber: '',
                    withholdingPeriod: '',
                    doseRate: '',
                    doseRateNumber: '',
                    doseRateUnit: 'mL/kg',
                    doseRatePerKg: '',
                    calculatedDosePerAnimal: '',
                    totalQuantity: '',
                    treatmentPurpose: '',
                    applicationMethod: 'Injectable',
                  }));
                  setEditingEntry(null);
                  setSection('animal');
                }}
                className="bg-orange-500 text-white p-5 rounded-xl shadow-lg text-center hover:bg-orange-600 active:scale-95 transition-transform"
              >
                <div className="text-4xl mb-2">{'\ud83d\udc04'}</div>
                <div className="font-bold text-sm leading-tight">Record Animal Med Use</div>
              </button>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-lg p-4 mb-6">
            <h2 className="text-xl font-bold text-gray-800 mb-3">Inventory</h2>
            <button
              onClick={() => setSection('inventory')}
              className="w-full bg-purple-500 text-white p-4 rounded-xl shadow-lg text-lg font-semibold hover:bg-purple-600"
            >
              {'\ud83d\udce6'} View Stock on Hand
            </button>
          </div>

          <div className="bg-white rounded-lg shadow-lg p-4 mb-6">
            <h2 className="text-xl font-bold text-gray-800 mb-4">Records</h2>
            <div className="grid grid-cols-3 gap-4 text-center mb-4">
              <div
                className="cursor-pointer hover:bg-green-50 rounded-lg p-2 transition-colors"
                onClick={() => { setFilter('land'); setSearchTerm(''); setFilterYear(''); setFilterSeason(''); setFilterPaddock(''); setFilterSpecies(''); setSection('records'); }}
              >
                <div className="text-3xl font-bold text-green-600">
                  {records.filter(r => r.type === 'land').length}
                </div>
                <div className="text-sm text-gray-600">Land</div>
              </div>
              <div
                className="cursor-pointer hover:bg-orange-50 rounded-lg p-2 transition-colors"
                onClick={() => { setFilter('animal'); setSearchTerm(''); setFilterYear(''); setFilterSeason(''); setFilterPaddock(''); setFilterSpecies(''); setSection('records'); }}
              >
                <div className="text-3xl font-bold text-orange-600">
                  {records.filter(r => r.type === 'animal').length}
                </div>
                <div className="text-sm text-gray-600">Animal</div>
              </div>
              <div
                className="cursor-pointer hover:bg-blue-50 rounded-lg p-2 transition-colors"
                onClick={() => { setFilter('all'); setSearchTerm(''); setFilterYear(''); setFilterSeason(''); setFilterPaddock(''); setFilterSpecies(''); setSection('records'); }}
              >
                <div className="text-3xl font-bold text-blue-600">{records.length}</div>
                <div className="text-sm text-gray-600">Total</div>
              </div>
            </div>
            <button
              onClick={() => {
                setFilter('all');
                setSearchTerm('');
                setFilterYear('');
                setFilterSeason('');
                setFilterPaddock('');
                setFilterSpecies('');
                setSection('records');
              }}
              className="w-full bg-blue-600 text-white p-3 rounded-lg font-semibold hover:bg-blue-700"
            >
              {'\ud83d\udccb'} View All Records
            </button>
            <div className="grid grid-cols-2 gap-2 mt-2">
              <button
                onClick={() => { setFilter('in-whp'); setSection('records'); }}
                className="bg-black text-red-500 p-3 rounded-lg font-semibold text-sm hover:bg-gray-900"
              >
                {'\u26a0\ufe0f'} Within WHP
              </button>
              <button
                onClick={() => { setFilter('in-esi'); setSection('records'); }}
                className="bg-black text-red-500 p-3 rounded-lg font-semibold text-sm hover:bg-gray-900"
              >
                {'\u26a0\ufe0f'} Within ESI
              </button>
            </div>
          </div>

          <button
            onClick={() => setSection('settings')}
            className="w-full bg-gray-600 text-white p-4 rounded-xl shadow-lg text-xl font-semibold hover:bg-gray-700"
          >
            {'\u2699\ufe0f'} Settings
          </button>

          {/* Auto-Backup Status */}
          {lastAutoBackup && (
            <div className="bg-green-50 border border-green-300 rounded-lg p-3 mt-4">
              <div className="flex items-center justify-center">
                <span className="text-green-700 text-sm">
                  {'\u2713'} Last auto-backup: {new Date(lastAutoBackup).toLocaleDateString('en-AU')}
                </span>
              </div>
            </div>
          )}


        </div>
      </div>

      {/* Backup Prompt Modal */}
      {showBackupPrompt && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-xl shadow-2xl p-6 max-w-sm w-full">
            <div className="text-4xl text-center mb-3">💾</div>
            <h2 className="text-xl font-bold text-center text-gray-800 mb-2">Time to back up your records</h2>
            <p className="text-sm text-gray-600 text-center mb-4 leading-relaxed">
              It's been 7 days since your last backup. Tap <strong>Back Up Now</strong>, then select <strong>Save to Files</strong> to keep a copy safe.
            </p>
            <div className="bg-gray-50 border border-gray-200 rounded-lg p-3 mb-4 flex items-start gap-3">
              <span className="text-lg">📁</span>
              <p className="text-xs text-gray-600 leading-relaxed">
                When the share screen appears, tap <strong>Save to Files</strong> then tap <strong>Save</strong> in the top right corner.
              </p>
            </div>
            <button
              onClick={() => performBackup('automatic')}
              className="w-full bg-green-700 text-white p-3 rounded-lg font-semibold mb-2 hover:bg-green-800"
            >
              Back Up Now
            </button>
            <button
              onClick={() => {
                const now = new Date().toISOString();
                localStorage.setItem('lastAutoBackup', now);
                setLastAutoBackup(now);
                setShowBackupPrompt(false);
              }}
              className="w-full bg-white text-gray-500 border border-gray-300 p-3 rounded-lg text-sm hover:bg-gray-50"
            >
              Remind me later
            </button>
          </div>
        </div>
      )}
      </>
    );
  }

  // Land Chemical Purchase Form
  if (section === 'purchaseLand') {
    const landChemicalsOnly = allChemicals.filter(c =>
      ['Herbicide', 'Insecticide', 'Fungicide', 'Rodenticide'].includes(c.category)
    );
    return (
      <div className="min-h-screen bg-green-50 p-4">
        {ConfirmModal}
        <div className="max-w-2xl mx-auto">
          <div className="bg-white rounded-lg shadow-lg p-6 mb-6">
            <h2 className="text-2xl font-bold text-center text-green-800">
              {'\ud83e\udd6c'} Land Chemical Purchase
            </h2>
            <p className="text-center text-gray-600">Add to Inventory</p>
          </div>

          {/* Label photo modals — shared */}
          {showPhotoInstructions && (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
              <div className="bg-white rounded-lg shadow-2xl p-6 max-w-md w-full">
                <h3 className="text-xl font-bold text-gray-800 mb-4">{'\ud83d\udcf7'} Photo Instructions</h3>
                <div className="space-y-3 mb-6">
                  <p className="text-sm text-gray-700">Take up to <strong>3 clear photos</strong> focusing on:</p>
                  {labelPhotoContext === 'full' ? (
                    <ul className="text-sm text-gray-700 space-y-2 ml-4">
                      <li>{'\u2713'} Product name</li>
                      <li>{'\u2713'} Active ingredient and concentration</li>
                      <li>{'\u2713'} Withholding period</li>
                      <li>{'\u2713'} Batch/Lot number</li>
                      <li>{'\u2713'} Manufacturing date</li>
                      <li>{'\u2713'} Container size</li>
                    </ul>
                  ) : (
                    <ul className="text-sm text-gray-700 space-y-2 ml-4">
                      <li>{'\u2713'} Batch/Lot number</li>
                      <li>{'\u2713'} Manufacturing date</li>
                      <li>{'\u2713'} Expiry date</li>
                      <li>{'\u2713'} Container size</li>
                    </ul>
                  )}
                </div>
                {labelPhotos.length > 0 && (
                  <div className="mb-4">
                    <p className="text-sm font-semibold text-green-700 mb-2">{'\u2713'} {labelPhotos.length} photo(s) captured</p>
                    <div className="grid grid-cols-3 gap-2">
                      {labelPhotos.map((photo, idx) => (
                        <img key={idx} src={photo.dataUrl} alt={`Label ${idx + 1}`} className="w-full h-20 object-cover rounded border-2 border-green-500" />
                      ))}
                    </div>
                  </div>
                )}
                <div className="flex space-x-3">
                  {labelPhotos.length < 3 && (
                    <button onClick={captureLabelPhoto} className="flex-1 bg-blue-600 text-white p-3 rounded-lg font-semibold hover:bg-blue-700">
                      {'\ud83d\udcf7'} Take Photo ({labelPhotos.length}/3)
                    </button>
                  )}
                  {labelPhotos.length > 0 && (
                    <button onClick={processLabelPhotos} className="flex-1 bg-green-600 text-white p-3 rounded-lg font-semibold hover:bg-green-700">
                      {'\u2713'} Process Photos
                    </button>
                  )}
                </div>
                <button onClick={() => { setShowPhotoInstructions(false); setLabelPhotos([]); }}
                  className="w-full bg-gray-500 text-white p-3 rounded-lg font-semibold hover:bg-gray-600 mt-3">
                  Cancel
                </button>
              </div>
            </div>
          )}
          {isProcessingPhotos && (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
              <div className="bg-white rounded-lg shadow-2xl p-8 max-w-md w-full text-center">
                <div className="text-6xl mb-4">{'\u23f3'}</div>
                <h3 className="text-xl font-bold text-gray-800 mb-2">Processing Photos...</h3>
                <p className="text-gray-600">AI is reading the label information</p>
              </div>
            </div>
          )}

          {/* Extracted data confirmation dialog - Land Chemicals */}
          {showConfirmationDialog && extractedData && (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
              <div className="bg-white rounded-lg shadow-2xl p-6 max-w-md w-full max-h-screen overflow-y-auto">
                <h3 className="text-xl font-bold text-gray-800 mb-2">
                  {extractedData.isExisting ? '\u2713 Chemical Found in Database' : '\u26a0\ufe0f New Chemical Detected'}
                </h3>
                <p className="text-sm text-gray-600 mb-4">
                  {extractedData.isExisting
                    ? `"${extractedData.chemicalName}" — using database values for chemical details.`
                    : `"${extractedData.chemicalName}" will be added to your database.`}
                </p>
                {!extractedData.isExisting && extractedData.missingFields && extractedData.missingFields.length > 0 && (
                  <div className="bg-yellow-50 border border-yellow-300 rounded-lg p-3 mb-4">
                    <p className="text-sm font-semibold text-yellow-800 mb-1">{'\u26a0\ufe0f'} Not found on label — fill in manually:</p>
                    <ul className="text-sm text-yellow-700 space-y-1">
                      {extractedData.missingFields.map(f => (
                        <li key={f}>{'\u2022'} {f}</li>
                      ))}
                    </ul>
                  </div>
                )}
                <p className="text-sm font-semibold text-gray-700 mb-3">Information extracted from label:</p>
                <div className="space-y-2 mb-4">
                  {[
                    { key: 'chemicalName', label: 'Product Name', value: extractedData.chemicalName },
                    { key: 'activeIngredient', label: 'Active Ingredient', value: extractedData.activeIngredient },
                    { key: 'category', label: 'Category', value: extractedData.category },
                    { key: 'withholdingPeriod', label: 'Withholding Period', value: extractedData.withholdingPeriod },
                    { key: 'withholdingDays', label: 'WHP (days)', value: extractedData.withholdingDays != null ? String(extractedData.withholdingDays) : null },
                    { key: 'esiDays', label: 'ESI (days)', value: extractedData.esiDays != null ? String(extractedData.esiDays) : null },
                    { key: 'applicationRate', label: 'Application Rate', value: extractedData.animalRate || extractedData.landRate || extractedData.applicationRate },
                    { key: 'batchNumber', label: 'Batch Number', value: extractedData.batchNumber },
                    { key: 'containerSize', label: 'Container Size', value: extractedData.containerSizeNumber ? `${extractedData.containerSizeNumber} ${extractedData.containerSizeUnit}` : null },
                    { key: 'expiryDate', label: 'Expiry Date', value: extractedData.expiryDate },
                    { key: 'manufacturingDate', label: 'Manufacturing Date', value: extractedData.manufacturingDate },
                  ].filter(f => f.value).map(field => (
                    <div key={field.key} className="bg-gray-50 rounded p-2">
                      <span className="text-sm text-gray-700">{field.label}: <strong>{field.value}</strong></span>
                    </div>
                  ))}
                </div>
                <div className="flex space-x-3">
                  <button
                    onClick={() => {
                      setShowConfirmationDialog(false);
                      setExtractedData(null);
                      setLabelPhotos([]);
                      setConfirmationChecks({});
                    }}
                    className="flex-1 bg-gray-500 text-white p-3 rounded-lg font-semibold"
                  >
                    Go Back
                  </button>
                  <button
                    onClick={handleConfirmExtractedData}
                    className="flex-1 bg-green-600 text-white p-3 rounded-lg font-semibold"
                  >
                    {'\u2713'} Use This Data
                  </button>
                </div>
              </div>
            </div>
          )}

          <div className="bg-white rounded-lg shadow-lg p-6 space-y-4">

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">Purchase Date *</label>
              <input type="date" value={purchaseForm.purchaseDate}
                onChange={(e) => setPurchaseForm({ ...purchaseForm, purchaseDate: e.target.value })}
                className="w-full p-3 border-2 border-gray-300 rounded-lg" />
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">Chemical/Product *</label>
              <select value={purchaseForm.selectedChemical}
                onChange={(e) => handlePurchaseChemicalSelect(e.target.value)}
                className="w-full p-3 border-2 border-gray-300 rounded-lg">
                <option value="">Select chemical...</option>
                <option value="Other">Other (Add New Chemical)</option>
                {landChemicalsOnly.sort((a, b) => a.name.localeCompare(b.name)).map(c => (
                  <option key={c.name} value={c.name}>{c.name}</option>
                ))}
              </select>
            </div>

            {/* Other selected: name field + lookup/label buttons + extra fields */}
            {purchaseForm.selectedChemical === 'Other' && (
              <>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Chemical Name *</label>
                  <input type="text" value={purchaseForm.chemicalName}
                    onChange={(e) => setPurchaseForm({ ...purchaseForm, chemicalName: e.target.value })}
                    placeholder="Enter product name"
                    className="w-full p-3 border-2 border-gray-300 rounded-lg" />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <button onClick={() => handlePopulateFromLabel('full')}
                    className="bg-blue-600 text-white p-4 rounded-lg font-semibold hover:bg-blue-700">
                    {'\ud83d\udcf7'} Fill in from Label
                  </button>
                  <button onClick={handleLookup}
                    disabled={!purchaseForm.chemicalName || isLookingUp}
                    className={`p-4 rounded-lg font-semibold ${purchaseForm.chemicalName && !isLookingUp ? 'bg-purple-600 text-white hover:bg-purple-700' : 'bg-gray-300 text-gray-500 cursor-not-allowed'}`}>
                    {isLookingUp ? '\u23f3 Looking up...' : '\ud83d\udd0d Look up Chemical Name'}
                  </button>
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Active Ingredient</label>
                  <input type="text" value={purchaseForm.activeIngredient}
                    onChange={(e) => setPurchaseForm({ ...purchaseForm, activeIngredient: e.target.value })}
                    placeholder="e.g., Glyphosate 570g/L"
                    className="w-full p-3 border-2 border-gray-300 rounded-lg" />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Category</label>
                  <select value={purchaseForm.category}
                    onChange={(e) => setPurchaseForm({ ...purchaseForm, category: e.target.value })}
                    className="w-full p-3 border-2 border-gray-300 rounded-lg">
                    <option value="">Select...</option>
                    <option>Herbicide</option>
                    <option>Insecticide</option>
                    <option>Fungicide</option>
                    <option>Rodenticide</option>
                    <option>Marker</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Withholding Period (from label)</label>
                  <input type="text" value={purchaseForm.withholdingPeriod}
                    readOnly
                    placeholder="Auto-filled from label scan"
                    className="w-full p-3 border-2 border-gray-200 rounded-lg bg-amber-50 text-amber-900 cursor-default" />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">WHP (days)</label>
                  <div className="flex items-center gap-2">
                    <input type="number" min="0" style={{MozAppearance:"textfield"}} onWheel={(e)=>e.target.blur()}
                      value={purchaseForm.withholdingDays}
                      onChange={(e) => setPurchaseForm({ ...purchaseForm, withholdingDays: e.target.value })}
                      placeholder="e.g., 7"
                      className="flex-1 p-3 border-2 border-gray-300 rounded-lg" />
                    <span className="text-gray-600 font-medium whitespace-nowrap">days</span>
                  </div>
                </div>
              </>
            )}

            {/* Known chemical: show product info summary — WHP from DB, no manual entry */}
            {purchaseForm.selectedChemical && purchaseForm.selectedChemical !== 'Other' && (
              <div className="bg-green-50 p-4 rounded-lg space-y-2">
                <div className="text-sm text-gray-700">
                  <div><strong>Active Ingredient:</strong> {purchaseForm.activeIngredient}</div>
                  <div><strong>Category:</strong> {purchaseForm.category}</div>
                </div>
                {purchaseForm.withholdingPeriod && (
                  <div className="bg-amber-50 border border-amber-300 rounded p-2">
                    <p className="text-xs font-semibold text-amber-800 mb-0.5">Withholding Period (from label)</p>
                    <p className="text-sm text-amber-900 font-medium">{purchaseForm.withholdingPeriod}</p>
                    <p className="text-xs text-amber-700 mt-1">Withholding days are entered when recording usage, as they depend on application.</p>
                  </div>
                )}
              </div>
            )}

            {/* Container details — only show once a chemical is selected */}
            {purchaseForm.selectedChemical && (
              <div className="border-t-2 border-gray-200 pt-4">
                <h3 className="text-lg font-bold text-gray-800 mb-3">Container Details</h3>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Container Size *</label>
                  <div className="grid grid-cols-3 gap-3">
                    <div className="col-span-2">
                      <input type="number" step="0.1" style={{MozAppearance:"textfield"}} onWheel={(e)=>e.target.blur()} value={purchaseForm.containerSizeNumber}
                        onChange={(e) => {
                          const newSize = e.target.value;
                          setPurchaseForm(prev => {
                            const isSynced = prev.quantityRemainingNumber === '' || prev.quantityRemainingNumber === prev.containerSizeNumber;
                            return { ...prev, containerSizeNumber: newSize, quantityRemainingNumber: isSynced ? newSize : prev.quantityRemainingNumber };
                          });
                        }}
                        placeholder="e.g., 20" className="w-full p-3 border-2 border-gray-300 rounded-lg" />
                    </div>
                    <select value={purchaseForm.containerSizeUnit}
                      onChange={(e) => setPurchaseForm({ ...purchaseForm, containerSizeUnit: e.target.value, quantityRemainingUnit: e.target.value })}
                      className="w-full p-3 border-2 border-gray-300 rounded-lg">
                      <option value="L">L</option>
                      <option value="mL">mL</option>
                      <option value="kg">kg</option>
                      <option value="g">g</option>
                    </select>
                  </div>
                </div>
                <div className="mt-3">
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Quantity Remaining (editable for partial containers)</label>
                  <div className="grid grid-cols-3 gap-3">
                    <div className="col-span-2">
                      <input type="text" inputMode="decimal" style={{MozAppearance:"textfield"}} onWheel={(e)=>e.target.blur()}
                        value={purchaseForm.quantityRemainingNumber}
                        onChange={(e) => setPurchaseForm({ ...purchaseForm, quantityRemainingNumber: e.target.value })}
                        placeholder="Auto-filled from container size"
                        className="w-full p-3 border-2 border-gray-300 rounded-lg" />
                    </div>
                    <select value={purchaseForm.quantityRemainingUnit}
                      onChange={(e) => setPurchaseForm({ ...purchaseForm, quantityRemainingUnit: e.target.value })}
                      className="w-full p-3 border-2 border-gray-300 rounded-lg">
                      <option value="L">L</option>
                      <option value="mL">mL</option>
                      <option value="kg">kg</option>
                      <option value="g">g</option>
                    </select>
                  </div>
                </div>
                <div className="mt-3">
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Batch Number *</label>
                  <input type="text" value={purchaseForm.batchNumber}
                    onChange={(e) => setPurchaseForm({ ...purchaseForm, batchNumber: e.target.value })}
                    placeholder="e.g., ABC123456" className="w-full p-3 border-2 border-gray-300 rounded-lg" />
                </div>
                <div className="mt-3 grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Manufacturing Date (optional)</label>
                    <input type="date" value={purchaseForm.manufacturingDate}
                      onChange={(e) => handleManufacturingDateChange(e.target.value)}
                      className="w-full p-3 border-2 border-gray-300 rounded-lg" />
                    <p className="text-xs text-gray-500 mt-1">Auto-calculates expiry (+3 years)</p>
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Expiry Date *</label>
                    <input type="date" value={purchaseForm.expiryDate}
                      onChange={(e) => setPurchaseForm({ ...purchaseForm, expiryDate: e.target.value })}
                      className="w-full p-3 border-2 border-gray-300 rounded-lg" />
                  </div>
                </div>
                {/* Fill in from Label button for known chemicals — batch/dates area */}
                {purchaseForm.selectedChemical !== 'Other' && (
                  <div className="mt-3">
                    <button onClick={() => handlePopulateFromLabel('batch')}
                      className="w-full bg-blue-600 text-white p-3 rounded-lg font-semibold hover:bg-blue-700">
                      {'\ud83d\udcf7'} Fill in from Label
                    </button>
                    <p className="text-xs text-gray-500 mt-1">Use camera to read batch number and dates from the label</p>
                  </div>
                )}
              </div>
            )}

            {purchaseForm.selectedChemical && (
              <div className="flex space-x-3 pt-4">
                <button onClick={savePurchase}
                  className="flex-1 bg-green-600 text-white p-4 rounded-lg text-lg font-semibold hover:bg-green-700">
                  {'\u2713'} Save Purchase
                </button>
                <button onClick={() => setSection('home')}
                  className="flex-1 bg-gray-500 text-white p-4 rounded-lg text-lg font-semibold hover:bg-gray-600">
                  Cancel
                </button>
              </div>
            )}
            {!purchaseForm.selectedChemical && (
              <div className="flex pt-2">
                <button onClick={() => setSection('home')}
                  className="flex-1 bg-gray-500 text-white p-4 rounded-lg text-lg font-semibold hover:bg-gray-600">
                  Cancel
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  // Animal Medication Purchase Form
  if (section === 'purchaseMeds') {
    const animalMedsOnly = allChemicals.filter(c =>
      ['Parasiticide', 'Vaccine', 'Supplement', 'Hormone', 'Antibiotic', 'Anthelmintic'].includes(c.category)
    );
    return (
      <div className="min-h-screen bg-orange-50 p-4">
        {ConfirmModal}
        <div className="max-w-2xl mx-auto">
          <div className="bg-white rounded-lg shadow-lg p-6 mb-6">
            <h2 className="text-2xl font-bold text-center text-orange-800">
              {'\ud83d\udc89'} Animal Medication Purchase
            </h2>
            <p className="text-center text-gray-600">Add to Inventory</p>
          </div>

          {showPhotoInstructions && (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
              <div className="bg-white rounded-lg shadow-2xl p-6 max-w-md w-full">
                <h3 className="text-xl font-bold text-gray-800 mb-4">{'\ud83d\udcf7'} Photo Instructions</h3>
                <div className="space-y-3 mb-6">
                  <p className="text-sm text-gray-700">Take up to <strong>3 clear photos</strong> focusing on:</p>
                  {labelPhotoContext === 'full' ? (
                    <ul className="text-sm text-gray-700 space-y-2 ml-4">
                      <li>{'\u2713'} Product name</li>
                      <li>{'\u2713'} Active ingredient and concentration</li>
                      <li>{'\u2713'} Withholding period</li>
                      <li>{'\u2713'} Batch/Lot number</li>
                      <li>{'\u2713'} Manufacturing date</li>
                      <li>{'\u2713'} Container size</li>
                    </ul>
                  ) : (
                    <ul className="text-sm text-gray-700 space-y-2 ml-4">
                      <li>{'\u2713'} Batch/Lot number</li>
                      <li>{'\u2713'} Manufacturing date</li>
                      <li>{'\u2713'} Expiry date</li>
                      <li>{'\u2713'} Container size</li>
                    </ul>
                  )}
                </div>
                {labelPhotos.length > 0 && (
                  <div className="mb-4">
                    <p className="text-sm font-semibold text-green-700 mb-2">{'\u2713'} {labelPhotos.length} photo(s) captured</p>
                    <div className="grid grid-cols-3 gap-2">
                      {labelPhotos.map((photo, idx) => (
                        <img key={idx} src={photo.dataUrl} alt={`Label ${idx + 1}`} className="w-full h-20 object-cover rounded border-2 border-green-500" />
                      ))}
                    </div>
                  </div>
                )}
                <div className="flex space-x-3">
                  {labelPhotos.length < 3 && (
                    <button onClick={captureLabelPhoto} className="flex-1 bg-blue-600 text-white p-3 rounded-lg font-semibold hover:bg-blue-700">
                      {'\ud83d\udcf7'} Take Photo ({labelPhotos.length}/3)
                    </button>
                  )}
                  {labelPhotos.length > 0 && (
                    <button onClick={processLabelPhotos} className="flex-1 bg-green-600 text-white p-3 rounded-lg font-semibold hover:bg-green-700">
                      {'\u2713'} Process Photos
                    </button>
                  )}
                </div>
                <button onClick={() => { setShowPhotoInstructions(false); setLabelPhotos([]); }}
                  className="w-full bg-gray-500 text-white p-3 rounded-lg font-semibold hover:bg-gray-600 mt-3">
                  Cancel
                </button>
              </div>
            </div>
          )}
          {isProcessingPhotos && (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
              <div className="bg-white rounded-lg shadow-2xl p-8 max-w-md w-full text-center">
                <div className="text-6xl mb-4">{'\u23f3'}</div>
                <h3 className="text-xl font-bold text-gray-800 mb-2">Processing Photos...</h3>
                <p className="text-gray-600">AI is reading the label information</p>
              </div>
            </div>
          )}

          {/* Extracted data confirmation dialog - Animal Medications */}
          {showConfirmationDialog && extractedData && (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
              <div className="bg-white rounded-lg shadow-2xl p-6 max-w-md w-full max-h-screen overflow-y-auto">
                <h3 className="text-xl font-bold text-gray-800 mb-2">
                  {extractedData.isExisting ? '\u2713 Medication Found in Database' : '\u26a0\ufe0f New Medication Detected'}
                </h3>
                <p className="text-sm text-gray-600 mb-4">
                  {extractedData.isExisting
                    ? `"${extractedData.chemicalName}" — using database values for medication details.`
                    : `"${extractedData.chemicalName}" will be added to your database.`}
                </p>
                {!extractedData.isExisting && extractedData.missingFields && extractedData.missingFields.length > 0 && (
                  <div className="bg-yellow-50 border border-yellow-300 rounded-lg p-3 mb-4">
                    <p className="text-sm font-semibold text-yellow-800 mb-1">{'\u26a0\ufe0f'} Not found on label — fill in manually:</p>
                    <ul className="text-sm text-yellow-700 space-y-1">
                      {extractedData.missingFields.map(f => (
                        <li key={f}>{'\u2022'} {f}</li>
                      ))}
                    </ul>
                  </div>
                )}
                <p className="text-sm font-semibold text-gray-700 mb-3">Information extracted from label:</p>
                <div className="space-y-2 mb-4">
                  {[
                    { key: 'chemicalName', label: 'Product Name', value: extractedData.chemicalName },
                    { key: 'activeIngredient', label: 'Active Ingredient', value: extractedData.activeIngredient },
                    { key: 'category', label: 'Category', value: extractedData.category },
                    { key: 'withholdingPeriod', label: 'Withholding Period', value: extractedData.withholdingPeriod },
                    { key: 'withholdingDays', label: 'WHP (days)', value: extractedData.withholdingDays != null ? String(extractedData.withholdingDays) : null },
                    { key: 'esiDays', label: 'ESI (days)', value: extractedData.esiDays != null ? String(extractedData.esiDays) : null },
                    { key: 'applicationRate', label: 'Dose Rate', value: extractedData.animalRate || extractedData.landRate || extractedData.applicationRate },
                    { key: 'batchNumber', label: 'Batch Number', value: extractedData.batchNumber },
                    { key: 'containerSize', label: 'Container Size', value: extractedData.containerSizeNumber ? `${extractedData.containerSizeNumber} ${extractedData.containerSizeUnit}` : null },
                    { key: 'expiryDate', label: 'Expiry Date', value: extractedData.expiryDate },
                    { key: 'manufacturingDate', label: 'Manufacturing Date', value: extractedData.manufacturingDate },
                  ].filter(f => f.value).map(field => (
                    <div key={field.key} className="bg-gray-50 rounded p-2">
                      <span className="text-sm text-gray-700">{field.label}: <strong>{field.value}</strong></span>
                    </div>
                  ))}
                </div>
                <div className="flex space-x-3">
                  <button
                    onClick={() => {
                      setShowConfirmationDialog(false);
                      setExtractedData(null);
                      setLabelPhotos([]);
                      setConfirmationChecks({});
                    }}
                    className="flex-1 bg-gray-500 text-white p-3 rounded-lg font-semibold"
                  >
                    Go Back
                  </button>
                  <button
                    onClick={handleConfirmExtractedData}
                    className="flex-1 bg-green-600 text-white p-3 rounded-lg font-semibold"
                  >
                    {'\u2713'} Use This Data
                  </button>
                </div>
              </div>
            </div>
          )}

          <div className="bg-white rounded-lg shadow-lg p-6 space-y-4">

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">Purchase Date *</label>
              <input type="date" value={purchaseForm.purchaseDate}
                onChange={(e) => setPurchaseForm({ ...purchaseForm, purchaseDate: e.target.value })}
                className="w-full p-3 border-2 border-gray-300 rounded-lg" />
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">Medication/Product *</label>
              <select value={purchaseForm.selectedChemical}
                onChange={(e) => handlePurchaseChemicalSelect(e.target.value)}
                className="w-full p-3 border-2 border-gray-300 rounded-lg">
                <option value="">Select medication...</option>
                <option value="Other">Other (Add New Medication)</option>
                {animalMedsOnly.sort((a, b) => a.name.localeCompare(b.name)).map(c => (
                  <option key={c.name} value={c.name}>{c.name}</option>
                ))}
              </select>
            </div>

            {/* Other selected: name field + lookup/label buttons + extra fields */}
            {purchaseForm.selectedChemical === 'Other' && (
              <>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Product Name *</label>
                  <input type="text" value={purchaseForm.chemicalName}
                    onChange={(e) => setPurchaseForm({ ...purchaseForm, chemicalName: e.target.value })}
                    placeholder="Enter product name"
                    className="w-full p-3 border-2 border-gray-300 rounded-lg" />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <button onClick={() => handlePopulateFromLabel('full')}
                    className="bg-blue-600 text-white p-4 rounded-lg font-semibold hover:bg-blue-700">
                    {'\ud83d\udcf7'} Fill in from Label
                  </button>
                  <button onClick={handleLookup}
                    disabled={!purchaseForm.chemicalName || isLookingUp}
                    className={`p-4 rounded-lg font-semibold ${purchaseForm.chemicalName && !isLookingUp ? 'bg-purple-600 text-white hover:bg-purple-700' : 'bg-gray-300 text-gray-500 cursor-not-allowed'}`}>
                    {isLookingUp ? '\u23f3 Looking up...' : '\ud83d\udd0d Look up Chemical Name'}
                  </button>
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Active Ingredient</label>
                  <input type="text" value={purchaseForm.activeIngredient}
                    onChange={(e) => setPurchaseForm({ ...purchaseForm, activeIngredient: e.target.value })}
                    placeholder="e.g., Doramectin 10mg/mL"
                    className="w-full p-3 border-2 border-gray-300 rounded-lg" />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Category</label>
                  <select value={purchaseForm.category}
                    onChange={(e) => setPurchaseForm({ ...purchaseForm, category: e.target.value })}
                    className="w-full p-3 border-2 border-gray-300 rounded-lg">
                    <option value="">Select...</option>
                    <option>Parasiticide</option>
                    <option>Vaccine</option>
                    <option>Supplement</option>
                    <option>Anthelmintic</option>
                    <option>Antibiotic</option>
                    <option>Hormone</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Dose Rate *</label>
                  <div className="flex gap-2">
                    <input type="number" step="0.001" style={{MozAppearance:"textfield"}} onWheel={(e)=>e.target.blur()} value={purchaseForm.animalRateNumber}
                      onChange={(e) => setPurchaseForm({ ...purchaseForm, animalRateNumber: e.target.value })}
                      placeholder="e.g., 1"
                      className="flex-1 p-3 border-2 border-gray-300 rounded-lg" />
                    <select value={purchaseForm.animalRateUnit}
                      onChange={(e) => setPurchaseForm({ ...purchaseForm, animalRateUnit: e.target.value })}
                      className="p-3 border-2 border-gray-300 rounded-lg">
                      <option value="mL/kg">mL/kg</option>
                      <option value="mL/animal">mL/animal</option>
                      <option value="g/kg">g/kg</option>
                      <option value="tablets/kg">tablets/kg</option>
                      <option value="tablets/animal">tablets/animal</option>
                      <option value="syringes/animal">syringes/animal</option>
                    </select>
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Withholding Period (from label)</label>
                  <input type="text" value={purchaseForm.withholdingPeriod}
                    readOnly
                    placeholder="Auto-filled from label scan"
                    className="w-full p-3 border-2 border-gray-200 rounded-lg bg-amber-50 text-amber-900 cursor-default" />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">WHP (days)</label>
                  <div className="flex items-center gap-2">
                    <input type="number" min="0" value={purchaseForm.withholdingDays} style={{MozAppearance:"textfield"}} onWheel={(e)=>e.target.blur()}
                      onChange={(e) => setPurchaseForm({ ...purchaseForm, withholdingDays: e.target.value })}
                      placeholder="e.g., 28"
                      className="flex-1 p-3 border-2 border-gray-300 rounded-lg" />
                    <span className="text-gray-600 font-medium whitespace-nowrap">days</span>
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">ESI — Export Slaughter Interval (days)</label>
                  <div className="flex items-center gap-2">
                    <input type="number" min="0" value={purchaseForm.esiDays} style={{MozAppearance:"textfield"}} onWheel={(e)=>e.target.blur()}
                      onChange={(e) => setPurchaseForm({ ...purchaseForm, esiDays: e.target.value })}
                      placeholder="e.g., 42"
                      className="flex-1 p-3 border-2 border-gray-300 rounded-lg" />
                    <span className="text-gray-600 font-medium whitespace-nowrap">days</span>
                  </div>
                </div>
              </>
            )}

            {/* Known medication: show info summary + read-only WHP and ESI from DB */}
            {purchaseForm.selectedChemical && purchaseForm.selectedChemical !== 'Other' && (
              <div className="bg-orange-50 p-4 rounded-lg space-y-2">
                <div className="text-sm text-gray-700 space-y-1">
                  <div><strong>Active Ingredient:</strong> {purchaseForm.activeIngredient}</div>
                  <div><strong>Category:</strong> {purchaseForm.category}</div>
                  {purchaseForm.animalRate && <div><strong>Dose Rate:</strong> {purchaseForm.animalRate}</div>}
                </div>
                {purchaseForm.withholdingPeriod && (
                  <div className="bg-amber-50 border border-amber-300 rounded p-2">
                    <p className="text-xs font-semibold text-amber-800 mb-0.5">Withholding Period (from database)</p>
                    <p className="text-sm text-amber-900 font-medium">{purchaseForm.withholdingPeriod}</p>
                  </div>
                )}
                {purchaseForm.esiDays !== '' && purchaseForm.esiDays !== null && purchaseForm.esiDays !== undefined && (
                  <div className="bg-red-50 border border-red-200 rounded p-2">
                    <p className="text-xs font-semibold text-red-800 mb-0.5">ESI — Export Slaughter Interval (from database)</p>
                    <p className="text-sm text-red-900 font-medium">{purchaseForm.esiDays} days</p>
                  </div>
                )}
                <p className="text-xs text-amber-700">WHP and ESI days are entered when recording usage, as they depend on the animal and application.</p>
              </div>
            )}

            {/* Container details — only show once a medication is selected */}
            {purchaseForm.selectedChemical && (
              <div className="border-t-2 border-gray-200 pt-4">
                <h3 className="text-lg font-bold text-gray-800 mb-3">Container Details</h3>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Container Size *</label>
                  <div className="grid grid-cols-3 gap-3">
                    <div className="col-span-2">
                      <input type="number" step="0.1" style={{MozAppearance:"textfield"}} onWheel={(e)=>e.target.blur()} value={purchaseForm.containerSizeNumber}
                        onChange={(e) => {
                          const newSize = e.target.value;
                          setPurchaseForm(prev => {
                            const isSynced = prev.quantityRemainingNumber === '' || prev.quantityRemainingNumber === prev.containerSizeNumber;
                            return { ...prev, containerSizeNumber: newSize, quantityRemainingNumber: isSynced ? newSize : prev.quantityRemainingNumber };
                          });
                        }}
                        placeholder="e.g., 500" className="w-full p-3 border-2 border-gray-300 rounded-lg" />
                    </div>
                    <select value={purchaseForm.containerSizeUnit}
                      onChange={(e) => setPurchaseForm({ ...purchaseForm, containerSizeUnit: e.target.value, quantityRemainingUnit: e.target.value })}
                      className="w-full p-3 border-2 border-gray-300 rounded-lg">
                      <option value="mL">mL</option>
                      <option value="L">L</option>
                      <option value="tablets">tablets</option>
                      <option value="syringes">syringes</option>
                      <option value="g">g</option>
                      <option value="kg">kg</option>
                    </select>
                  </div>
                </div>
                <div className="mt-3">
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Quantity Remaining (editable for partial containers)</label>
                  <div className="grid grid-cols-3 gap-3">
                    <div className="col-span-2">
                      <input type="text" inputMode="decimal" style={{MozAppearance:"textfield"}} onWheel={(e)=>e.target.blur()}
                        value={purchaseForm.quantityRemainingNumber}
                        onChange={(e) => setPurchaseForm({ ...purchaseForm, quantityRemainingNumber: e.target.value })}
                        placeholder="Auto-filled from container size"
                        className="w-full p-3 border-2 border-gray-300 rounded-lg" />
                    </div>
                    <select value={purchaseForm.quantityRemainingUnit}
                      onChange={(e) => setPurchaseForm({ ...purchaseForm, quantityRemainingUnit: e.target.value })}
                      className="w-full p-3 border-2 border-gray-300 rounded-lg">
                      <option value="mL">mL</option>
                      <option value="L">L</option>
                      <option value="tablets">tablets</option>
                      <option value="syringes">syringes</option>
                      <option value="g">g</option>
                      <option value="kg">kg</option>
                    </select>
                  </div>
                </div>
                <div className="mt-3">
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Batch Number *</label>
                  <input type="text" value={purchaseForm.batchNumber}
                    onChange={(e) => setPurchaseForm({ ...purchaseForm, batchNumber: e.target.value })}
                    placeholder="e.g., ABC123456" className="w-full p-3 border-2 border-gray-300 rounded-lg" />
                </div>
                <div className="mt-3">
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Expiry Date *</label>
                  <input type="date" value={purchaseForm.expiryDate}
                    onChange={(e) => setPurchaseForm({ ...purchaseForm, expiryDate: e.target.value })}
                    className="w-full p-3 border-2 border-gray-300 rounded-lg" />
                </div>
                {/* Fill in from Label for known medications */}
                {purchaseForm.selectedChemical !== 'Other' && (
                  <div className="mt-3">
                    <button onClick={() => handlePopulateFromLabel('batch')}
                      className="w-full bg-blue-600 text-white p-3 rounded-lg font-semibold hover:bg-blue-700">
                      {'\ud83d\udcf7'} Fill in from Label
                    </button>
                    <p className="text-xs text-gray-500 mt-1">Use camera to read batch number and expiry date from the label</p>
                  </div>
                )}
              </div>
            )}

            {purchaseForm.selectedChemical && (
              <div className="flex space-x-3 pt-4">
                <button onClick={savePurchase}
                  className="flex-1 bg-green-600 text-white p-4 rounded-lg text-lg font-semibold hover:bg-green-700">
                  {'\u2713'} Save Purchase
                </button>
                <button onClick={() => setSection('home')}
                  className="flex-1 bg-gray-500 text-white p-4 rounded-lg text-lg font-semibold hover:bg-gray-600">
                  Cancel
                </button>
              </div>
            )}
            {!purchaseForm.selectedChemical && (
              <div className="flex pt-2">
                <button onClick={() => setSection('home')}
                  className="flex-1 bg-gray-500 text-white p-4 rounded-lg text-lg font-semibold hover:bg-gray-600">
                  Cancel
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  // Inventory Report
  if (section === 'inventory') {
    const today = new Date();
    const twoMonthsFromNow = new Date();
    twoMonthsFromNow.setMonth(today.getMonth() + 2);

    // Split inventory into Land and Animal categories
    const landCategories = ['Herbicide', 'Insecticide', 'Fungicide', 'Rodenticide', 'Marker'];
    const animalCategories = ['Parasiticide', 'Vaccine', 'Antibiotic', 'Supplement', 'Hormone', 'Anthelmintic'];

    const landInventory = [...inventory]
      .filter(item => landCategories.includes(item.category))
      .sort((a, b) => a.chemicalName.localeCompare(b.chemicalName));

    const animalInventory = [...inventory]
      .filter(item => animalCategories.includes(item.category))
      .sort((a, b) => a.chemicalName.localeCompare(b.chemicalName));

    const renderInventoryItem = (item) => {
      const expiry = new Date(item.expiryDate);
      const isExpired = expiry < today;
      const isExpiringSoon = expiry <= twoMonthsFromNow && !isExpired;
      
      return (
        <div 
          key={item.id} 
          className={`bg-white rounded-lg shadow-lg p-4 ${
            isExpired ? 'border-2 border-red-500' : 
            isExpiringSoon ? 'border-2 border-orange-400' : ''
          }`}
        >
          <div className="flex justify-between items-start mb-3">
            <div className="flex-1">
              <div className="font-bold text-lg text-gray-900 mb-1">
                {item.chemicalName}
                {isExpired && <span className="ml-2 text-red-600 font-bold"> {'\u26a0\ufe0f'} EXPIRED</span>}
                {isExpiringSoon && !isExpired && <span className="ml-2 text-orange-600 font-bold">{'\u26a0\ufe0f'} Expiring Soon</span>}
              </div>
              
              <div className="text-sm text-gray-600 mb-2">
                {item.activeIngredient} {'\u2022'} {item.category}
              </div>

              <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-sm">
                <div><span className="font-semibold">Batch:</span> {item.batchNumber}</div>
                <div><span className="font-semibold">Container:</span> {item.containerSizeNumber} {item.containerSizeUnit}</div>
                <div className={parseFloat(item.quantityRemainingNumber) === 0 ? 'text-red-600 font-bold' : ''}>
                  <span className="font-semibold">Remaining:</span> {item.quantityRemainingNumber} {item.quantityRemainingUnit}
                </div>
                {item.manufacturingDate && (
                  <div><span className="font-semibold">Mfg Date:</span> {new Date(item.manufacturingDate).toLocaleDateString('en-AU')}</div>
                )}
                <div className={isExpired ? 'text-red-600 font-bold' : isExpiringSoon ? 'text-orange-600 font-semibold' : ''}>
                  <span className="font-semibold">Expiry:</span> {new Date(item.expiryDate).toLocaleDateString('en-AU')}
                </div>
              </div>
            </div>

            <div className="flex flex-col space-y-2 ml-4">
              <button
                onClick={() => {
                  const newQty = prompt(`Edit quantity remaining for ${item.chemicalName} (Batch ${item.batchNumber}):\n\nCurrent: ${item.quantityRemainingNumber} ${item.quantityRemainingUnit}`, item.quantityRemainingNumber);
                  if (newQty === null) return;
                  const trimmed = newQty.trim();
                  const parsed = parseFloat(trimmed);
                  if (trimmed === '' || isNaN(parsed) || !isFinite(parsed) || parsed < 0 || !/^\d*\.?\d*$/.test(trimmed)) {
                    alert('Please enter a valid quantity (a number of 0 or more).');
                    return;
                  }
                  setInventory(inventory.map(inv =>
                    inv.id === item.id ? { ...inv, quantityRemainingNumber: trimmed } : inv
                  ));
                }}
                className="bg-blue-500 text-white px-3 py-1 rounded text-sm font-medium hover:bg-blue-600"
              >
                Edit Qty
              </button>
              <button
                onClick={() => setEditingInventoryItem({ ...item })}
                className="bg-blue-200 text-black px-3 py-1 rounded text-sm font-medium hover:bg-blue-300"
              >
                Edit Record
              </button>
              <button
                onClick={() => {
                  // Match records that used this specific product + batch.
                  // For multi-medication animal records, check inside medications[].
                  const usageRecords = records.filter(r => {
                    if (r.type === 'land') {
                      // Land: check chemicals array or legacy top-level fields
                      if (r.chemicals && r.chemicals.length > 0) {
                        return r.chemicals.some(c => c.productName === item.chemicalName && c.batchNumber === item.batchNumber);
                      }
                      return r.productName === item.chemicalName && r.batchNumber === item.batchNumber;
                    } else {
                      // Animal: check medications array first, then legacy top-level fields
                      if (r.medications && r.medications.length > 0) {
                        return r.medications.some(m => m.productName === item.chemicalName && m.batchNumber === item.batchNumber);
                      }
                      return r.productName === item.chemicalName && r.batchNumber === item.batchNumber;
                    }
                  });

                  if (usageRecords.length === 0) {
                    alert(`No usage records found for ${item.chemicalName} (Batch ${item.batchNumber})`);
                  } else {
                    const historyText = usageRecords.map(r => {
                      if (r.type === 'land') {
                        // Find the specific chemical entry for this batch
                        const chem = (r.chemicals || []).find(c => c.productName === item.chemicalName && c.batchNumber === item.batchNumber);
                        const qtyUsed = chem
                          ? (chem.calculatedQty ? `${chem.calculatedQty} ${chem.calculatedQtyUnit || ''}`.trim() : 'qty not recorded')
                          : (r.totalQuantityUsed || 'qty not recorded');
                        return `${r.date} - Land: ${qtyUsed}\n  Area: ${r.areaHectares} ha, Paddock: ${r.paddockName || 'n/a'}`;
                      } else {
                        // Find the specific medication entry for this batch
                        const med = (r.medications || []).find(m => m.productName === item.chemicalName && m.batchNumber === item.batchNumber);
                        const qty = med ? med.totalQuantity : r.totalQuantity;
                        const doseUnit = (() => {
                          const u = (med || r).doseRateUnit || '';
                          if (u.startsWith('tablets')) return 'tablets';
                          if (u.startsWith('syringes')) return 'syringes';
                          if (u.startsWith('g')) return 'g';
                          return 'mL';
                        })();
                        const qtyDisplay = qty ? `${qty} ${doseUnit}` : 'qty not recorded';
                        const otherMeds = r.medications && r.medications.length > 1
                          ? `\n  Also given: ${r.medications.filter(m => m.productName !== item.chemicalName).map(m => m.productName).join(', ')}`
                          : '';
                        return `${r.date} - Animal: ${qtyDisplay}\n  ${r.species}, ${r.animalCount} animals${otherMeds}`;
                      }
                    }).join('\n\n');

                    alert(
                      `Usage History for ${item.chemicalName}\n` +
                      `Batch: ${item.batchNumber}\n\n` +
                      `Original Purchase: ${item.containerSizeNumber} ${item.containerSizeUnit}\n` +
                      `Current Remaining: ${item.quantityRemainingNumber} ${item.quantityRemainingUnit}\n\n` +
                      `Usage Records:\n${historyText}`
                    );
                  }
                }}
                className="bg-purple-500 text-white px-3 py-1 rounded text-sm font-medium hover:bg-purple-600"
              >
                Usage
              </button>
              <button
                onClick={() => deleteInventoryItem(item.id)}
                className="bg-red-500 text-white px-3 py-1 rounded text-sm font-medium hover:bg-red-600"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      );
    };

    return (
      <>
      {ConfirmModal}
      {/* Edit Inventory Record Modal */}
      {editingInventoryItem && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-lg max-h-screen overflow-y-auto">
            <div className="bg-blue-600 text-white p-4 rounded-t-lg">
              <h2 className="text-xl font-bold">Edit Inventory Record</h2>
              <p className="text-sm text-blue-100">{editingInventoryItem.chemicalName}</p>
            </div>
            <div className="p-4 space-y-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Product Name</label>
                <input type="text" value={editingInventoryItem.chemicalName}
                  onChange={e => setEditingInventoryItem({...editingInventoryItem, chemicalName: e.target.value})}
                  className="w-full border border-gray-300 rounded p-2 text-sm" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Active Ingredient</label>
                <input type="text" value={editingInventoryItem.activeIngredient || ''}
                  onChange={e => setEditingInventoryItem({...editingInventoryItem, activeIngredient: e.target.value})}
                  className="w-full border border-gray-300 rounded p-2 text-sm" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Category</label>
                <select value={editingInventoryItem.category || ''}
                  onChange={e => setEditingInventoryItem({...editingInventoryItem, category: e.target.value})}
                  className="w-full border border-gray-300 rounded p-2 text-sm">
                  <option value="Herbicide">Herbicide</option>
                  <option value="Insecticide">Insecticide</option>
                  <option value="Fungicide">Fungicide</option>
                  <option value="Rodenticide">Rodenticide</option>
                  <option value="Marker">Marker</option>
                  <option value="Parasiticide">Parasiticide</option>
                  <option value="Vaccine">Vaccine</option>
                  <option value="Antibiotic">Antibiotic</option>
                  <option value="Supplement">Supplement</option>
                  <option value="Hormone">Hormone</option>
                  <option value="Anthelmintic">Anthelmintic</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Batch Number</label>
                <input type="text" value={editingInventoryItem.batchNumber || ''}
                  onChange={e => setEditingInventoryItem({...editingInventoryItem, batchNumber: e.target.value})}
                  className="w-full border border-gray-300 rounded p-2 text-sm" />
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Container Size</label>
                  <input type="number" value={editingInventoryItem.containerSizeNumber || ''}
                    onChange={e => setEditingInventoryItem({...editingInventoryItem, containerSizeNumber: e.target.value})}
                    className="w-full border border-gray-300 rounded p-2 text-sm" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Unit</label>
                  <select value={editingInventoryItem.containerSizeUnit || 'L'}
                    onChange={e => setEditingInventoryItem({...editingInventoryItem, containerSizeUnit: e.target.value, quantityRemainingUnit: e.target.value})}
                    className="w-full border border-gray-300 rounded p-2 text-sm">
                    <option value="mL">mL</option>
                    <option value="L">L</option>
                    <option value="g">g</option>
                    <option value="kg">kg</option>
                    <option value="tablets">tablets</option>
                  </select>
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Qty Remaining ({editingInventoryItem.quantityRemainingUnit || editingInventoryItem.containerSizeUnit})</label>
                <input type="number" value={editingInventoryItem.quantityRemainingNumber || ''}
                  onChange={e => setEditingInventoryItem({...editingInventoryItem, quantityRemainingNumber: e.target.value})}
                  className="w-full border border-gray-300 rounded p-2 text-sm" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Purchase Date</label>
                <input type="date" value={editingInventoryItem.purchaseDate || ''}
                  onChange={e => setEditingInventoryItem({...editingInventoryItem, purchaseDate: e.target.value})}
                  className="w-full border border-gray-300 rounded p-2 text-sm" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Manufacturing Date</label>
                <input type="date" value={editingInventoryItem.manufacturingDate || ''}
                  onChange={e => setEditingInventoryItem({...editingInventoryItem, manufacturingDate: e.target.value})}
                  className="w-full border border-gray-300 rounded p-2 text-sm" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Expiry Date</label>
                <input type="date" value={editingInventoryItem.expiryDate || ''}
                  onChange={e => setEditingInventoryItem({...editingInventoryItem, expiryDate: e.target.value})}
                  className="w-full border border-gray-300 rounded p-2 text-sm" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Withholding Period</label>
                <input type="text" value={editingInventoryItem.withholdingPeriod || ''}
                  onChange={e => setEditingInventoryItem({...editingInventoryItem, withholdingPeriod: e.target.value})}
                  className="w-full border border-gray-300 rounded p-2 text-sm" placeholder="e.g. 14 days" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Application Rate</label>
                <input type="text" value={editingInventoryItem.applicationRate || ''}
                  onChange={e => setEditingInventoryItem({...editingInventoryItem, applicationRate: e.target.value})}
                  className="w-full border border-gray-300 rounded p-2 text-sm" placeholder="e.g. 2 L/ha" />
              </div>
              <div className="flex space-x-3 pt-2">
                <button
                  onClick={() => {
                    setInventory(inventory.map(inv => inv.id === editingInventoryItem.id ? editingInventoryItem : inv));
                    setEditingInventoryItem(null);
                  }}
                  className="flex-1 bg-green-600 text-white p-3 rounded-lg font-semibold hover:bg-green-700"
                >
                  ✓ Save Changes
                </button>
                <button
                  onClick={() => setEditingInventoryItem(null)}
                  className="flex-1 bg-gray-500 text-white p-3 rounded-lg font-semibold hover:bg-gray-600"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="min-h-screen bg-purple-50 p-4">
        <div className="max-w-4xl mx-auto">
          <div className="bg-white rounded-lg shadow-lg p-6 mb-6">
            <h2 className="text-2xl font-bold text-center text-purple-800">Stock on Hand</h2>
            <p className="text-center text-gray-600">{inventory.length} inventory items</p>
          </div>

          <button
            onClick={exportInventoryToCSV}
            className="w-full bg-green-600 text-white p-4 rounded-lg text-lg font-semibold hover:bg-green-700 mb-6"
          >
            {'\ud83d\udcca'} Export to CSV
          </button>

          {inventory.length === 0 ? (
            <div className="bg-white rounded-lg shadow-lg p-8 text-center text-gray-500 mb-6">
              No inventory items yet. Record a purchase to get started.
            </div>
          ) : (
            <>
              {/* Land Chemicals Section */}
              {landInventory.length > 0 && (
                <div className="mb-6">
                  <div className="bg-green-600 text-white p-3 rounded-lg mb-3">
                    <h3 className="text-xl font-bold">{'\ud83c\udf3e'} Land Chemicals ({landInventory.length})</h3>
                    <p className="text-sm">Herbicides, Insecticides, Fungicides, Rodenticides</p>
                  </div>
                  <div className="space-y-3">
                    {landInventory.map(renderInventoryItem)}
                  </div>
                </div>
              )}

              {/* Animal Medications Section */}
              {animalInventory.length > 0 && (
                <div className="mb-6">
                  <div className="bg-orange-600 text-white p-3 rounded-lg mb-3">
                    <h3 className="text-xl font-bold">{'\ud83d\udc04'} Animal Medications ({animalInventory.length})</h3>
                    <p className="text-sm">Parasiticides, Vaccines, Antibiotics, Supplements</p>
                  </div>
                  <div className="space-y-3">
                    {animalInventory.map(renderInventoryItem)}
                  </div>
                </div>
              )}
            </>
          )}

          <button
            onClick={() => setSection('home')}
            className="w-full bg-gray-500 text-white p-4 rounded-xl text-xl font-semibold hover:bg-gray-600"
          >
            {'\u2190'} Back to Home
          </button>
        </div>
      </div>
      </>
    );
  }

  // Land Pesticides Form
  if (section === 'land') {
    const landChemOptions = allChemicals
      .filter(c => ['Herbicide', 'Insecticide', 'Fungicide', 'Rodenticide'].includes(c.category))
      .sort((a, b) => a.name.localeCompare(b.name));

// Spray Rec Review Modal
    const SprayRecModal = sprayRecModal.visible ? (
      <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center p-4 z-50">
        <div className="bg-white rounded-xl shadow-2xl p-6 max-w-lg w-full max-h-screen overflow-y-auto">
          <h3 className="text-xl font-bold text-blue-800 mb-4">📄 Spray Rec Import</h3>

          {sprayRecModal.loading && (
            <div className="text-center py-8">
              <div className="text-4xl mb-3">⏳</div>
              <p className="text-gray-600 font-medium">Reading PDF...</p>
              <p className="text-sm text-gray-400 mt-1">This takes a few seconds</p>
            </div>
          )}

          {sprayRecModal.error && (
            <div className="bg-red-50 border border-red-300 rounded-lg p-4 mb-4">
              <p className="text-red-800 font-semibold">❌ Could not import</p>
              <p className="text-sm text-red-700 mt-1">{sprayRecModal.error}</p>
            </div>
          )}

          {!sprayRecModal.loading && !sprayRecModal.error && sprayRecModal.parsed && (() => {
            const p = sprayRecModal.parsed;
            return (
              <div className="space-y-4">

                {/* Paddocks */}
                <div>
                  <p className="text-sm font-bold text-gray-700 mb-1">Paddocks</p>
                  {(p.paddocks || []).length === 0
                    ? <p className="text-sm text-red-600">None found</p>
                    : (p.paddocks || []).map((pd, i) => (
                        <div key={i} className="text-sm text-gray-700 bg-blue-50 rounded px-2 py-1 mb-1">
                          {pd.name} — {pd.areaHectares} ha
                          {!savedPaddocks.some(sp => sp.name.toLowerCase() === pd.name.toLowerCase()) && (
                            <span className="ml-2 text-xs text-green-700 font-semibold">NEW</span>
                          )}
                        </div>
                      ))
                  }
                </div>

                {/* Crop */}
                {p.cropLandUse && (
                  <div>
                    <p className="text-sm font-bold text-gray-700 mb-1">Crop/Land Use</p>
                    <p className="text-sm text-gray-700 bg-gray-50 rounded px-2 py-1">{p.cropLandUse}</p>
                  </div>
                )}

                {/* Pest targets */}
                <div>
                  <p className="text-sm font-bold text-gray-700 mb-1">Pest/Weed Targets</p>
                  {(p.pestTargets || []).length === 0
                    ? <p className="text-sm text-red-600">None found</p>
                    : <div className="flex flex-wrap gap-1">
                        {(p.pestTargets || []).map((w, i) => (
                          <span key={i} className="text-xs bg-yellow-100 text-yellow-800 px-2 py-1 rounded-full">
                            {w}
                            {!savedWeeds.some(sw => sw.toLowerCase() === w.toLowerCase()) && (
                              <span className="ml-1 text-green-700 font-bold">+</span>
                            )}
                          </span>
                        ))}
                      </div>
                  }
                </div>

                {/* Herbicides with stock check */}
                <div>
                  <p className="text-sm font-bold text-gray-700 mb-1">Herbicides/Pesticides</p>
                  {(p.herbicides || []).length === 0
                    ? <p className="text-sm text-red-600">None found</p>
                    : (p.herbicides || []).map((h, i) => {
                        const stock = checkSprayRecStock(h.productName, h.totalQty, h.totalQtyUnit);
                        const stockIcon = stock.status === 'ok' ? '✅' : stock.status === 'low' ? '⚠️' : '❌';
                        const stockText = stock.status === 'ok'
                          ? `${stock.available} in stock`
                          : stock.status === 'low'
                            ? `Only ${stock.available} in stock`
                            : 'Not in inventory';
                        const stockColor = stock.status === 'ok' ? 'text-green-700' : stock.status === 'low' ? 'text-amber-700' : 'text-red-700';
                        return (
                          <div key={i} className="border border-gray-200 rounded-lg p-3 mb-2 bg-gray-50">
                            <div className="font-semibold text-sm text-gray-900">{h.productName}</div>
                            <div className="text-xs text-gray-500 mb-1">{h.activeIngredient}</div>
                            <div className="flex justify-between items-center text-xs">
                              <span className="text-gray-600">Rate: {h.rateQty} {h.rateUnit} | Need: {h.totalQty} {h.totalQtyUnit}</span>
                              <span className={`font-semibold ${stockColor}`}>{stockIcon} {stockText}</span>
                            </div>
                          </div>
                        );
                      })
                  }
                </div>

                {/* Application method + volume */}
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <p className="text-sm font-bold text-gray-700 mb-1">Application Method</p>
                    <p className="text-sm text-gray-700 bg-gray-50 rounded px-2 py-1">{p.applicationMethod || '—'}</p>
                  </div>
                  <div>
                    <p className="text-sm font-bold text-gray-700 mb-1">Total Volume</p>
                    <p className="text-sm text-gray-700 bg-gray-50 rounded px-2 py-1">{p.totalVolumeQty ? `${p.totalVolumeQty} L` : '—'}</p>
                  </div>
                </div>

                <p className="text-xs text-gray-500">
                  NEW paddocks and weeds will be added to your saved lists. Batch numbers must be selected after loading.
                </p>

                <div className="flex gap-3">
                  <button
                    onClick={() => applySprayRec(p)}
                    className="flex-1 bg-green-600 text-white p-3 rounded-lg font-semibold hover:bg-green-700"
                  >
                    ✓ Load into Form
                  </button>
                  <button
                    onClick={() => setSprayRecModal({ visible: false, parsed: null, loading: false, error: null })}
                    className="flex-1 bg-gray-500 text-white p-3 rounded-lg font-semibold hover:bg-gray-600"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            );
          })()}

          {(sprayRecModal.error || (!sprayRecModal.loading && !sprayRecModal.parsed)) && !sprayRecModal.loading && (
            <button
              onClick={() => setSprayRecModal({ visible: false, parsed: null, loading: false, error: null })}
              className="w-full bg-gray-500 text-white p-3 rounded-lg font-semibold hover:bg-gray-600 mt-3"
            >
              Close
            </button>
          )}
        </div>
      </div>
    ) : null;



    return (
      <div className="min-h-screen bg-blue-50 p-4">
        {ConfirmModal}
	{SprayRecModal}
        <div className="max-w-2xl mx-auto">
          <div className="bg-white rounded-lg shadow-lg p-6 mb-6">
            <h2 className="text-2xl font-bold text-center text-blue-800">Land Pesticides</h2>
            {editingEntry ? (
              <p className="text-center text-blue-600 font-semibold">
                Editing Record {records.find(r => r.id === editingEntry.id)?.displayId || ''}
              </p>
            ) : (
              <p className="text-center text-gray-600">Mandatory Record Keeping</p>
            )}
          </div>

          <div className="bg-white rounded-lg shadow-lg p-6 space-y-4">

  {/* Spray Rec Import Button */}
            {!editingEntry && (
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                <p className="text-sm font-semibold text-blue-800 mb-2">📄 Import from Spray Rec</p>
                <p className="text-xs text-gray-600 mb-2">Upload a spray rec PDF to pre-fill this form</p>
                <label className="block">
                  <input
                    type="file"
                    accept="application/pdf"
                    className="hidden"
                    onChange={(e) => {
                      const file = e.target.files[0];
                      if (file) {
                        e.target.value = '';
                        parseSprayRec(file);
                      }
                    }}
                  />
                  <span className="inline-block w-full text-center bg-blue-600 text-white p-3 rounded-lg font-semibold cursor-pointer hover:bg-blue-700">
                    📂 Choose PDF
                  </span>
                </label>
              </div>
            )}


            {/* Row 1: Date */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">Date *</label>
              <input
                type="date"
                value={landForm.date}
                onChange={(e) => setLandForm({ ...landForm, date: e.target.value })}
                className="w-full p-3 border-2 border-gray-300 rounded-lg"
              />
            </div>

            {/* Wind Speed + Direction */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Wind Speed (km/h) *</label>
                <select
                  value={landForm.windSpeed}
                  onChange={(e) => setLandForm({ ...landForm, windSpeed: e.target.value })}
                  className="w-full p-3 border-2 border-gray-300 rounded-lg"
                >
                  <option value="">Select...</option>
                  <option value="0-5">0-5 (Calm)</option>
                  <option value="6-10">6-10 (Light)</option>
                  <option value="11-15">11-15 (Moderate)</option>
                  <option value="16-20">16-20 (Fresh)</option>
                  <option value="21+">21+ (Strong)</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Wind Direction *</label>
                <select
                  value={landForm.windDirection}
                  onChange={(e) => setLandForm({ ...landForm, windDirection: e.target.value })}
                  className="w-full p-3 border-2 border-gray-300 rounded-lg"
                >
                  <option value="">Select...</option>
                  <option>N</option><option>NE</option><option>E</option><option>SE</option>
                  <option>S</option><option>SW</option><option>W</option><option>NW</option>
                </select>
              </div>
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">Applicator Name *</label>
              <input
                type="text"
                value={landForm.applicator}
                onChange={(e) => setLandForm({ ...landForm, applicator: e.target.value })}
                className="w-full p-3 border-2 border-gray-300 rounded-lg"
              />
            </div>

            {/* Farm Name */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">Farm Name *</label>
              <div className="relative">
                <input
                  type="text"
                  value={landForm.farmName || ''}
                  onChange={(e) => { setLandForm({ ...landForm, farmName: e.target.value }); setShowFarmDropdown(true); }}
                  onFocus={() => setShowFarmDropdown(true)}
                  onBlur={() => setTimeout(() => setShowFarmDropdown(false), 150)}
                  placeholder="Select or type a farm name..."
                  className="w-full p-3 pr-8 border-2 border-gray-300 rounded-lg"
                />
                <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 text-xs">&#9662;</span>
                {showFarmDropdown && farmList.filter(f =>
                  f.toLowerCase().includes((farmList.includes(landForm.farmName) ? '' : (landForm.farmName || '')).toLowerCase())
                ).length > 0 && (
                  <div className="absolute z-20 w-full bg-white border-2 border-gray-300 rounded-lg shadow-lg mt-1 max-h-40 overflow-y-auto">
                    {farmList
                      .filter(f => f.toLowerCase().includes((farmList.includes(landForm.farmName) ? '' : (landForm.farmName || '')).toLowerCase()))
                      .map((f, i) => (
                        <button
                          key={i}
                          type="button"
                          onMouseDown={() => { setLandForm({ ...landForm, farmName: f }); setShowFarmDropdown(false); }}
                          className="w-full text-left px-3 py-2 text-sm hover:bg-blue-50 border-b border-gray-100 last:border-0"
                        >
                          {f}
                        </button>
                      ))}
                  </div>
                )}
              </div>
            </div>

            {/* Row 2: Paddock Tag Picker */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">Paddock(s) *</label>

              {/* Selected paddock tags */}
              {landForm.paddocks.length > 0 && (
                <div className="flex flex-wrap gap-2 mb-2">
                  {landForm.paddocks.map((p, i) => (
                    <span key={i} className="inline-flex items-center gap-1 bg-blue-100 text-blue-800 text-sm font-medium px-2 py-1 rounded-full">
                      {p.name} ({p.areaHectares} ha)
                      <button
                        onClick={() => {
                          const updated = landForm.paddocks.filter((_, idx) => idx !== i);
                          const totalHa = updated.reduce((sum, x) => sum + parseFloat(x.areaHectares || 0), 0);
                          setLandForm({ ...landForm, paddocks: updated, areaHectares: updated.length ? totalHa.toFixed(2) : '' });
                        }}
                        className="text-blue-600 hover:text-blue-900 font-bold leading-none"
                      >&times;</button>
                    </span>
                  ))}
                </div>
              )}

              {/* Paddock input row */}
              <div className="flex gap-2 mb-1">
                <div className="relative flex-1">
                  <input
                    type="text"
                    value={paddockInput}
                    onChange={(e) => { setPaddockInput(e.target.value); setShowPaddockDropdown(true); }}
                    onFocus={() => setShowPaddockDropdown(true)}
                    onBlur={() => setTimeout(() => setShowPaddockDropdown(false), 150)}
                    placeholder="Paddock name..."
                    className="w-full p-3 pr-8 border-2 border-gray-300 rounded-lg text-sm"
                  />
                  <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 text-xs">&#9662;</span>
                  {showPaddockDropdown && savedPaddocks.filter(p =>
                    p.name.toLowerCase().includes(paddockInput.toLowerCase()) &&
                    !landForm.paddocks.some(sel => sel.name === p.name) &&
                    (!landForm.farmName || p.farm === landForm.farmName)
                  ).length > 0 && (
                    <div className="absolute z-20 w-full bg-white border-2 border-gray-300 rounded-lg shadow-lg mt-1 max-h-40 overflow-y-auto">
                      {savedPaddocks
                        .filter(p => p.name.toLowerCase().includes(paddockInput.toLowerCase()) && !landForm.paddocks.some(sel => sel.name === p.name) && (!landForm.farmName || p.farm === landForm.farmName))
                        .map((p, i) => (
                          <button
                            key={i}
                            onMouseDown={() => {
                              const updated = [...landForm.paddocks, { name: p.name, areaHectares: p.areaHectares }];
                              const totalHa = updated.reduce((sum, x) => sum + parseFloat(x.areaHectares || 0), 0);
                              setLandForm({ ...landForm, paddocks: updated, areaHectares: totalHa.toFixed(2) });
                              setPaddockInput('');
                              setPaddockAreaInput('');
                              setShowPaddockDropdown(false);
                            }}
                            className="w-full text-left px-3 py-2 text-sm hover:bg-blue-50 border-b border-gray-100 last:border-0"
                          >
                            {p.name} <span className="text-gray-500">({p.areaHectares} ha)</span>
                          </button>
                        ))}
                    </div>
                  )}
                </div>
                <input
                  type="text"
                  inputMode="decimal"
                  value={paddockAreaInput}
                  onChange={(e) => setPaddockAreaInput(e.target.value)}
                  placeholder="ha"
                  className="w-20 p-3 border-2 border-gray-300 rounded-lg text-sm"
                />
                <button
                  onClick={() => {
                    const name = paddockInput.trim();
                    const area = paddockAreaInput.trim();
                    if (!name) { alert('Enter a paddock name.'); return; }
                    if (!area) { alert('Enter the paddock area (ha).'); return; }
                    // Add to form
                    const updated = [...landForm.paddocks, { name, areaHectares: area }];
                    const totalHa = updated.reduce((sum, x) => sum + parseFloat(x.areaHectares || 0), 0);
                    setLandForm({ ...landForm, paddocks: updated, areaHectares: totalHa.toFixed(2) });
                    // Save to savedPaddocks if new — tag with the currently selected farm
                    if (!savedPaddocks.some(p => p.name.toLowerCase() === name.toLowerCase())) {
                      setSavedPaddocks(prev => [...prev, { name, areaHectares: area, farm: landForm.farmName || '' }].sort((a, b) => a.name.localeCompare(b.name)));
                    }
                    setPaddockInput('');
                    setPaddockAreaInput('');
                  }}
                  className="px-3 py-2 bg-blue-600 text-white rounded-lg text-sm font-semibold hover:bg-blue-700 whitespace-nowrap"
                >
                  + Add
                </button>
              </div>
              <p className="text-xs text-gray-500">Select from saved paddocks or type a new name + area to add</p>
            </div>

            {/* Total area display */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">Total Area (ha) *</label>
              <input
                type="text"
                inputMode="decimal"
                value={landForm.areaHectares}
                onChange={(e) => setLandForm({ ...landForm, areaHectares: e.target.value })}
                placeholder="Auto-calculated from paddocks, or enter manually"
                className="w-full p-3 border-2 border-gray-300 rounded-lg"
              />
              {landForm.paddocks.length > 1 && (
                <p className="text-xs text-gray-500 mt-1">Sum of selected paddocks — edit if needed</p>
              )}
            </div>

            {/* Row 3: Crop/Land Use + Pest/Weed Tag Picker */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">Crop/Land Use *</label>
              <select
                value={landForm.cropLandUse}
                onChange={(e) => setLandForm({ ...landForm, cropLandUse: e.target.value })}
                className="w-full p-3 border-2 border-gray-300 rounded-lg"
              >
                <option value="">Select...</option>
                {commonCrops.map(crop => (
                  <option key={crop} value={crop}>{crop}</option>
                ))}
              </select>
              {landForm.cropLandUse === 'Other' && (
                <input
                  type="text"
                  value={landForm.cropLandUseOther}
                  onChange={(e) => setLandForm({ ...landForm, cropLandUseOther: e.target.value })}
                  placeholder="Specify crop/land use"
                  className="w-full p-3 border-2 border-gray-300 rounded-lg mt-2"
                />
              )}
            </div>

            {/* Pest/Weed Tag Picker */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">Pest/Weed Target(s) *</label>

              {/* Selected weed tags */}
              {landForm.pestTargets.length > 0 && (
                <div className="flex flex-wrap gap-2 mb-2">
                  {landForm.pestTargets.map((w, i) => (
                    <span key={i} className="inline-flex items-center gap-1 bg-green-100 text-green-800 text-sm font-medium px-2 py-1 rounded-full">
                      {w}
                      <button
                        onClick={() => {
                          const updated = landForm.pestTargets.filter((_, idx) => idx !== i);
                          setLandForm({ ...landForm, pestTargets: updated });
                        }}
                        className="text-green-600 hover:text-green-900 font-bold leading-none"
                      >&times;</button>
                    </span>
                  ))}
                </div>
              )}

              {/* Weed input */}
              <div className="flex gap-2">
                <div className="relative flex-1">
                  <input
                    type="text"
                    value={weedInput}
                    onChange={(e) => { setWeedInput(e.target.value); setShowWeedDropdown(true); }}
                    onFocus={() => setShowWeedDropdown(true)}
                    onBlur={() => setTimeout(() => setShowWeedDropdown(false), 150)}
                    placeholder="Search or add weed/pest..."
                    className="w-full p-3 border-2 border-gray-300 rounded-lg text-sm"
                  />
                  {showWeedDropdown && savedWeeds.filter(w =>
                    w.toLowerCase().includes(weedInput.toLowerCase()) &&
                    !landForm.pestTargets.includes(w)
                  ).length > 0 && (
                    <div className="absolute z-20 w-full bg-white border-2 border-gray-300 rounded-lg shadow-lg mt-1 max-h-48 overflow-y-auto">
                      {savedWeeds
                        .filter(w => w.toLowerCase().includes(weedInput.toLowerCase()) && !landForm.pestTargets.includes(w))
                        .map((w, i) => (
                          <button
                            key={i}
                            onMouseDown={() => {
                              setLandForm(prev => ({ ...prev, pestTargets: [...prev.pestTargets, w] }));
                              setWeedInput('');
                              setShowWeedDropdown(false);
                            }}
                            className="w-full text-left px-3 py-2 text-sm hover:bg-green-50 border-b border-gray-100 last:border-0"
                          >
                            {w}
                          </button>
                        ))}
                    </div>
                  )}
                </div>
                <button
                  onClick={() => {
                    const w = weedInput.trim();
                    if (!w || landForm.pestTargets.includes(w)) return;
                    setLandForm(prev => ({ ...prev, pestTargets: [...prev.pestTargets, w] }));
                    if (!savedWeeds.some(s => s.toLowerCase() === w.toLowerCase())) {
                      setSavedWeeds(prev => [...prev, w].sort((a, b) => a.localeCompare(b)));
                    }
                    setWeedInput('');
                  }}
                  className="px-3 py-2 bg-green-600 text-white rounded-lg text-sm font-semibold hover:bg-green-700 whitespace-nowrap"
                >
                  + Add
                </button>
              </div>
              <p className="text-xs text-gray-500 mt-1">Select from list or type a new weed/pest to add</p>
            </div>

            {/* Row 4: Application Method */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">Application Method *</label>
              <select
                value={landForm.applicationMethod}
                onChange={(e) => setLandForm({ ...landForm, applicationMethod: e.target.value })}
                className="w-full p-3 border-2 border-gray-300 rounded-lg"
              >
                <option>Spot Spray</option>
                <option>Boom Spray</option>
                <option>Aerial Application</option>
                <option>Wiper Application</option>
                <option>Hand Application</option>
              </select>
            </div>

            {/* Row 5: Total Diluted Volume */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">Total Diluted Volume *</label>
              <div className="grid grid-cols-3 gap-3">
                <div className="col-span-2">
                  <input
                    type="text"
                    inputMode="decimal"
                    value={landForm.totalDilutedVolumeQty}
                    onChange={(e) => recalcAllChemicals(e.target.value, undefined)}
                    placeholder="e.g., 100"
                    className="w-full p-3 border-2 border-gray-300 rounded-lg"
                  />
                </div>
                <div>
                  <select
                    value={landForm.totalDilutedVolumeUnit}
                    onChange={(e) => recalcAllChemicals(undefined, e.target.value)}
                    className="w-full p-3 border-2 border-gray-300 rounded-lg"
                  >
                    <option value="L">Litres</option>
                    <option value="gal">Gallons</option>
                  </select>
                </div>
              </div>
              <p className="text-xs text-gray-500 mt-1">
                Total volume of mixed spray in the drum
                {landForm.totalDilutedVolumeUnit === 'gal' && landForm.totalDilutedVolumeQty
                  ? ` (${(parseFloat(landForm.totalDilutedVolumeQty) * IMPERIAL_GALLON_TO_L).toFixed(1)} L)`
                  : ''}
              </p>
            </div>

            {/* Chemical Blocks */}
            {landForm.chemicals.map((chem, index) => {
              const availableBatches = chem.productName ? getAvailableBatches(chem.productName) : [];
              const drumDisplay = landForm.totalDilutedVolumeQty
                ? `${landForm.totalDilutedVolumeQty} ${landForm.totalDilutedVolumeUnit === 'gal' ? 'gal' : 'L'}`
                : '?';
              return (
                <div key={index} className="border-2 border-green-200 rounded-lg p-4 bg-green-50 space-y-3">
                  <div className="flex justify-between items-center">
                    <h4 className="font-bold text-green-800">
                      {index === 0 ? 'Chemical 1' : `Chemical ${index + 1}`}
                    </h4>
                    {index > 0 && (
                      <button
                        onClick={() => removeChemicalEntry(index)}
                        className="text-red-500 text-sm font-medium hover:text-red-700"
                      >
                        Remove
                      </button>
                    )}
                  </div>

                  {/* Chemical/Product dropdown */}
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-1">Chemical/Product *</label>
                    <select
                      value={chem.selectedChemical}
                      onChange={(e) => handleLandChemicalChange(index, e.target.value)}
                      className="w-full p-3 border-2 border-gray-300 rounded-lg bg-white"
                    >
                      <option value="">Select chemical...</option>
                      {landChemOptions.map(c => (
                        <option key={c.name} value={c.name}>{c.name}</option>
                      ))}
                      <option value="Other">Other (Manual Entry)</option>
                    </select>
                  </div>

                  {chem.selectedChemical === 'Other' && (
                    <input
                      type="text"
                      value={chem.productName}
                      onChange={(e) => updateChemicalField(index, 'productName', e.target.value)}
                      placeholder="Enter product name"
                      className="w-full p-3 border-2 border-gray-300 rounded-lg"
                    />
                  )}

                  {/* Active Ingredient */}
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-1">Active Ingredient(s)</label>
                    <input
                      type="text"
                      value={chem.activeIngredient}
                      onChange={(e) => updateChemicalField(index, 'activeIngredient', e.target.value)}
                      placeholder="Auto-filled or enter manually"
                      className="w-full p-3 border-2 border-gray-300 rounded-lg"
                      readOnly={chem.selectedChemical && chem.selectedChemical !== 'Other'}
                    />
                  </div>

                  {/* Withholding Period — read-only label info + editable numeric days */}
                  {chem.productName && (
                    <div className="space-y-2">
                      {chem.withholdingPeriod && (
                        <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
                          <p className="text-xs font-semibold text-amber-800 mb-1">Label Withholding Period</p>
                          <p className="text-sm text-amber-900">{chem.withholdingPeriod}</p>
                        </div>
                      )}
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-1">WHP (days) * <span className="font-normal text-gray-500 text-xs">Enter 0 if nil</span></label>
                        <div className="flex items-center gap-2">
                          <input type="number" inputMode="numeric" min="0"
                            style={{MozAppearance:"textfield"}} onWheel={(e)=>e.target.blur()}
                            value={chem.withholdingDays}
                            onChange={(e) => updateChemicalField(index, 'withholdingDays', e.target.value)}
                            placeholder="e.g. 14"
                            className={`flex-1 p-3 border-2 rounded-lg ${chem.withholdingDays === '' ? 'border-amber-400' : 'border-gray-300'}`} />
                          <span className="text-gray-600 font-medium whitespace-nowrap">days</span>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Batch Number(s) — multi-batch support */}
                  {chem.productName && (
                    <div className="space-y-2">
                      <div className="flex justify-between items-center">
                        <label className="block text-sm font-semibold text-gray-700">Batch Number(s) (optional)</label>
                        {availableBatches.length > 1 && (
                          <button
                            onClick={() => {
                              const current = chem.selectedBatches && chem.selectedBatches.length > 0 ? chem.selectedBatches : [{ batchId: '', qty: '' }];
                              updateChemicalField(index, 'selectedBatches', [...current, { batchId: '', qty: '' }]);
                            }}
                            className="text-xs text-green-700 font-semibold hover:text-green-900"
                          >
                            + Add batch
                          </button>
                        )}
                      </div>
                      {(chem.selectedBatches || [{ batchId: '', qty: '' }]).map((sb, bi) => {
                        // eslint-disable-next-line no-unused-vars
                        const selectedB = availableBatches.find(b => b.id === sb.batchId);
                        return (
                          <div key={bi} className="flex items-center gap-2 w-full min-w-0">
                            <select
                              value={sb.batchId}
                              onChange={(e) => {
                                const newBatchId = e.target.value;
                                const b = availableBatches.find(x => String(x.id) === newBatchId);
                                const updatedBatches = [...(chem.selectedBatches || [{ batchId: '', qty: '' }])];
                                updatedBatches[bi] = { ...updatedBatches[bi], batchId: newBatchId };
                                // Single atomic state update — two separate calls cause stale closure overwrites
                                const updatedChemicals = [...landForm.chemicals];
                                updatedChemicals[index] = {
                                  ...updatedChemicals[index],
                                  selectedBatches: updatedBatches,
                                  batchNumber: b ? b.batchNumber : updatedChemicals[index].batchNumber
                                };
                                setLandForm({ ...landForm, chemicals: updatedChemicals });
                              }}
                              className="flex-1 min-w-0 w-full p-2 border-2 border-gray-300 rounded-lg bg-white text-sm"
                              style={{maxWidth: '100%', overflow: 'hidden', textOverflow: 'ellipsis'}}
                            >
                              <option value="">No batch tracking</option>
                              {availableBatches.map(batch => (
                                <option key={batch.id} value={String(batch.id)}>
                                  {batch.batchNumber} — {batch.quantityRemainingNumber}{batch.quantityRemainingUnit}
                                </option>
                              ))}
                            </select>
                            {(chem.selectedBatches || []).length > 1 && (
                              <button
                                onClick={() => {
                                  const updated = (chem.selectedBatches || []).filter((_, i) => i !== bi);
                                  updateChemicalField(index, 'selectedBatches', updated);
                                }}
                                className="text-red-500 text-lg font-bold px-1 flex-shrink-0"
                              >×</button>
                            )}
                          </div>
                        );
                      })}
                      {availableBatches.length === 0 && (
                        <p className="text-xs text-orange-600">No batches in inventory — record a purchase first, or proceed without batch tracking.</p>
                      )}

                      {/* Real-time stock warning */}
                      {(() => {
                        if (!chem.calculatedQty) return null;
                        const unit = chem.calculatedQtyUnit || chem.rateUnit || '';
                        // Convert any qty+unit to mL or g base for consistent comparison
                        const toBase = (qty, u) => {
                          const n = parseFloat(qty) || 0;
                          if (u === 'L' || u === 'kg') return n * 1000;
                          return n;
                        };
                        const totalAvailBase = availableBatches.reduce((sum, b) => {
                          return sum + toBase(b.quantityRemainingNumber, b.quantityRemainingUnit);
                        }, 0);
                        const neededBase = toBase(chem.calculatedQty, unit);
                        if (totalAvailBase === 0) return null;
                        if (neededBase > totalAvailBase) {
                          // Display shortfall back in original unit (L or kg)
                          const isLarge = (unit === 'L' || unit === 'kg');
                          const shortfallBase = neededBase - totalAvailBase;
                          const shortfallDisplay = isLarge ? (shortfallBase / 1000).toFixed(2) : shortfallBase.toFixed(2);
                          const shortfallUnit = (unit === 'L' || unit === 'mL') ? (isLarge ? 'L' : 'mL') : (isLarge ? 'kg' : 'g');
                          const availDisplay = isLarge ? (totalAvailBase / 1000).toFixed(2) : totalAvailBase.toFixed(2);
                          const availUnit = availableBatches[0]?.quantityRemainingUnit || unit;
                          return (
                            <div className="bg-red-50 border-2 border-red-400 rounded-lg p-3 text-sm">
                              <span className="font-bold text-red-700">{'\u26a0\ufe0f'} Insufficient stock!</span>
                              <span className="text-red-700"> Need {chem.calculatedQty} {unit} but only {availDisplay} {availUnit} on hand across all batches.</span>
                              <div className="text-red-600 mt-1">Shortfall: approx {shortfallDisplay} {shortfallUnit}. Record another purchase before spraying.</div>
                            </div>
                          );
                        }
                        return (
                          <div className="bg-green-50 border border-green-300 rounded-lg p-2 text-xs text-green-700">
                            {'\u2713'} Sufficient stock available across all batches
                          </div>
                        );
                      })()}
                    </div>
                  )}

                  {/* Application Rate Mode Toggle */}
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-1">Application Rate Method</label>
                    <div className="grid grid-cols-2 gap-2">
                      <button
                        onClick={() => updateChemicalField(index, 'rateMode', 'dilution')}
                        className={`p-2 rounded-lg text-sm font-semibold border-2 ${
                          chem.rateMode === 'dilution'
                            ? 'bg-green-600 text-white border-green-600'
                            : 'bg-white text-gray-600 border-gray-300'
                        }`}
                      >
                        Dilution Rate
                      </button>
                      <button
                        onClick={() => updateChemicalField(index, 'rateMode', 'area')}
                        className={`p-2 rounded-lg text-sm font-semibold border-2 ${
                          chem.rateMode === 'area'
                            ? 'bg-blue-600 text-white border-blue-600'
                            : 'bg-white text-gray-600 border-gray-300'
                        }`}
                      >
                        Area Rate (per ha)
                      </button>
                    </div>
                  </div>

                  {/* Dilution Rate fields */}
                  {chem.rateMode === 'dilution' && (
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-1">
                        Dilution Rate * (per litre water)
                      </label>
                      <div className="flex items-center gap-2 flex-wrap">
                        <input
                          type="text"
                          inputMode="decimal"
                          value={chem.rateQty}
                          onChange={(e) => updateChemicalField(index, 'rateQty', e.target.value)}
                          placeholder="Amount"
                          className="w-24 p-3 border-2 border-gray-300 rounded-lg"
                        />
                        <select
                          value={chem.rateUnit}
                          onChange={(e) => updateChemicalField(index, 'rateUnit', e.target.value)}
                          className="p-3 border-2 border-gray-300 rounded-lg"
                        >
                          <option value="mL">mL</option>
                          <option value="L">L</option>
                          <option value="g">g</option>
                          <option value="kg">kg</option>
                        </select>
                        <span className="text-gray-600 font-medium text-sm">per</span>
                        <input
                          type="text"
                          inputMode="decimal"
                          value={chem.rateWaterQty}
                          onChange={(e) => updateChemicalField(index, 'rateWaterQty', e.target.value)}
                          placeholder="100"
                          className="w-20 p-3 border-2 border-gray-300 rounded-lg"
                        />
                        <span className="text-gray-600 font-medium text-sm">L water</span>
                      </div>
                      <p className="text-xs text-gray-500 mt-1">e.g., 50 mL per 100 L — confirm with product label</p>
                    </div>
                  )}

                  {/* Area Rate fields */}
                  {chem.rateMode === 'area' && (
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-1">
                        Area Rate *
                      </label>
                      <div className="flex items-center gap-2">
                        <input
                          type="text"
                          inputMode="decimal"
                          value={chem.areaRateQty}
                          onChange={(e) => updateChemicalField(index, 'areaRateQty', e.target.value)}
                          placeholder="Amount"
                          className="w-24 p-3 border-2 border-gray-300 rounded-lg"
                        />
                        <select
                          value={chem.areaRateUnit}
                          onChange={(e) => updateChemicalField(index, 'areaRateUnit', e.target.value)}
                          className="p-3 border-2 border-gray-300 rounded-lg"
                        >
                          <option value="L">L</option>
                          <option value="mL">mL</option>
                          <option value="kg">kg</option>
                          <option value="g">g</option>
                        </select>
                        <span className="text-gray-600 font-medium text-sm">per hectare</span>
                      </div>
                      <p className="text-xs text-gray-500 mt-1">
                        {landForm.areaHectares
                          ? `Paddock area: ${landForm.areaHectares} ha`
                          : 'Enter paddock area above to calculate total required'}
                      </p>
                    </div>
                  )}

                  {/* Calculated Quantity Required */}
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-1">
                      {chem.rateMode === 'dilution' ? 'Chemical Required for Drum' : 'Total Chemical Required'}
                    </label>
                    <input
                      type="text"
                      value={chem.calculatedQty ? `${chem.calculatedQty} ${chem.calculatedQtyUnit || chem.rateUnit || chem.areaRateUnit || ''}` : ''}
                      readOnly
                      placeholder="Auto-calculated"
                      className="w-full p-3 border-2 border-gray-200 rounded-lg bg-yellow-50 font-semibold text-gray-800"
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      {chem.rateMode === 'dilution'
                        ? (chem.rateQty && chem.rateWaterQty && landForm.totalDilutedVolumeQty
                            ? `(${chem.rateQty} ${chem.rateUnit} ÷ ${chem.rateWaterQty} L) × ${drumDisplay}`
                            : 'Enter dilution rate and total diluted volume above to calculate')
                        : (chem.areaRateQty && landForm.areaHectares
                            ? `${chem.areaRateQty} ${chem.areaRateUnit}/ha × ${landForm.areaHectares} ha`
                            : 'Enter area rate and paddock area above to calculate')
                      }
                    </p>
                  </div>
                </div>
              );
            })}

            {/* Withholding Period Summary — show longest across all chemicals */}
            {landForm.chemicals.some(c => c.productName) && (
              <div className="bg-amber-50 border-2 border-amber-400 rounded-lg p-4">
                <div className="flex items-center gap-2 mb-2">
                  <span className="text-lg">{'\u23f1\ufe0f'}</span>
                  <span className="font-bold text-amber-900 text-base">Withholding Periods — Do Not Harvest/Graze Until:</span>
                </div>
                <div className="space-y-1">
                  {(() => {
                    const activeChem = landForm.chemicals.filter(c => c.productName);
                    const maxDays = Math.max(...activeChem.map(c => parseInt(c.withholdingDays, 10) || 0));
                    return activeChem.map((c, i) => {
                      const d = parseInt(c.withholdingDays, 10);
                      const isLongest = activeChem.length > 1 && !isNaN(d) && d === maxDays && d > 0;
                      const calcClear = () => {
                        if (c.withholdingDays === '' || isNaN(d)) return null;
                        if (d === 0) return 'Nil';
                        if (!landForm.date) return null;
                        const base = new Date(landForm.date);
                        if (isNaN(base.getTime())) return null;
                        base.setDate(base.getDate() + d);
                        return base.toLocaleDateString('en-AU');
                      };
                      const clearDate = calcClear();
                      return (
                        <div key={i} className={`text-sm flex justify-between ${isLongest ? 'font-bold text-amber-900' : 'text-amber-800'}`}>
                          <span>{c.productName}:</span>
                          <span className={clearDate ? '' : 'italic text-amber-600'}>
                            {clearDate || 'Enter WHP days above'}
                          </span>
                        </div>
                      );
                    });
                  })()}
                </div>
                {landForm.chemicals.filter(c => c.productName).length > 1 && (
                  <div className="mt-2 pt-2 border-t border-amber-300 text-xs font-bold text-amber-900">
                    {'\u26a0\ufe0f'} Observe the longest period above before re-entering, grazing or harvesting this paddock.
                  </div>
                )}
              </div>
            )}

            {/* Add Another Chemical button */}
            {landForm.chemicals.length < 6 && (
              <button
                onClick={addChemicalEntry}
                className="w-full bg-green-100 text-green-800 border-2 border-green-400 p-3 rounded-lg font-semibold hover:bg-green-200"
              >
                + Add Another Chemical ({landForm.chemicals.length}/6)
              </button>
            )}





            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">Notes</label>
              <textarea
                value={landForm.notes}
                onChange={(e) => setLandForm({ ...landForm, notes: e.target.value })}
                rows="3"
                className="w-full p-3 border-2 border-gray-300 rounded-lg"
                placeholder="Additional notes..."
              />
            </div>

            <div className="flex space-x-3 pt-4">
              <button
                onClick={saveLandRecord}
                className="flex-1 bg-green-600 text-white p-4 rounded-lg text-lg font-semibold hover:bg-green-700"
              >
                {editingEntry ? '\u2713 Update Record' : '\u2713 Save Record'}
              </button>
              <button
                onClick={() => {
                  setEditingEntry(null);
                  setLandForm({
                    date: new Date().toISOString().split('T')[0],
                    paddocks: [],
                    paddockName: '',
                    areaHectares: '',
                    pestTargets: [],
                    pestTarget: '',
                    pestTargetOther: '',
                    cropLandUse: '',
                    cropLandUseOther: '',
                    applicationMethod: 'Spot Spray',
                    totalDilutedVolumeQty: '',
                    totalDilutedVolumeUnit: 'L',
                    chemicals: [emptyChemicalEntry()],
                    windSpeed: '',
                    windDirection: '',
                    applicator: farmerName,
                    photos: [],
                    notes: ''
                  });
                  setPaddockInput('');
                  setPaddockAreaInput('');
                  setWeedInput('');
                  setSection('home');
                }}
                className="flex-1 bg-gray-500 text-white p-4 rounded-lg text-lg font-semibold hover:bg-gray-600"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Animal Medications Form
  if (section === 'animal') {
    const animalMedOptions = allChemicals
      .filter(c => ['Parasiticide', 'Vaccine', 'Supplement', 'Hormone', 'Antibiotic', 'Anthelmintic'].includes(c.category))
      .sort((a, b) => a.name.localeCompare(b.name));

    return (
      <div className="min-h-screen bg-orange-50 p-4">
        {ConfirmModal}
        <div className="max-w-2xl mx-auto">
          <div className="bg-white rounded-lg shadow-lg p-6 mb-6">
            <h2 className="text-2xl font-bold text-center text-orange-800">Animal Medications</h2>
            {editingEntry ? (
              <p className="text-center text-orange-600 font-semibold">
                Editing Record {records.find(r => r.id === editingEntry.id)?.displayId || ''}
              </p>
            ) : (
              <p className="text-center text-gray-600">Mandatory Record Keeping</p>
            )}
          </div>

          <div className="bg-white rounded-lg shadow-lg p-6 space-y-4">

            {/* === SHARED FIELDS === */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">Date *</label>
              <input type="date" value={animalForm.date}
                onChange={(e) => setAnimalForm({ ...animalForm, date: e.target.value })}
                className="w-full p-3 border-2 border-gray-300 rounded-lg" />
            </div>

            {/* Farm Name */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">Farm Name *</label>
              <div className="relative">
                <input
                  type="text"
                  value={animalForm.farmName || ''}
                  onChange={(e) => { setAnimalForm({ ...animalForm, farmName: e.target.value }); setShowFarmDropdown(true); }}
                  onFocus={() => setShowFarmDropdown(true)}
                  onBlur={() => setTimeout(() => setShowFarmDropdown(false), 150)}
                  placeholder="Select or type a farm name..."
                  className="w-full p-3 pr-8 border-2 border-gray-300 rounded-lg"
                />
                <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 text-xs">&#9662;</span>
                {showFarmDropdown && farmList.filter(f =>
                  f.toLowerCase().includes((farmList.includes(animalForm.farmName) ? '' : (animalForm.farmName || '')).toLowerCase())
                ).length > 0 && (
                  <div className="absolute z-20 w-full bg-white border-2 border-gray-300 rounded-lg shadow-lg mt-1 max-h-40 overflow-y-auto">
                    {farmList
                      .filter(f => f.toLowerCase().includes((farmList.includes(animalForm.farmName) ? '' : (animalForm.farmName || '')).toLowerCase()))
                      .map((f, i) => (
                        <button
                          key={i}
                          type="button"
                          onMouseDown={() => { setAnimalForm({ ...animalForm, farmName: f }); setShowFarmDropdown(false); }}
                          className="w-full text-left px-3 py-2 text-sm hover:bg-blue-50 border-b border-gray-100 last:border-0"
                        >
                          {f}
                        </button>
                      ))}
                  </div>
                )}
              </div>
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">Species *</label>
              <select value={animalForm.species}
                onChange={(e) => setAnimalForm({ ...animalForm, species: e.target.value })}
                className="w-full p-3 border-2 border-gray-300 rounded-lg">
                <option>Cattle</option>
                <option>Sheep</option>
                <option>Goats</option>
                <option>Pigs</option>
                <option>Horses</option>
                <option>Poultry</option>
              </select>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Animal Count *</label>
                <input type="text" value={animalForm.animalCount || ''}
                  onChange={(e) => setAnimalForm({ ...animalForm, animalCount: e.target.value })}
                  onBlur={calculateDosage}
                  placeholder="e.g., 50"
                  className="w-full p-3 border-2 border-gray-300 rounded-lg" />
              </div>
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Avg Weight (kg)</label>
                <input type="text" value={animalForm.averageWeight || ''}
                  onChange={(e) => setAnimalForm({ ...animalForm, averageWeight: e.target.value })}
                  onBlur={calculateDosage}
                  placeholder="e.g., 500"
                  className="w-full p-3 border-2 border-gray-300 rounded-lg" />
              </div>
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">Animal IDs / Mob Name</label>
              <input type="text" value={animalForm.animalIDs}
                onChange={(e) => setAnimalForm({ ...animalForm, animalIDs: e.target.value })}
                placeholder="e.g., Mob 7, NLIS tags 123-456"
                className="w-full p-3 border-2 border-gray-300 rounded-lg" />
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">Applicator Name</label>
              <input type="text" value={animalForm.applicator}
                onChange={(e) => setAnimalForm({ ...animalForm, applicator: e.target.value })}
                className="w-full p-3 border-2 border-gray-300 rounded-lg" />
            </div>

            {/* === MEDICATION CARDS === */}
            <div className="border-t-2 border-orange-200 pt-4">
              <h3 className="text-lg font-bold text-orange-800 mb-3">
                {'\ud83d\udc89'} Medications Applied ({animalForm.medications.length})
              </h3>
            </div>

            {animalForm.medications.map((med, index) => {
              const availableBatchesForMed = med.productName ? getAvailableBatches(med.productName) : [];
              const calcBatch = med.batchNumber
                ? inventory.find(i => i.chemicalName === med.productName && i.batchNumber === med.batchNumber)
                : null;
              let stockWarn = null;
              if (calcBatch && med.totalQuantity) {
                const doseUnit = med.doseRateUnit || 'mL/animal';
                const isGrams = doseUnit.startsWith('g');
                const baseUnit = isGrams ? 'g' : 'mL';
                let avail = parseFloat(calcBatch.quantityRemainingNumber);
                if (isGrams) { if (calcBatch.quantityRemainingUnit === 'kg') avail *= 1000; }
                else { if (calcBatch.quantityRemainingUnit === 'L') avail *= 1000; }
                const used = parseFloat(med.totalQuantity);
                if (used > avail) stockWarn = `Insufficient stock: ${avail.toFixed(0)} ${baseUnit} available, ${used.toFixed(0)} ${baseUnit} needed`;
              }

              return (
                <div key={index} className="bg-orange-50 border-2 border-orange-300 rounded-lg p-4 space-y-3">
                  {/* Card header */}
                  <div className="flex justify-between items-center">
                    <span className="font-bold text-orange-900 text-base">
                      Medication {index + 1}{med.productName ? `: ${med.productName}` : ''}
                    </span>
                    {animalForm.medications.length > 1 && (
                      <button onClick={() => removeMedicationEntry(index)}
                        className="text-red-600 font-bold text-xl leading-none hover:text-red-800 px-2">
                        {'\u00d7'}
                      </button>
                    )}
                  </div>

                  {/* Medication select */}
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-1">Medication/Product *</label>
                    <select value={med.selectedMedication}
                      onChange={(e) => handleMedicationEntryChange(index, e.target.value)}
                      className="w-full p-3 border-2 border-gray-300 rounded-lg">
                      <option value="">Select medication...</option>
                      {animalMedOptions.map(m => (
                        <option key={m.name} value={m.name}>{m.name}</option>
                      ))}
                      <option value="Other">Other (Manual Entry)</option>
                    </select>
                  </div>

                  {/* Manual product name for Other */}
                  {med.selectedMedication === 'Other' && (
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-1">Product Name *</label>
                      <input type="text" value={med.productName}
                        onChange={(e) => updateMedicationField(index, 'productName', e.target.value)}
                        placeholder="Enter product name"
                        className="w-full p-3 border-2 border-gray-300 rounded-lg" />
                    </div>
                  )}

                  {/* Batch Number(s) — multi-batch support */}
                  {med.productName && (
                    <div className="space-y-2">
                      <div className="flex justify-between items-center">
                        <label className="block text-sm font-semibold text-gray-700">Batch Number(s) (optional)</label>
                        {availableBatchesForMed.length > 1 && (
                          <button
                            onClick={() => {
                              const current = med.selectedBatches && med.selectedBatches.length > 0 ? med.selectedBatches : [{ batchId: '', qty: '' }];
                              updateMedicationField(index, 'selectedBatches', [...current, { batchId: '', qty: '' }]);
                            }}
                            className="text-xs text-orange-700 font-semibold hover:text-orange-900"
                          >
                            + Add batch
                          </button>
                        )}
                      </div>
                      {(med.selectedBatches || [{ batchId: '', qty: '' }]).map((sb, bi) => (
                        <div key={bi} className="flex items-center gap-2 w-full min-w-0">
                          <select
                            value={sb.batchId}
                            onChange={(e) => {
                              const newBatchId = e.target.value;
                              const b = availableBatchesForMed.find(x => String(x.id) === newBatchId);
                              const updatedBatches = [...(med.selectedBatches || [{ batchId: '', qty: '' }])];
                              updatedBatches[bi] = { ...updatedBatches[bi], batchId: newBatchId };
                              // Single atomic update to avoid stale closure overwrites
                              const updatedMeds = [...animalForm.medications];
                              updatedMeds[index] = {
                                ...updatedMeds[index],
                                selectedBatches: updatedBatches,
                                batchNumber: b ? b.batchNumber : updatedMeds[index].batchNumber
                              };
                              setAnimalForm({ ...animalForm, medications: updatedMeds });
                            }}
                            className="flex-1 min-w-0 w-full p-2 border-2 border-gray-300 rounded-lg bg-white text-sm"
                            style={{maxWidth: '100%', overflow: 'hidden', textOverflow: 'ellipsis'}}
                          >
                            <option value="">No batch tracking</option>
                            {availableBatchesForMed.map(batch => (
                              <option key={batch.id} value={String(batch.id)}>
                                {batch.batchNumber} — {batch.quantityRemainingNumber}{batch.quantityRemainingUnit}
                              </option>
                            ))}
                          </select>
                          {(med.selectedBatches || []).length > 1 && (
                            <button
                              onClick={() => {
                                const updated = (med.selectedBatches || []).filter((_, i) => i !== bi);
                                updateMedicationField(index, 'selectedBatches', updated);
                              }}
                              className="text-red-500 text-lg font-bold px-1 flex-shrink-0"
                            >×</button>
                          )}
                        </div>
                      ))}
                      {availableBatchesForMed.length === 0 && (
                        <p className="text-xs text-orange-600">No batches in inventory — record a purchase first, or proceed without batch tracking.</p>
                      )}

                      {/* Real-time stock warning */}
                      {(() => {
                        if (!med.totalQuantity) return null;
                        const doseUnit = med.doseRateUnit || 'mL/animal';
                        const baseUnit = doseUnit.startsWith('g') ? 'g' : doseUnit.startsWith('tablets') ? 'tablets' : doseUnit.startsWith('syringes') ? 'syringes' : 'mL';
                        const toBase = (qty, u) => {
                          const n = parseFloat(qty) || 0;
                          if (u === 'L' || u === 'kg') return n * 1000;
                          return n;
                        };
                        const totalAvailBase = availableBatchesForMed.reduce((sum, b) => {
                          return sum + toBase(b.quantityRemainingNumber, b.quantityRemainingUnit);
                        }, 0);
                        if (totalAvailBase === 0) return null;
                        const neededBase = parseFloat(med.totalQuantity) || 0;
                        if (neededBase > totalAvailBase) {
                          const shortfall = (neededBase - totalAvailBase).toFixed(1);
                          const avail = totalAvailBase.toFixed(1);
                          return (
                            <div className="bg-red-50 border-2 border-red-400 rounded-lg p-3 text-sm">
                              <span className="font-bold text-red-700">{'\u26a0\ufe0f'} Insufficient stock!</span>
                              <span className="text-red-700"> Need {med.totalQuantity} {baseUnit} but only {avail} {baseUnit} on hand.</span>
                              <div className="text-red-600 mt-1">Shortfall: {shortfall} {baseUnit}. Record another purchase first.</div>
                            </div>
                          );
                        }
                        return (
                          <div className="bg-green-50 border border-green-300 rounded-lg p-2 text-xs text-green-700">
                            {'\u2713'} Sufficient stock available across all batches
                          </div>
                        );
                      })()}
                    </div>
                  )}

                  {/* Active ingredient — full width */}
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-1">Active Ingredient</label>
                    <input type="text" value={med.activeIngredient}
                      onChange={(e) => updateMedicationField(index, 'activeIngredient', e.target.value)}
                      placeholder="Auto-filled"
                      className="w-full p-3 border-2 border-gray-300 rounded-lg" />
                  </div>

                  {/* WHP label info (read-only) + editable WHP and ESI numeric fields */}
                  {med.withholdingPeriod && (
                    <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
                      <p className="text-xs font-semibold text-amber-800 mb-1">Label Withholding Period</p>
                      <p className="text-sm text-amber-900">{med.withholdingPeriod}</p>
                    </div>
                  )}
                  {/* WHP and ESI on same row — items-end keeps inputs aligned even if labels wrap */}
                  <div className="grid grid-cols-2 gap-3 items-end">
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-1">WHP (days) *<br/><span className="font-normal text-gray-500 text-xs">0 if nil</span></label>
                      <input type="number" inputMode="numeric" min="0" style={{MozAppearance:"textfield"}} onWheel={(e)=>e.target.blur()}
                        value={med.withholdingDays}
                        onChange={(e) => updateMedicationField(index, 'withholdingDays', e.target.value)}
                        placeholder="e.g. 28"
                        className={`w-full p-3 border-2 rounded-lg ${med.withholdingDays === '' ? 'border-amber-400 bg-amber-50' : 'border-gray-300'}`} />
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-1">ESI (days) *<br/><span className="font-normal text-gray-500 text-xs">0 if nil</span></label>
                      <input type="number" inputMode="numeric" min="0" style={{MozAppearance:"textfield"}} onWheel={(e)=>e.target.blur()}
                        value={med.esiDays}
                        onChange={(e) => updateMedicationField(index, 'esiDays', e.target.value)}
                        placeholder="e.g. 42"
                        className={`w-full p-3 border-2 rounded-lg ${med.esiDays === '' ? 'border-amber-400 bg-amber-50' : 'border-gray-300'}`} />
                    </div>
                  </div>

                  {/* Dose rate */}
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-1">Dose Rate</label>
                    <div className="flex gap-2 items-center">
                      <input type="text" inputMode="decimal" value={med.doseRateNumber}
                        onChange={(e) => updateMedicationField(index, 'doseRateNumber', e.target.value)}
                        placeholder="e.g., 1"
                        className="flex-1 p-3 border-2 border-gray-300 rounded-lg min-w-0" />
                      <select value={med.doseRateUnit}
                        onChange={(e) => updateMedicationField(index, 'doseRateUnit', e.target.value)}
                        className="p-3 border-2 border-gray-300 rounded-lg">
                        <option value="mL/kg">mL/kg</option>
                        <option value="mL/animal">mL/animal</option>
                        <option value="g/kg">g/kg</option>
                        <option value="tablets/kg">tablets/kg</option>
                        <option value="tablets/animal">tablets/animal</option>
                        <option value="syringes/animal">syringes/animal</option>
                      </select>
                    </div>
                    {(med.doseRateUnit === 'mL/kg' || med.doseRateUnit === 'g/kg' || med.doseRateUnit === 'tablets/kg') && (
                      <div className="mt-2">
                        <div className="flex items-center gap-2">
                          <span className="text-sm text-gray-600 whitespace-nowrap">per</span>
                          <input type="text" inputMode="decimal" value={med.doseRatePerKg}
                            onChange={(e) => updateMedicationField(index, 'doseRatePerKg', e.target.value)}
                            placeholder="1"
                            className="w-20 p-2 border-2 border-gray-300 rounded-lg text-center" />
                          <span className="text-sm text-gray-600 whitespace-nowrap">kg</span>
                        </div>
                        <p className="text-xs text-gray-500 mt-1">Leave blank if rate is already per 1 kg</p>
                      </div>
                    )}
                    {(med.doseRateUnit === 'tablets/kg' || med.doseRateUnit === 'tablets/animal' || med.doseRateUnit === 'syringes/animal') && (
                      <p className="text-xs text-amber-700 mt-1">{'⚠️'} Dose rounded to nearest ½ tablet/syringe per animal</p>
                    )}
                  </div>

                  {/* Calculated dose result */}
                  {med.calculatedDosePerAnimal && (
                    <div className={`p-3 rounded-lg border-2 ${stockWarn ? 'bg-red-50 border-red-400' : 'bg-green-50 border-green-300'}`}>
                      <div className={`text-sm font-semibold mb-1 ${stockWarn ? 'text-red-800' : 'text-green-800'}`}>
                        {(() => {
                          const u = med.doseRateUnit || 'mL/animal';
                          const du = u.startsWith('tablets') ? 'tablets' : u.startsWith('syringes') ? 'syringes' : u.startsWith('g') ? 'g' : 'mL';
                          return `Dose per animal: ${med.calculatedDosePerAnimal} ${du}`;
                        })()}
                      </div>
                      <div className={`text-base font-bold ${stockWarn ? 'text-red-800' : 'text-green-800'}`}>
                        {(() => {
                          const u = med.doseRateUnit || 'mL/animal';
                          const du = u.startsWith('tablets') ? 'tablets' : u.startsWith('syringes') ? 'syringes' : u.startsWith('g') ? 'g' : 'mL';
                          const total = parseFloat(med.totalQuantity);
                          let display;
                          if (du === 'mL' && total >= 1000) display = `${(total/1000).toFixed(2)} L`;
                          else if (du === 'g' && total >= 1000) display = `${(total/1000).toFixed(2)} kg`;
                          else display = `${med.totalQuantity} ${du}`;
                          return `Total for whole mob: ${display}`;
                        })()}
                      </div>
                      {stockWarn && (
                        <div className="text-sm font-bold text-red-700 mt-1">{'⚠️'} {stockWarn}</div>
                      )}
                    </div>
                  )}

                  {/* Treatment purpose + application method */}
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-1">Treatment Purpose *</label>
                    <input type="text" value={med.treatmentPurpose}
                      onChange={(e) => updateMedicationField(index, 'treatmentPurpose', e.target.value)}
                      placeholder="e.g., Parasite control, vaccination"
                      className="w-full p-3 border-2 border-gray-300 rounded-lg" />
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-1">Application Method</label>
                    <select value={med.applicationMethod}
                      onChange={(e) => updateMedicationField(index, 'applicationMethod', e.target.value)}
                      className="w-full p-3 border-2 border-gray-300 rounded-lg">
                      <option>Injectable</option>
                      <option>Pour-On</option>
                      <option>Drench</option>
                      <option>Oral</option>
                      <option>In-Feed</option>
                      <option>In-Water</option>
                    </select>
                  </div>
                </div>
              );
            })}

            {/* Withholding Period / ESI Summary */}
            {animalForm.medications.some(m => m.productName && (m.withholdingDays !== '' || m.esiDays !== '')) && (
              <div className="bg-amber-50 border-2 border-amber-400 rounded-lg p-4">
                <div className="flex items-center gap-2 mb-2">
                  <span className="text-lg">{'\u23f1\ufe0f'}</span>
                  <span className="font-bold text-amber-900 text-base">Withholding Periods &amp; ESI — Do Not Slaughter/Export Until:</span>
                </div>
                <div className="space-y-2">
                  {(() => {
                    const activeMeds = animalForm.medications.filter(m => m.productName);
                    const maxWhp = Math.max(...activeMeds.map(m => parseInt(m.withholdingDays, 10) || 0));
                    const maxEsi = Math.max(...activeMeds.map(m => parseInt(m.esiDays, 10) || 0));
                    const calcExpiry = (days) => {
                      if (days === '' || days === null || days === undefined) return null;
                      const d = parseInt(days, 10);
                      if (isNaN(d)) return null;
                      if (d === 0) return 'Nil';
                      const base = animalForm.date ? new Date(animalForm.date) : new Date();
                      base.setDate(base.getDate() + d);
                      return base.toLocaleDateString('en-AU');
                    };
                    return activeMeds.map((m, i) => {
                      const whpExpiry = calcExpiry(m.withholdingDays);
                      const esiExpiry = calcExpiry(m.esiDays);
                      const whpD = parseInt(m.withholdingDays, 10) || 0;
                      const esiD = parseInt(m.esiDays, 10) || 0;
                      const isLongestWhp = activeMeds.length > 1 && whpD > 0 && whpD === maxWhp;
                      const isLongestEsi = activeMeds.length > 1 && esiD > 0 && esiD === maxEsi;
                      return (
                        <div key={i} className="text-sm text-amber-800">
                          <div className="font-semibold mb-1">{m.productName}:</div>
                          <div className="space-y-0.5 pl-2">
                            <div className={isLongestWhp ? 'font-bold text-amber-900' : ''}>
                              Do not slaughter until: <span className={isLongestWhp ? 'font-bold' : 'font-semibold'}>{whpExpiry || 'Enter WHP days above'}</span>
                            </div>
                            <div className={isLongestEsi ? 'font-bold text-amber-900' : ''}>
                              Do not export until: <span className={isLongestEsi ? 'font-bold' : 'font-semibold'}>{esiExpiry || 'Enter ESI days above'}</span>
                            </div>
                          </div>
                        </div>
                      );
                    });
                  })()}
                </div>
                {animalForm.medications.filter(m => m.productName).length > 1 && (
                  <div className="mt-2 pt-2 border-t border-amber-300 text-xs font-bold text-amber-900">
                    {'\u26a0\ufe0f'} Observe the longest withholding period and ESI above before slaughter or export.
                  </div>
                )}
              </div>
            )}

            {/* Add Another Medication */}
            {animalForm.medications.length < 6 && (
              <button onClick={addMedicationEntry}
                className="w-full bg-orange-100 text-orange-800 border-2 border-orange-400 p-3 rounded-lg font-semibold hover:bg-orange-200">
                + Add Another Medication ({animalForm.medications.length}/6)
              </button>
            )}

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">Notes</label>
              <textarea value={animalForm.notes}
                onChange={(e) => setAnimalForm({ ...animalForm, notes: e.target.value })}
                rows="3"
                className="w-full p-3 border-2 border-gray-300 rounded-lg"
                placeholder="Additional notes..." />
            </div>

            <div className="flex space-x-3 pt-4">
              <button onClick={saveAnimalRecord}
                className="flex-1 bg-green-600 text-white p-4 rounded-lg text-lg font-semibold hover:bg-green-700">
                {editingEntry ? '\u2713 Update Record' : '\u2713 Save Record'}
              </button>
              <button
                onClick={() => {
                  setEditingEntry(null);
                  setSection('home');
                }}
                className="flex-1 bg-gray-500 text-white p-4 rounded-lg text-lg font-semibold hover:bg-gray-600">
                Cancel
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Records View
  if (section === 'records') {
    // Helper: parse year from AU date string (dd/mm/yyyy) or ISO
    const getRecordYear = (record) => {
      if (!record.date) return '';
      const d = record.date.includes('/') ? record.date.split('/')[2] : record.date.split('-')[0];
      return d ? d.toString() : '';
    };
    // Helper: get season from AU date string
    const getRecordSeason = (record) => {
      if (!record.date) return '';
      let month;
      if (record.date.includes('/')) {
        month = parseInt(record.date.split('/')[1]);
      } else {
        month = parseInt(record.date.split('-')[1]);
      }
      if (month === 12 || month === 1 || month === 2) return 'Summer';
      if (month >= 3 && month <= 5) return 'Autumn';
      if (month >= 6 && month <= 8) return 'Winter';
      if (month >= 9 && month <= 11) return 'Spring';
      return '';
    };

    // Derive available years from records
    const availableYears = [...new Set(records.map(r => getRecordYear(r)).filter(Boolean))].sort().reverse();
    // Derive available paddocks from land records
    const availablePaddocks = [...new Set(records.filter(r => r.type === 'land' && r.paddockName).map(r => r.paddockName))].sort();
    const availableSpecies = [...new Set(records.filter(r => r.type === 'animal' && r.species).map(r => r.species))].sort();
    const availableFarms = [...new Set(records.filter(r => r.farmName).map(r => r.farmName))].sort();

    const today = new Date();
    today.setHours(0,0,0,0);

    const isWithinPeriod = (days, dateStr) => {
      const d = parseInt(days, 10);
      if (isNaN(d) || d === 0 || days == null || days === '') return false;
      if (!dateStr) return false;
      const parts = dateStr.includes('/') ? dateStr.split('/') : null;
      const base = parts ? new Date(`${parts[2]}-${parts[1].padStart(2,'0')}-${parts[0].padStart(2,'0')}`) : new Date(dateStr);
      if (isNaN(base.getTime())) return false;
      base.setDate(base.getDate() + d);
      return today < base;
    };

    const filteredRecords = records.filter(record => {
      if (filter === 'in-whp') {
        if (record.type === 'land') {
          const chems = record.chemicals && record.chemicals.length > 0 ? record.chemicals : [{ withholdingDays: record.withholdingDays }];
          return chems.some(c => isWithinPeriod(c.withholdingDays, record.date));
        }
        if (record.type === 'animal') {
          const meds = record.medications && record.medications.length > 0 ? record.medications : [{ withholdingDays: record.withholdingDays }];
          return meds.some(m => isWithinPeriod(m.withholdingDays, record.date));
        }
        return false;
      }
      if (filter === 'in-esi') {
        if (record.type === 'animal') {
          const meds = record.medications && record.medications.length > 0 ? record.medications : [{ esiDays: record.esiDays }];
          return meds.some(m => isWithinPeriod(m.esiDays, record.date));
        }
        return false;
      }
      const matchesType = filter === 'all' || record.type === filter;
      const matchesSearch = !searchTerm ||
        record.productName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        record.activeIngredient?.toLowerCase().includes(searchTerm.toLowerCase());
      const matchesYear = !filterYear || getRecordYear(record) === filterYear;
      const matchesSeason = !filterSeason || getRecordSeason(record) === filterSeason;
      const matchesPaddock = !filterPaddock || (record.type === 'land' && record.paddockName === filterPaddock);
      const matchesSpecies = !filterSpecies || (record.type === 'animal' && record.species === filterSpecies);
      const matchesFarm = !filterFarm || record.farmName === filterFarm;
      return matchesType && matchesSearch && matchesYear && matchesSeason && matchesPaddock && matchesSpecies && matchesFarm;
    });

    const whpCount = records.filter(record => {
      const chems = record.type === 'land' ? (record.chemicals && record.chemicals.length > 0 ? record.chemicals : [{ withholdingDays: record.withholdingDays }]) : [];
      const meds = record.type === 'animal' ? (record.medications && record.medications.length > 0 ? record.medications : [{ withholdingDays: record.withholdingDays }]) : [];
      return [...chems, ...meds].some(x => isWithinPeriod(x.withholdingDays, record.date));
    }).length;

    const esiCount = records.filter(record => {
      if (record.type !== 'animal') return false;
      const meds = record.medications && record.medications.length > 0 ? record.medications : [{ esiDays: record.esiDays }];
      return meds.some(m => isWithinPeriod(m.esiDays, record.date));
    }).length;

    const clearFilters = () => {
      setFilter('all');
      setSearchTerm('');
      setFilterYear('');
      setFilterSeason('');
      setFilterPaddock('');
      setFilterSpecies('');
      setFilterFarm('');
    };
    const hasActiveFilters = filter !== 'all' || searchTerm || filterYear || filterSeason || filterPaddock || filterSpecies || filterFarm;

    return (
      <div className="min-h-screen bg-gray-50 p-4">
        {ConfirmModal}
        <div className="max-w-2xl mx-auto">
          <div className="bg-white rounded-lg shadow-lg p-6 mb-6">
            <h2 className="text-2xl font-bold text-center text-gray-800">All Records</h2>
            <p className="text-center text-gray-600">{filteredRecords.length} of {records.length} records</p>
          </div>

          <div className="bg-white rounded-lg shadow-lg p-4 mb-6 space-y-3">
            {/* Type filter */}
            <div className="grid grid-cols-3 gap-2">
              <button
                onClick={() => { setFilter('all'); setFilterPaddock(''); setFilterSpecies(''); }}
                className={`p-3 rounded-lg font-semibold text-sm ${filter === 'all' ? 'bg-blue-600 text-white' : 'bg-gray-200 text-gray-700'}`}
              >
                All ({records.length})
              </button>
              <button
                onClick={() => { setFilter('land'); setFilterSpecies(''); }}
                className={`p-3 rounded-lg font-semibold text-sm ${filter === 'land' ? 'bg-green-600 text-white' : 'bg-gray-200 text-gray-700'}`}
              >
                Land ({records.filter(r => r.type === 'land').length})
              </button>
              <button
                onClick={() => { setFilter('animal'); setFilterPaddock(''); }}
                className={`p-3 rounded-lg font-semibold text-sm ${filter === 'animal' ? 'bg-orange-600 text-white' : 'bg-gray-200 text-gray-700'}`}
              >
                Animal ({records.filter(r => r.type === 'animal').length})
              </button>
            </div>

            {/* WHP + ESI active period filters */}
            <div className="grid grid-cols-2 gap-2">
              <button
                onClick={() => { setFilter('in-whp'); setFilterPaddock(''); setFilterSpecies(''); setSearchTerm(''); setFilterYear(''); setFilterSeason(''); }}
                className={`p-3 rounded-lg font-semibold text-sm border-2 ${filter === 'in-whp' ? 'bg-black text-red-400 border-black' : 'bg-black text-red-500 border-black hover:bg-gray-900'}`}
              >
                {'\u26a0\ufe0f'} Within WHP ({whpCount})
              </button>
              <button
                onClick={() => { setFilter('in-esi'); setFilterPaddock(''); setFilterSpecies(''); setSearchTerm(''); setFilterYear(''); setFilterSeason(''); }}
                className={`p-3 rounded-lg font-semibold text-sm border-2 ${filter === 'in-esi' ? 'bg-black text-red-400 border-black' : 'bg-black text-red-500 border-black hover:bg-gray-900'}`}
              >
                {'\u26a0\ufe0f'} Within ESI ({esiCount})
              </button>
            </div>

            {/* Year + Season row — not shown for WHP/ESI filters */}
            {filter !== 'in-whp' && filter !== 'in-esi' && (
              <div className="grid grid-cols-2 gap-2">
                <select value={filterYear} onChange={(e) => setFilterYear(e.target.value)}
                  className="p-2 border-2 border-gray-300 rounded-lg text-sm">
                  <option value="">All Years</option>
                  {availableYears.map(y => <option key={y} value={y}>{y}</option>)}
                </select>
                <select value={filterSeason} onChange={(e) => setFilterSeason(e.target.value)}
                  className="p-2 border-2 border-gray-300 rounded-lg text-sm">
                  <option value="">All Seasons</option>
                  <option>Summer</option>
                  <option>Autumn</option>
                  <option>Winter</option>
                  <option>Spring</option>
                </select>
              </div>
            )}

            {/* Farm filter — applies to both land and animal */}
            {filter !== 'in-whp' && filter !== 'in-esi' && availableFarms.length > 0 && (
              <select value={filterFarm} onChange={(e) => setFilterFarm(e.target.value)}
                className="w-full p-2 border-2 border-gray-300 rounded-lg text-sm">
                <option value="">All Farms</option>
                {availableFarms.map(f => <option key={f} value={f}>{f}</option>)}
              </select>
            )}

            {/* Paddock filter — land only */}
            {(filter === 'all' || filter === 'land') && availablePaddocks.length > 0 && (
              <select value={filterPaddock} onChange={(e) => setFilterPaddock(e.target.value)}
                className="w-full p-2 border-2 border-gray-300 rounded-lg text-sm">
                <option value="">All Paddocks</option>
                {availablePaddocks.map(p => <option key={p} value={p}>{p}</option>)}
              </select>
            )}

            {/* Species filter — animal only */}
            {(filter === 'all' || filter === 'animal') && availableSpecies.length > 0 && (
              <select value={filterSpecies} onChange={(e) => setFilterSpecies(e.target.value)}
                className="w-full p-2 border-2 border-gray-300 rounded-lg text-sm">
                <option value="">All Species</option>
                {availableSpecies.map(s => <option key={s} value={s}>{s}</option>)}
              </select>
            )}

            {/* Search — not shown for WHP/ESI filters */}
            {filter !== 'in-whp' && filter !== 'in-esi' && (
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Search by product or ingredient..."
                className="w-full p-2 border-2 border-gray-300 rounded-lg text-sm"
              />
            )}

            {/* Clear filters */}
            {hasActiveFilters && (
              <button onClick={clearFilters}
                className="w-full p-2 bg-red-100 text-red-700 rounded-lg text-sm font-semibold hover:bg-red-200">
                Clear All Filters
              </button>
            )}
          </div>

          <button
            onClick={exportToCSV}
            className="w-full bg-green-600 text-white p-4 rounded-lg text-lg font-semibold hover:bg-green-700 mb-6"
          >
            {'\ud83d\udcca'} Export to CSV
          </button>

          <div className="space-y-3 mb-6">
            {filteredRecords.length === 0 ? (
              <div className="bg-white rounded-lg shadow-lg p-8 text-center text-gray-500">
                No records found
              </div>
            ) : (
              filteredRecords.map((record) => (
                <div key={record.id} className="bg-white rounded-lg shadow-lg p-4">
                  <div className="flex justify-between items-start">
                    <div className="flex-1 mr-3">

                      {/* Row 1: type badge + date + record ID */}
                      <div className="flex items-center space-x-2 mb-1">
                        <span className={`px-3 py-1 rounded-full text-sm font-semibold ${
                          record.type === 'land' ? 'bg-blue-100 text-blue-800' : 'bg-orange-100 text-orange-800'
                        }`}>
                          {record.type === 'land' ? '\ud83c\udf3e Land' : '\ud83d\udc04 Animal'}
                        </span>
                        {record.displayId && (
                          <span className="text-xs font-mono font-semibold text-gray-500 bg-gray-100 px-2 py-1 rounded">
                            {record.displayId}
                          </span>
                        )}
                        <span className="text-sm text-gray-600">{record.date}</span>
                      </div>

                      {/* Farm name */}
                      {record.farmName && (
                        <div className="text-xs font-semibold text-green-700 mb-1">
                          {'🏡'} {record.farmName}
                        </div>
                      )}

                      {/* Land record layout */}
                      {record.type === 'land' && (
                        <>
                          {/* Chemical name(s) */}
                          <div className="font-bold text-base text-gray-900 mb-1">
                            {record.chemicals && record.chemicals.length > 0
                              ? record.chemicals.map((c, i) => (
                                  <div key={i}>{c.productName}</div>
                                ))
                              : <div>{record.productName}</div>
                            }
                          </div>

                          {/* Paddock / area / pest / volume */}
                          <div className="text-sm text-gray-600">
                            {record.paddockName && <span className="font-medium">{record.paddockName}</span>}
                            {record.areaHectares && <span> {'\u2022'} {record.areaHectares} ha</span>}
                            {record.totalDilutedVolumeQty && <span> {'\u2022'} {record.totalDilutedVolumeQty} {record.totalDilutedVolumeUnit}</span>}
                            {record.pestTarget && <span> {'\u2022'} {record.pestTarget}</span>}
                          </div>

                          {/* WHP per chemical with expiry */}
                          {(() => {
                            const today = new Date();
                            today.setHours(0,0,0,0);
                            const calcExpiry = (days, dateStr) => {
                              const d = parseInt(days, 10);
                              if (isNaN(d) || days == null) return null;
                              if (d === 0) return 'Nil';
                              if (!dateStr) return null;
                              const parts = dateStr.includes('/') ? dateStr.split('/') : null;
                              const base = parts ? new Date(`${parts[2]}-${parts[1].padStart(2,'0')}-${parts[0].padStart(2,'0')}`) : new Date(dateStr);
                              if (isNaN(base.getTime())) return null;
                              base.setDate(base.getDate() + d);
                              return base;
                            };
                            const chems = (record.chemicals && record.chemicals.length > 0)
                              ? record.chemicals
                              : [{ withholdingPeriod: record.withholdingPeriod, withholdingDays: record.withholdingDays, productName: record.productName }];
                            const relevant = chems.filter(c => c.withholdingDays != null || c.withholdingPeriod);
                            if (relevant.length === 0) return null;
                            return relevant.map((c, i) => {
                              const expiryDate = calcExpiry(c.withholdingDays, record.date);
                              const isNil = expiryDate === 'Nil';
                              const isActive = expiryDate && !isNil && expiryDate > today;
                              const expiryStr = isNil ? 'Nil' : expiryDate ? expiryDate.toLocaleDateString('en-AU') : null;
                              return (
                                <div key={i} className={`text-sm font-medium mt-1 ${isActive ? 'text-red-600' : 'text-gray-500'}`}>
                                  {record.chemicals && record.chemicals.length > 1 && <span className="text-xs">({c.productName}) </span>}
                                  {expiryStr
                                    ? isNil
                                      ? 'Do not harvest/graze until: Nil'
                                      : `Do not harvest/graze until: ${expiryStr}`
                                    : (c.withholdingPeriod || '')
                                  }
                                </div>
                              );
                            });
                          })()}
                        </>
                      )}

                      {/* Animal record layout */}
                      {record.type === 'animal' && (
                        <>
                          <div className="font-bold text-base text-gray-900 mb-1">
                            {record.medications && record.medications.length > 0
                              ? record.medications.map((m, i) => (
                                  <div key={i}>{m.productName}</div>
                                ))
                              : <div>{record.productName}</div>
                            }
                          </div>
                          <div className="text-sm text-gray-600 mt-1">
                            {record.species} {'\u2022'} {record.animalCount} animals
                            {record.animalIDs && <span> {'\u2022'} {record.animalIDs}</span>}
                            {record.medications && record.medications.length > 0
                              ? ` \u2022 ${record.medications.map(m => m.treatmentPurpose).filter(Boolean).join(', ')}`
                              : (record.treatmentPurpose ? ` \u2022 ${record.treatmentPurpose}` : '')
                            }
                          </div>

                          {/* Dose per animal */}
                          {(record.medications && record.medications.length > 0
                            ? record.medications.some(m => m.dosePerAnimal)
                            : record.dosePerAnimal) && (
                            <div className="text-sm text-gray-600 mt-1">
                              {record.medications && record.medications.length > 0
                                ? record.medications.filter(m => m.dosePerAnimal).map((m, i) => {
                                    const u = (m.doseRate || '').includes('tablets') ? 'tablets' : (m.doseRate || '').includes('syringes') ? 'syringes' : (m.doseRate || '').startsWith('g') ? 'g' : 'mL';
                                    return <div key={i}>{record.medications.length > 1 && <span className="text-gray-500">({m.productName}) </span>}Dose/animal: <span className="font-medium">{m.dosePerAnimal} {u}</span></div>;
                                  })
                                : <div>Dose/animal: <span className="font-medium">{record.dosePerAnimal}</span></div>
                              }
                            </div>
                          )}

                          {/* WHP + ESI per medication with calculated expiry */}
                          {(() => {
                            const today = new Date();
                            today.setHours(0,0,0,0);
                            const calcExpiry = (days, dateStr) => {
                              const d = parseInt(days, 10);
                              if (isNaN(d) || days == null) return null;
                              if (d === 0) return 'Nil';
                              if (!dateStr) return null;
                              const parts = dateStr.includes('/') ? dateStr.split('/') : null;
                              const base = parts ? new Date(`${parts[2]}-${parts[1].padStart(2,'0')}-${parts[0].padStart(2,'0')}`) : new Date(dateStr);
                              if (isNaN(base.getTime())) return null;
                              base.setDate(base.getDate() + d);
                              return base;
                            };
                            const meds = (record.medications && record.medications.length > 0)
                              ? record.medications
                              : [{ productName: record.productName, withholdingDays: record.withholdingDays, esiDays: record.esiDays }];
                            const hasDates = meds.some(m => m.withholdingDays != null || m.esiDays != null);
                            if (!hasDates) return null;
                            const maxWhp = Math.max(...meds.map(m => parseInt(m.withholdingDays, 10) || 0));
                            const maxEsi = Math.max(...meds.map(m => parseInt(m.esiDays, 10) || 0));
                            return (
                              <div className="text-sm font-medium mt-1 space-y-1">
                                {meds.map((m, i) => {
                                  const whpDate = calcExpiry(m.withholdingDays, record.date);
                                  const esiDate = calcExpiry(m.esiDays, record.date);
                                  const whpStr = whpDate === 'Nil' ? 'Nil' : whpDate ? whpDate.toLocaleDateString('en-AU') : null;
                                  const esiStr = esiDate === 'Nil' ? 'Nil' : esiDate ? esiDate.toLocaleDateString('en-AU') : null;
                                  const whpActive = whpDate && whpDate !== 'Nil' && whpDate > today;
                                  const esiActive = esiDate && esiDate !== 'Nil' && esiDate > today;
                                  const whpD = parseInt(m.withholdingDays, 10) || 0;
                                  const esiD = parseInt(m.esiDays, 10) || 0;
                                  const isLongestWhp = meds.length > 1 && whpD > 0 && whpD === maxWhp;
                                  const isLongestEsi = meds.length > 1 && esiD > 0 && esiD === maxEsi;
                                  return (
                                    <div key={i}>
                                      {meds.length > 1 && <div className="text-xs text-gray-500 mb-0.5">({m.productName})</div>}
                                      {whpStr && (
                                        <div className={`${whpActive ? 'text-red-600' : 'text-gray-500'} ${isLongestWhp ? 'font-bold' : ''}`}>
                                          Do not slaughter until: {whpStr}
                                        </div>
                                      )}
                                      {esiStr && (
                                        <div className={`${esiActive ? 'text-red-600' : 'text-gray-500'} ${isLongestEsi ? 'font-bold' : ''}`}>
                                          Do not export until: {esiStr}
                                        </div>
                                      )}
                                    </div>
                                  );
                                })}
                              </div>
                            );
                          })()}
                        </>
                      )}

                    </div>

                    {/* Action buttons */}
                    <div className="flex flex-col space-y-2">
                      {/* Edit */}
                      <button
                        onClick={() => {
                          if (record.type === 'land') {
                          const chemicals = record.chemicals && record.chemicals.length > 0
                              ? record.chemicals.map(c => {
                                  const dbMatch = allChemicals.find(db => db.name === c.productName);
                                  return {
                                    selectedChemical: dbMatch ? c.productName : 'Other',
                                    productName: c.productName || '',
                                    activeIngredient: c.activeIngredient || '',
                                    batchNumber: c.batchNumber || '',
                                    rateMode: c.rateMode || 'dilution',
                                    rateQty: c.rateQty || '',
                                    rateUnit: c.rateUnit || 'mL',
                                    rateWaterQty: c.rateWaterQty || '100',
                                    areaRateQty: c.areaRateQty || '',
                                    areaRateUnit: c.areaRateUnit || 'mL/ha',
                                    calculatedQty: c.calculatedQty || '',
                                    calculatedQtyUnit: c.calculatedQtyUnit || 'mL',
                                    withholdingPeriod: c.withholdingPeriod || '',
                                    withholdingDays: c.withholdingDays != null ? String(c.withholdingDays) : '',
                                    selectedBatches: c.selectedBatches && c.selectedBatches.length > 0
                                      ? c.selectedBatches : [{ batchId: '', qty: '' }]
                                  };
                                })
                              : [emptyChemicalEntry()];
                            setLandForm({
                              date: (() => {
                                if (!record.date) return new Date().toISOString().split('T')[0];
                                if (record.date.includes('/')) {
                                  const [d, m, y] = record.date.split('/');
                                  return `${y}-${m.padStart(2,'0')}-${d.padStart(2,'0')}`;
                                }
                                return record.date;
                              })(),
                              farmName: record.farmName || propertyAddress || '',
                              paddocks: record.paddocks && record.paddocks.length > 0
                                ? record.paddocks
                                : (record.paddockName ? [{ name: record.paddockName, areaHectares: record.areaHectares || '' }] : []),
                              paddockName: record.paddockName || '',
                              areaHectares: record.areaHectares || '',
                              pestTargets: record.pestTargets && record.pestTargets.length > 0
                                ? record.pestTargets
                                : (record.pestTarget ? record.pestTarget.split(', ').map(s => s.trim()).filter(Boolean) : []),
                              pestTarget: record.pestTarget || '',
                              pestTargetOther: '',
                              cropLandUse: record.cropLandUse || '',
                              cropLandUseOther: '',
                              applicationMethod: record.applicationMethod || 'Spot Spray',
                              totalDilutedVolumeQty: record.totalDilutedVolumeQty || '',
                              totalDilutedVolumeUnit: record.totalDilutedVolumeUnit || 'L',
                              chemicals,
                              windSpeed: record.windSpeed || '',
                              windDirection: record.windDirection || '',
                              applicator: record.applicator || '',
                              photos: [],
                              notes: record.notes || ''
                            });
                            setEditingEntry({ id: record.id });
                            setSection('land');
                          } else {
                            // Build medications array from record — handles both new multi-med and legacy records
                            const parseDoseRateNumber = (doseRate) => {
                              const dr = doseRate || '';
                              const mKg = dr.match(/(\d+\.?\d*)\s*mL\s*\/\s*(\d+\.?\d*)\s*kg/i);
                              const mAn = dr.match(/(\d+\.?\d*)\s*mL\s*\/\s*animal/i);
                              if (mKg) return (parseFloat(mKg[1]) / parseFloat(mKg[2])).toString();
                              if (mAn) return mAn[1];
                              const simple = dr.match(/^([\d.]+)/);
                              return simple ? simple[1] : '';
                            };
                            const parseDoseRateUnit = (doseRateUnit, doseRate) => {
                              const dr = doseRateUnit || doseRate || '';
                              if (dr.includes('tablets/animal')) return 'tablets/animal';
                              if (dr.includes('tablets/kg')) return 'tablets/kg';
                              if (dr.includes('syringes/animal')) return 'syringes/animal';
                              if (dr.includes('g/kg')) return 'g/kg';
                              if (dr.includes('/animal')) return 'mL/animal';
                              return 'mL/kg';
                            };
                            const editMedications = (record.medications && record.medications.length > 0)
                              ? record.medications.map(m => ({
                                  selectedMedication: m.productName || 'Other',
                                  productName: m.productName || '',
                                  activeIngredient: m.activeIngredient || '',
                                  batchNumber: m.batchNumber || '',
                                  selectedBatches: m.selectedBatches && m.selectedBatches.length > 0 ? m.selectedBatches : [{ batchId: '', qty: '' }],
                                  withholdingPeriod: m.withholdingPeriod || '',
                                  withholdingDays: m.withholdingDays != null ? String(m.withholdingDays) : '',
                                  esiDays: m.esiDays != null ? String(m.esiDays) : '',
                                  doseRate: m.doseRate || '',
                                  doseRateNumber: parseDoseRateNumber(m.doseRate),
                                  doseRateUnit: parseDoseRateUnit(m.doseRateUnit, m.doseRate),
                                  doseRatePerKg: m.doseRatePerKg || '',
                                  treatmentPurpose: m.treatmentPurpose || '',
                                  applicationMethod: m.applicationMethod || 'Injectable',
                                  calculatedDosePerAnimal: m.dosePerAnimal || '',
                                  totalQuantity: m.totalQuantity || '',
                                }))
                              : [{
                                  selectedMedication: record.productName ? 'Other' : '',
                                  productName: record.productName || '',
                                  activeIngredient: record.activeIngredient || '',
                                  batchNumber: record.batchNumber || '',
                                  selectedBatches: [{ batchId: '', qty: '' }],
                                  withholdingPeriod: record.withholdingPeriod || '',
                                  withholdingDays: record.withholdingDays != null ? String(record.withholdingDays) : '',
                                  esiDays: record.esiDays != null ? String(record.esiDays) : '',
                                  doseRate: record.doseRate || '',
                                  doseRateNumber: parseDoseRateNumber(record.doseRate),
                                  doseRateUnit: parseDoseRateUnit(record.doseRateUnit, record.doseRate),
                                  doseRatePerKg: record.doseRatePerKg || '',
                                  treatmentPurpose: record.treatmentPurpose || '',
                                  applicationMethod: record.applicationMethod || 'Injectable',
                                  calculatedDosePerAnimal: record.dosePerAnimal || '',
                                  totalQuantity: record.totalQuantity || '',
                                }];
                            setAnimalForm({
                              date: (() => {
                                if (record.date && record.date.includes('/')) {
                                  const [d, m, y] = record.date.split('/');
                                  return `${y}-${m.padStart(2,'0')}-${d.padStart(2,'0')}`;
                                }
                                return record.date || new Date().toISOString().split('T')[0];
                              })(),
                              farmName: record.farmName || propertyAddress || '',
                              species: record.species || 'Cattle',
                              animalCount: record.animalCount || '',
                              averageWeight: record.averageWeight || '',
                              animalIDs: record.animalIDs || '',
                              applicator: record.applicator || '',
                              photos: [],
                              notes: record.notes || '',
                              medications: editMedications,
                              // Legacy fields
                              selectedMedication: 'Other',
                              productName: record.productName || '',
                              activeIngredient: record.activeIngredient || '',
                              batchNumber: record.batchNumber || '',
                              withholdingPeriod: record.withholdingPeriod || '',
                              doseRate: record.doseRate || '',
                              doseRateNumber: parseDoseRateNumber(record.doseRate),
                              doseRateUnit: parseDoseRateUnit(record.doseRateUnit, record.doseRate),
                              doseRatePerKg: record.doseRatePerKg || '',
                              calculatedDosePerAnimal: record.dosePerAnimal || '',
                              totalQuantity: record.totalQuantity || '',
                              treatmentPurpose: record.treatmentPurpose || '',
                              applicationMethod: record.applicationMethod || 'Injectable',
                            });
                            setEditingEntry({ id: record.id });
                            setSection('animal');
                          }
                        }}
                        className="bg-blue-500 text-white px-3 py-1 rounded text-sm font-medium hover:bg-blue-600"
                      >
                        Edit
                      </button>

                      {/* Copy (land or animal) */}
                      {record.type === 'land' && (
                        <button
                          onClick={() => {
                            const chemicals = record.chemicals && record.chemicals.length > 0
                              ? record.chemicals.map(c => {
                                  const dbMatch = allChemicals.find(db => db.name === c.productName);
                                  const chemEntry = {
                                    selectedChemical: dbMatch ? c.productName : 'Other',
                                    productName: c.productName || '',
                                    activeIngredient: c.activeIngredient || '',
                                    batchNumber: '',
                                    rateMode: c.rateMode || 'dilution',
                                    rateQty: c.rateQty || (c.applicationRate ? c.applicationRate.split(' ')[0] : ''),
                                    rateUnit: c.rateUnit || 'mL',
                                    rateWaterQty: c.rateWaterQty || '100',
                                    areaRateQty: c.areaRateQty || '',
                                    areaRateUnit: c.areaRateUnit || 'mL/ha',
                                    withholdingPeriod: c.withholdingPeriod || '',
                                    withholdingDays: c.withholdingDays != null ? String(c.withholdingDays) : '',
                                    selectedBatches: [{ batchId: '', qty: '' }]
                                  };
                                  // Recalculate quantity immediately from the carried-forward
                                  // rate/volume — don't leave it blank until a rate field is touched.
                                  const result = calcChemQty(chemEntry, record.totalDilutedVolumeQty || '', record.totalDilutedVolumeUnit || 'L', record.areaHectares || '');
                                  chemEntry.calculatedQty = result.qty;
                                  chemEntry.calculatedQtyUnit = result.unit;
                                  return chemEntry;
                                })
                              : [emptyChemicalEntry()];
                            setLandForm({
                              date: new Date().toISOString().split('T')[0],
                              farmName: record.farmName || lastUsedFarm,
                              paddocks: record.paddocks && record.paddocks.length > 0
                                ? record.paddocks
                                : (record.paddockName ? [{ name: record.paddockName, areaHectares: record.areaHectares || '' }] : []),
                              paddockName: record.paddockName || '',
                              areaHectares: record.areaHectares || '',
                              pestTargets: record.pestTargets && record.pestTargets.length > 0
                                ? record.pestTargets
                                : (record.pestTarget ? record.pestTarget.split(', ').map(s => s.trim()).filter(Boolean) : []),
                              pestTarget: record.pestTarget || '',
                              pestTargetOther: '',
                              cropLandUse: record.cropLandUse || '',
                              cropLandUseOther: '',
                              applicationMethod: record.applicationMethod || 'Spot Spray',
                              totalDilutedVolumeQty: record.totalDilutedVolumeQty || '',
                              totalDilutedVolumeUnit: record.totalDilutedVolumeUnit || 'L',
                              chemicals,
                              windSpeed: '',
                              windDirection: '',
                              applicator: farmerName,
                              photos: [],
                              notes: ''
                            });
                            setEditingEntry(null);
                            setSection('land');
                          }}
                          className="bg-green-500 text-white px-3 py-1 rounded text-sm font-medium hover:bg-green-600"
                        >
                          Copy
                        </button>
                      )}
                      {record.type === 'animal' && (
                        <button
                          onClick={() => {
                            const copyMeds = (record.medications && record.medications.length > 0
                              ? record.medications
                              : [{ productName: record.productName, activeIngredient: record.activeIngredient, withholdingPeriod: record.withholdingPeriod, withholdingDays: record.withholdingDays, esiDays: record.esiDays, doseRate: record.doseRate, doseRateUnit: record.doseRateUnit, doseRatePerKg: record.doseRatePerKg, treatmentPurpose: record.treatmentPurpose, applicationMethod: record.applicationMethod }]
                            ).map(m => ({
                              selectedMedication: m.productName || 'Other',
                              productName: m.productName || '',
                              activeIngredient: m.activeIngredient || '',
                              batchNumber: '',
                              selectedBatches: [{ batchId: '', qty: '' }],
                              withholdingPeriod: m.withholdingPeriod || '',
                              withholdingDays: m.withholdingDays != null ? String(m.withholdingDays) : '',
                              esiDays: m.esiDays != null ? String(m.esiDays) : '',
                              doseRate: m.doseRate || '',
                              doseRateNumber: m.doseRate ? m.doseRate.split(' ')[0] : '',
                              doseRateUnit: m.doseRateUnit || 'mL/kg',
                              doseRatePerKg: m.doseRatePerKg || '',
                              treatmentPurpose: m.treatmentPurpose || '',
                              applicationMethod: m.applicationMethod || 'Injectable',
                              calculatedDosePerAnimal: '',
                              totalQuantity: '',
                            }));
                            setAnimalForm({
                              date: new Date().toISOString().split('T')[0],
                              farmName: record.farmName || lastUsedFarm,
                              species: record.species || 'Cattle',
                              animalCount: record.animalCount || '',
                              averageWeight: record.averageWeight || '',
                              animalIDs: record.animalIDs || '',
                              applicator: record.applicator || farmerName,
                              photos: [],
                              notes: '',
                              medications: copyMeds,
                              selectedMedication: '',
                              productName: '',
                              activeIngredient: '',
                              batchNumber: '',
                              withholdingPeriod: '',
                              withholdingDays: '',
                              esiDays: '',
                              doseRate: '',
                              doseRateNumber: '',
                              doseRateUnit: 'mL/kg',
                              doseRatePerKg: '',
                              calculatedDosePerAnimal: '',
                              totalQuantity: '',
                              treatmentPurpose: '',
                              applicationMethod: 'Injectable',
                            });
                            setEditingEntry(null);
                            setSection('animal');
                          }}
                          className="bg-green-500 text-white px-3 py-1 rounded text-sm font-medium hover:bg-green-600"
                        >
                          Copy
                        </button>
                      )}

                      {/* Delete */}
                      <button
                        onClick={() => deleteRecord(record.id)}
                        className="bg-red-500 text-white px-3 py-1 rounded text-sm font-medium hover:bg-red-600"
                      >
                        Delete
                      </button>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>

          <button
            onClick={() => setSection('home')}
            className="w-full bg-gray-500 text-white p-4 rounded-xl text-xl font-semibold hover:bg-gray-600"
          >
            {'\u2190'} Back to Home
          </button>
        </div>
      </div>
    );
  }

  // Settings Screen
  if (section === 'settings') {
    return (
      <div className="min-h-screen bg-gray-50 p-4">
        {ConfirmModal}
        <div className="max-w-md mx-auto">
          <div className="bg-white rounded-lg shadow-lg p-6 mb-6">
            <h2 className="text-2xl font-bold text-center text-gray-800">Settings</h2>
          </div>

          <div className="bg-white rounded-lg shadow-lg p-6 mb-6">
            <h3 className="text-xl font-bold text-gray-800 mb-4">Farm Details</h3>
            <div className="space-y-3">
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Your Name
                </label>
                <input
                  type="text"
                  value={farmerName}
                  onChange={(e) => {
                    setFarmerName(e.target.value);
                    localStorage.setItem('defaultFarmerName', e.target.value);
                  }}
                  className="w-full p-3 border-2 border-gray-300 rounded-lg"
                />
              </div>
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Farm Name / Property Address
                </label>
                <input
                  type="text"
                  value={propertyAddress}
                  onChange={(e) => {
                    setPropertyAddress(e.target.value);
                    localStorage.setItem('propertyAddress', e.target.value);
                  }}
                  className="w-full p-3 border-2 border-gray-300 rounded-lg"
                />
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-lg p-6 mb-6">
            <h3 className="text-xl font-bold text-gray-800 mb-4">App Info</h3>
            <div className="text-gray-600 space-y-2">
              <div className="flex justify-between">
                <span>Version:</span>
                <span className="font-semibold">{APP_VERSION}</span>
              </div>
              <div className="flex justify-between">
                <span>Total Records:</span>
                <span className="font-semibold">{records.length}</span>
              </div>
              <div className="flex justify-between">
                <span>Inventory Items:</span>
                <span className="font-semibold">{inventory.length}</span>
              </div>
              <div className="flex justify-between">
                <span>Custom Chemicals:</span>
                <span className="font-semibold">{customChemicals.length}</span>
              </div>
              <div className="flex justify-between">
                <span>Retention Period:</span>
                <span className="font-semibold">3 Years</span>
              </div>
              <div className="flex justify-between">
                <span>Offline Ready:</span>
                <span className="text-green-600 font-semibold">{'\u2713'} Yes</span>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-lg p-6 mb-6">
            <h3 className="text-xl font-bold text-gray-800 mb-4">Feedback & Support</h3>
            <p className="text-sm text-gray-600 mb-4">
              Found a bug or have a suggestion? Send us the details.
            </p>
            <button
              onClick={() => {
                const email = localStorage.getItem('userEmail') || '';
                const subject = `Agri Admin Feedback (v${APP_VERSION})`;
                const body =
                  `\n\n\n--- Please describe your issue above this line ---\n` +
                  `Version: ${APP_VERSION}\n` +
                  `User: ${email}\n` +
                  `Records: ${records.length} | Inventory: ${inventory.length}\n` +
                  `Device: ${navigator.userAgent}`;
                window.location.href =
                  `mailto:agriadmi@gmail.com` +
                  `?subject=${encodeURIComponent(subject)}` +
                  `&body=${encodeURIComponent(body)}`;
              }}
              className="w-full bg-amber-500 text-white p-3 rounded-lg font-medium hover:bg-amber-600"
            >
              {'✉️'} Send Feedback
            </button>
          </div>

          <div className="bg-white rounded-lg shadow-lg p-6 mb-6">
            <h3 className="text-xl font-bold text-gray-800 mb-4">App Updates</h3>
            <button
              onClick={async () => {
                try {
                  if (!('serviceWorker' in navigator)) {
                    alert('\u26a0\ufe0f Service workers not supported on this device.');
                    return;
                  }
                  const registration = await navigator.serviceWorker.getRegistration();
                  if (!registration) {
                    alert('\u26a0\ufe0f No service worker found.\n\nTry closing and reopening the app.');
                    return;
                  }

                  const applyUpdate = () => {
                    registration.waiting.postMessage({ type: 'SKIP_WAITING' });
                    navigator.serviceWorker.addEventListener('controllerchange', () => {
                      window.location.reload();
                    }, { once: true });
                  };

                  // Case 1: update already downloaded and waiting — apply immediately
                  if (registration.waiting) {
                    showConfirm(
                      '\ud83c\udf89 New Version Available',
                      'Update now? The app will reload.\n\nYour data will be preserved.',
                      applyUpdate,
                      { confirmLabel: 'Update Now', confirmClass: 'bg-purple-600 text-white', cancelLabel: 'Later' }
                    );
                    return;
                  }

                  // Case 2: trigger a fresh fetch from the server
                  await registration.update();

                  // Case 3: update was sitting on server — now downloaded and waiting
                  if (registration.waiting) {
                    showConfirm(
                      '\ud83c\udf89 New Version Available',
                      'Update now? The app will reload.\n\nYour data will be preserved.',
                      applyUpdate,
                      { confirmLabel: 'Update Now', confirmClass: 'bg-purple-600 text-white', cancelLabel: 'Later' }
                    );
                    return;
                  }

                  // Case 4: still downloading — wait up to 8 seconds (shorter timeout)
                  if (registration.installing) {
                    const result = await new Promise((resolve) => {
                      const worker = registration.installing;
                      worker.addEventListener('statechange', () => {
                        if (worker.state === 'installed' || worker.state === 'waiting') resolve('update');
                        if (worker.state === 'activated' || worker.state === 'redundant') resolve('none');
                      });
                      setTimeout(() => resolve('timeout'), 8000);
                    });

                    if (result === 'update' && registration.waiting) {
                      showConfirm(
                        '\ud83c\udf89 New Version Available',
                        'Update now? The app will reload.\n\nYour data will be preserved.',
                        applyUpdate,
                        { confirmLabel: 'Update Now', confirmClass: 'bg-purple-600 text-white', cancelLabel: 'Later' }
                      );
                      return;
                    }
                  }

                  // No update found
                  alert(`\u2713 You have the latest version!\n\nVersion: ${APP_VERSION}`);

                } catch (error) {
                  alert('Error checking for updates: ' + error.message);
                }
              }}
              className="w-full bg-purple-600 text-white p-4 rounded-lg text-lg font-semibold hover:bg-purple-700"
            >
              {'\ud83d\udd14'} Check for Updates
            </button>
            <p className="text-xs text-gray-500 mt-2">
              Manually check if a new version is available. Your data is always preserved.
            </p>
          </div>

          <div className="bg-white rounded-lg shadow-lg p-6 mb-6">
            <h3 className="text-xl font-bold text-gray-800 mb-4">Data Management</h3>
            <div className="space-y-3">
              <button
                onClick={() => performBackup('manual')}
                className="w-full bg-blue-600 text-white p-3 rounded-lg font-medium hover:bg-blue-700"
              >
                {'\ud83d\udcbe'} Save Complete Backup
              </button>
              <button
                onClick={() => {
                  try {
                    const input = document.createElement('input');
                    input.type = 'file';
                    input.accept = '.json';
                    
                    input.onchange = (event) => {
                      const file = event.target.files[0];
                      if (file) {
                        const reader = new FileReader();
                        reader.onload = (e) => {
                          try {
                            const data = JSON.parse(e.target.result);
                            
                            showConfirm(
                              'Restore Backup',
                              `Load backup from ${data.exportDate || 'Unknown date'}?\n\nRecords: ${data.records?.length || 0}\nInventory: ${data.inventory?.length || 0}\nCustom Chemicals: ${data.customChemicals?.length || 0}\n\nThis will replace your current data.`,
                              () => {
                                if (data.records) setRecords(data.records);
                                if (data.inventory) setInventory(data.inventory);
                                if (data.customChemicals) {
                                  setCustomChemicals(data.customChemicals);
                                  localStorage.setItem('customChemicals', JSON.stringify(data.customChemicals));
                                }
                                if (data.savedPaddocks) {
                                  setSavedPaddocks(data.savedPaddocks);
                                  localStorage.setItem('savedPaddocks', JSON.stringify(data.savedPaddocks));
                                }
                                if (data.farmList) {
                                  setFarmList(data.farmList);
                                  localStorage.setItem('farmList', JSON.stringify(data.farmList));
                                }
                                if (data.lastUsedFarm) {
                                  setLastUsedFarm(data.lastUsedFarm);
                                  localStorage.setItem('lastUsedFarm', data.lastUsedFarm);
                                }
                                if (data.farmerName) {
                                  setFarmerName(data.farmerName);
                                  localStorage.setItem('defaultFarmerName', data.farmerName);
                                }
                                if (data.propertyAddress) {
                                  setPropertyAddress(data.propertyAddress);
                                  localStorage.setItem('propertyAddress', data.propertyAddress);
                                }
                                alert('\u2713 Backup restored successfully!');
                              },
                              { confirmLabel: 'Restore', confirmClass: 'bg-blue-600 text-white' }
                            );
                          } catch (error) {
                            alert('Error reading backup file: ' + error.message);
                          }
                        };
                        reader.readAsText(file);
                      }
                    };
                    
                    input.click();
                  } catch (error) {
                    alert('Error loading backup: ' + error.message);
                  }
                }}
                className="w-full bg-green-600 text-white p-3 rounded-lg font-medium hover:bg-green-700"
              >
                {'\ud83d\udcc1'} Restore from Backup
              </button>
            </div>
            <p className="text-xs text-gray-500 mt-3">
              {'\ud83d\udca1'} Tip: Save a backup regularly and before using "Check for Updates".
            </p>
          </div>

          <button
            onClick={() => setSection('home')}
            className="w-full bg-gray-500 text-white p-4 rounded-xl text-xl font-semibold hover:bg-gray-600"
          >
            {'\u2190'} Back to Home
          </button>
        </div>
      </div>
    );
  }

  return <div className="p-8 text-center">Loading...</div>;
}